import type { HeroClass } from './heroes.ts';
import type { StructureType } from './structures.ts';
import type { EnemyKind, NightmareType } from './nightmares.ts';
import type { NpcId, SagaCond } from './saga.ts';
import { daysMs, sec } from './units.ts';
import { BARBARIAN_PATH } from './sendas-barbaro.ts';
import { CAMINO_PATH } from './camino.ts';
import { DRUID_SENDA } from './senda-druid.ts';
import { GALO, RANGER } from './sendas-ranger.ts';
import { MAGE_PATH } from './sendas-mago.ts';

/**
 * The paths (docs/SENDAS.md; each class's story in docs/sendas/<clase>.md): one story per class, played alone by one
 * hero, five chapters from level 18 to 42. A chapter is a list of acts: scenes (with their cinematic, made with the
 * game's engine), the campfire (three questions, two asked), the one decision of chapter 4, and fights. The state
 * lives in the hero (`HeroState.senda`), not the group. Texts in i18n under `senda.<id>.*`:
 *
 * - `senda.<id>.title`, `.question`, `.call` (the push when chapter 5 begins, `{name}`), `.titled` (`{name}`, the tag)
 * - `senda.<id>.npc.<who>` (and `.role`): the path's people; `senda.<id>.face.<face>`: who a memory is played as
 * - `senda.<id>.<ch>.title`, `.closed` (the mentor's line when the tries run out), `.piece` (the look's piece it gives)
 * - scenes: `senda.<id>.<ch>.<act>.<line>`
 * - campfire: `senda.<id>.<ch>.<act>.q.<q>` (the question) and `senda.<id>.<ch>.<act>.<q>.<line>` (the answer)
 * - decision: `senda.<id>.<ch>.<act>.<line>`, `senda.<id>.<ch>.<act>.c.<option>` (+ `.desc`)
 * - fight: `senda.<id>.<ch>.<act>.title`, `.goal` (one line on screen), `.win`, `.lose`, and what is said in it,
 *   `senda.<id>.<ch>.<act>.say.<line>`
 *
 * Marks are the hero's, never the group's (`hero.senda.<cls>.<mark>` in the docs; stored as the mark alone).
 */

/**
 * The paths' master switch: on. Off, every path is shut (tests turn it off to check that; `?senda=1` and SENDA_DEV still
 * turn it on). Which classes walk theirs, and how far, is SENDA_READY's.
 */
export const SENDA_SWITCH = { on: true };

/**
 * The paths open, by class, and the chapter each is ready up to (docs/SENDAS.md): a class not here has its path off. A
 * chapter past its number doesn't open, whatever the hero's level and days («El próximo capítulo llega pronto» on its
 * card; the server won't hand out its fights either). Raised in a later deploy, the chapter opens by itself if the level
 * and the days since the last one allow it. Never past the written chapters (`sendaReady`).
 */
export const SENDA_READY: Partial<Record<HeroClass, number>> = {
  warrior: 5,
  paladin: 5,
  artificer: 5,
  barbarian: 5,
  cleric: 5,
  druid: 5,
  ranger: 2,
  mage: 1,
};

/** The second night remembered lasts this long (chapter 3, act 2). */
const SENDA_NIGHT_TICKS = sec(360);

export const SENDA = {
  /** Tries a chapter starts with (and starts again with after it closes). */
  tries: 3,
  /** Out of tries, the chapter closes until the hero levels up or this long passes (real time). */
  reopenMs: daysMs(3),
  /** Between one chapter done and the next opening (real time). */
  waitMs: daysMs(4),
  /** Tries friends can give, per chapter (one each). */
  gifts: 2,
  /** The hero's level each chapter opens at. */
  levels: [18, 24, 30, 36, 42],
  /** Each chapter's common nightmares: life and blow, over a lone hero's scale (docs/SENDAS.md, «Calibración»). */
  toughBp: [9000, 10000, 11000, 12000, 12500],
  /** The nightmares scale with the hero's level, at least the chapter's and at most this much above it. */
  above: 6,
  /** XP for a chapter done, about a day's play at its level (nothing to the common storage). */
  xp: [8000, 10000, 12500, 15000, 22000],
  /** Questions asked at a campfire (of its three). */
  asks: 2,
  /**
   * The path's enemy remembers: a try lost past its half leaves a scar, `max` at most, each one `hpBp` of its life
   * (its goal's half, for one won by time or by a task).
   */
  scars: { max: 2, hpBp: 1000 },
  /**
   * The path's people on the field (battle/senda.ts): every `every` ticks each nightmare within `range` lands a blow
   * (their life is in blows); a blow stops their work `flinch` ticks. They follow `follow` behind the hero at `speed`.
   */
  npc: { every: sec(1), range: 900, flinch: sec(2), follow: 900, speed: 70 },
  /** A fight handed out waits this long for its log. */
  issuedMs: 3 * 3_600_000,
  /**
   * The paths' own grounds (an open clearing, the ledge): their nightmares hit this much harder per level over the
   * first, as the sagas' descents do (SAGA.dmgBp), times the chapter's `toughBp`.
   */
  dmgPerLevelBp: 1000,
  dmgBp: 12000,
  /**
   * The wedges, small (docs/sendas/guerrero.md §4; battle/wedges.ts): three shields for six gaps, two seconds still
   * to plant, pull or push; the rock falls three seconds after the push; the counter goes to eight; six in loses it.
   * The column: sixteen goblins at the rim from 40 s, one every `every` ticks; at half pace where the way is one hex
   * wide. `reach`: how near the gap or the nook the hero stands to work it; `door`: how near the house's middle is in.
   * `hpBp`, `dmgBp`: the column's life and blow over the chapter's common nightmares (measured, docs/DECISIONS.md).
   */
  wedge: { shields: 3, stand: sec(2), reach: 1100, fall: sec(3), need: 8, lose: 6, door: 800, slowBp: 5000, column: 16, start: sec(40), every: 6, hpBp: 27500, dmgBp: 20000 },
  /**
   * The hooks (docs/sendas/guerrero.md, chapter 2, act 1; battle/hooks.ts): Kel winds the span up for `winch` ticks (a
   * blow stops him a moment, SENDA.npc.flinch). A hook bites the rim `warn` before the first climber shows, its rope
   * shaking once (one climber: `one`), twice (a band: `band`) or three times (the shadow ogre, `ogreD` seconds of D,
   * at `ogreAt`, slower up the rope by `ogreClimb`: «¡Esa soga se está estirando!»); standing by it `cut` cuts it and
   * what is on it falls. Climbers come over `climb` ticks apart. From
   * the seed: the first at `first`, then one every `gap` (from, to), two at once now and then (`pairBp`), a band
   * `bandBp` of the time; gargoyles need no rope (`garg`). `reach`: how near the rope the hero stands to cut it.
   * `hpBp`, `dmgBp`: the climbers over the chapter's common nightmares (measured, docs/DECISIONS.md).
   */
  hooks: {
    winch: sec(120),
    warn: sec(3),
    cut: sec(1),
    reach: 1250,
    climb: 8,
    first: sec(5),
    gap: [sec(7), sec(11)] as [number, number],
    pairBp: 3000,
    bandBp: 3500,
    one: ['goblin', 'skeleton'] as NightmareType[],
    band: [
      ['goblin', 3],
      ['skeleton', 2],
    ] as [NightmareType, number][],
    ogreAt: sec(58),
    ogreD: 50,
    ogreClimb: sec(5),
    garg: { n: 2, from: sec(30), every: sec(30) },
    anchors: 7,
    hpBp: 7500,
    dmgBp: 11000,
  },
  /**
   * The windbreak (chapter 2, act 2; battle/gusts.ts): Kel crosses the platform at `kel` a tick with the stretcher;
   * the wind comes in gusts along the gorge, seen `warn` before in the snow lifting, every `every` (± `jitter`) from
   * `first`; one pushes whatever it catches in the open `push` hexes downwind and knocks the stretcher out of Kel's
   * hands (gathering it is `drop`). Behind a cover the wind doesn't strike: `lee` hexes downwind of it, and the two
   * cells on its slant. There are `covers` (`shields` of them the Company's, the rest planks), planted in `stand` and
   * pulled again in `pull`; Ari and Gerd already cover one side. Crows in flocks (`crows`); the bone standard-bearer
   * waits at the far end and comes out when Kel is `bannerBp` of the way across. Won with Kel within `done` of the end.
   */
  gust: {
    kel: 26,
    every: sec(6),
    jitter: sec(1),
    first: sec(9),
    warn: sec(3),
    push: 2,
    drop: sec(4),
    fall: 1,
    lee: 2,
    covers: 5,
    shields: 2,
    stand: sec(2),
    pull: sec(1),
    reach: 1100,
    done: 700,
    crows: { n: 6, from: sec(5), every: sec(10) },
    bannerBp: 5500,
    hpBp: 10000,
    dmgBp: 17000,
  },
  /**
   * The counterweight (chapter 2, act 3, the test; battle/weights.ts, battle/weight-layout.ts): the five shields (all of
   * them hang) and two stones; posts with one share of weights, built in one to `ways` ways. Picking a piece up or
   * setting it on a pan is `pick` / `put` standing by it (`reach`); carrying slows to `carryBp`. Levelled, the spans
   * come down `hold` later. The shell serpent (era IV, `d` seconds of D) travels under the stone, out of reach; every
   * `every` (the first at `first`) it comes up by the heaviest pan, the rock cracking `warn` before; up, `scare` blows
   * within `grab` send it down empty-handed, else it throws that pan's heaviest piece over the edge (a shield hangs from
   * its chain: Kel hauls it up in `haul`; a stone comes back from the pile in `restock`) and stays up `up`. Still alive
   * when the spans come down, it sinks for good. `dmgBp`: its bite over the chapter's common blow.
   */
  weight: {
    shields: [1, 2, 3, 3, 4],
    stones: [2, 2],
    ways: 3,
    pick: sec(1),
    put: sec(1),
    reach: 1100,
    carryBp: 7000,
    haul: sec(8),
    restock: sec(10),
    hold: sec(2),
    serpent: { d: 90, warn: sec(3), every: sec(15), first: sec(12), grab: sec(2), up: sec(5), scare: 2, dmgBp: 10500 },
  },
  /**
   * The icy climb (chapter 3, act 1; battle/slope.ts): Kel leads the mule (Wenn on it) up the trail at `pace` a tick,
   * stopping while a nightmare is within `halt` of either, while the hero is more than `wait` behind, or where a rock's
   * shadow lies ahead. Rocks fall from the seed (the first at `first`, then one every `gap`), their shadow `warn`
   * before, striking all within `r`: the hero `heroBp` of its life, a person `blows`, a nightmare `foeBp` of its life.
   * Wolves in packs (`wolves`). The peat troll (`trollD` seconds of D, its blow `trollDmgBp` of a common's) waits at the door; it comes down when the
   * torch goes in (or the hero comes within `wake` hexes). Kel plants his torch in the snow at the landing (`torch`, hexes before the door): the
   * troll that stands on it (within `burn`) stops mending for the troll's own burn (NIGHTMARES.troll.regen). Dalmo
   * keeps to the mule (within `guard`) and strikes what comes at it (within `reach`) every `every`, `hitD` seconds of D
   * a blow. Won with the mule within `door` of the fort's door. `hpBp`, `dmgBp`: the wolves over the chapter's.
   */
  slope: {
    pace: 40,
    wait: 4,
    halt: 1700,
    first: sec(7),
    gap: [sec(4), sec(7)] as [number, number],
    warn: sec(3),
    r: 750,
    heroBp: 1300,
    blows: 2,
    foeBp: 3500,
    wolves: { n: 3, first: sec(5), every: sec(18) },
    trollD: 34,
    trollDmgBp: 5500,
    wake: 3,
    torch: 3,
    burn: 1050,
    dalmo: { every: sec(2), hitD: 1, reach: 1300, guard: 1400 },
    door: 1500,
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * The roof that tips (chapter 3, acts 2 and 3; battle/roof.ts, battle/fort-layout.ts; docs/sendas/guerrero.md §4): the
   * fort's gabled roof on its axis, each side's load what hangs on its hooks and the snow on it; at `tip` it tips, all
   * the roof's snow falls into that side's yard and, `bury` of it or more, buries what is under it (the common nightmares die; the old troll
   * loses `quarterBp` of its life and stands stunned `stun`; a hero under it, `heroStun`). A side's hooks hold `cap` at
   * most, on `hooks` hooks. The wind puts one of snow on the lee side every `wind`; it changes every `phase` (from, to), its flag shaking
   * `warn` before. The pieces: the seven shields (`shields`, rayed with their weight) and `sacks` (from, to) of wood
   * weighing `sack`; one in the hands at a time, slower (`carryBp`), hung or taken down in `hang` standing by its hook
   * (`reach`). The hearth in one yard: `kicks` blows put it out, standing by it `light` lights it again (`dig` when the
   * roof buried it: the snow off it first), out for `out`
   * in a row loses the fight. The nightmares come in bands from the far yard's mouth (`bands`: the first at `first`,
   * then one every `every`, `n` from, to, growing by one every `grow`) and file through the passage at half pace.
   */
  roof: {
    tip: 10,
    cap: 9,
    hooks: 4,
    bury: 4,
    wind: sec(10),
    phase: [sec(60), sec(90)] as [number, number],
    warn: sec(15),
    shields: [1, 2, 3, 3, 4, 2, 3],
    sack: 2,
    sacks: [2, 3] as [number, number],
    hang: sec(2),
    reach: 1100,
    carryBp: 7500,
    stun: sec(4),
    heroStun: sec(2),
    quarterBp: 2500,
    kicks: 3,
    light: sec(3),
    dig: sec(10),
    out: sec(20),
    bands: { first: sec(8), every: sec(24), n: [2, 4] as [number, number], grow: sec(180) },
    kinds: ['goblin', 'skeleton', 'wolf'] as NightmareType[],
    slowBp: 5000,
    hpBp: 10000,
    dmgBp: 6000,
  },
  /**
   * The old troll (chapter 3, act 3; battle/roof.ts): `d` seconds of D; it goes straight for the hearth (in `at` from the
   * fight's start), striking what stands in front of it; the lit fire within `fear` turns it back `scare` (toward the far
   * yard, at `fleeBp` of its pace); stunned under the snow it takes all of a blow, else only `armorBp` of it.
   */
  oldTroll: { d: 110, at: sec(20), fear: 1500, scare: sec(4), fleeBp: 14000, armorBp: 600, speedBp: 8500, dmgBp: 16000 },
  /**
   * The second night, remembered (chapter 3, act 2; battle/roof.ts): played as Ruy for `ticks`. Fermín works the roof:
   * he says to wait at `gather` in the far yard and tips it at `at` (the hearth's side never: he keeps it light). Juana
   * shoots from the loopholes every `every`, `hitD` seconds of D. Sanne and Tomé light the hearth again when it goes out.
   * The Marea comes up thicker that night than the test's (`bands`, as SENDA.roof.bands).
   */
  night: {
    ticks: SENDA_NIGHT_TICKS,
    gather: 3,
    at: 7,
    every: sec(2),
    hitD: 1,
    bands: { first: sec(8), every: sec(16), n: [4, 5] as [number, number], grow: sec(120) },
    hpBp: 9000,
    dmgBp: 9000,
  },
  /**
   * The advancing front (chapter 4, acts 1 and 3; battle/slide.ts): snow coming down the slope as a line, at `speed` a
   * tick, its white shadow on the snow `warn` before. What it catches in the open (not in the lee of a rock: the cell
   * straight below one) it strikes: the hero `heroBp` of its life and `push` hexes down the slope; a person `blows` and
   * the same push; a common nightmare swept away (a giant stands it).
   */
  front: { speed: 520, warn: sec(10), heroBp: 4500, push: 3, blows: 4, stun: 18 },
  /**
   * The loose slope (chapter 4, act 1; battle/slide.ts): Kel hauls the stretcher up the trail at `pace` a tick, waiting
   * while a nightmare is within `halt` of him, while the hero is more than `wait` hexes behind, and (he knows it) behind
   * a rock while a front's shadow is on the snow. Three fronts come down, at `fronts` (their shadow `SENDA.front.warn`
   * before each). Wolves in packs (`wolves`); a small sleeping giant (`giantD` seconds of D, its blow `giantDmgBp` of a
   * common's) at the trail's edge wakes with the hero or Kel within `wake` (sim units), or struck. Won with Kel within `mouth`
   * of the cave's mouth. `hpBp`, `dmgBp`: the wolves over the chapter's commons.
   */
  loose: {
    pace: 45,
    wait: 4,
    halt: 1700,
    fronts: [sec(16), sec(40), sec(64)],
    wolves: { n: 2, first: sec(6), every: sec(15) },
    giantD: 60,
    giantDmgBp: 4000,
    wake: 1500,
    mouth: 1500,
    hpBp: 7000,
    dmgBp: 10000,
  },
  /**
   * The ice cave (chapter 4, act 2; battle/cave.ts): three rooms one after another, `rooms` fallen sentinels in each,
   * standing guard facing the way in until the hero comes into their room. From the front (`arc` angle steps either side
   * of where it faces, 1024 a turn) nothing gets in (`frontBp` of a blow); they turn at most `turn` steps a tick, so the
   * hero gets round them. A room's sentinels down, the ice of its far door cracks open; past the third, the hall where
   * Fermín and Juana sit. Won within `reach` of them. `hpBp`, `dmgBp`: the sentinels over the chapter's commons.
   */
  cave: { rooms: [1, 2, 2], arc: 200, frontBp: 0, turn: 9, reach: 1600, hpBp: 5000, dmgBp: 7000 },
  /**
   * The wedges, large (chapter 4, act 3, the test; battle/slide.ts, battle/wedge-layout.ts; docs/sendas/guerrero.md
   * §4): seven lanes down the slope, nine rocks in three rows, each with its grain to a side (a slope from
   * `AVALANCHE_SLOPES`, or its mirror, by the seed); `wedges` iron wedges, driven in or pulled out standing still `stand`
   * by a rock (within `reach`). The avalanche comes down at `fall`; touching the cave once the stretcher holds (a shield
   * lost) and it comes down again `again` later; `touches` loses it. The sleeping giant (`giantD` seconds of D) crosses
   * the slope throwing rocks at the hero every `every` (the shadow `warn` before, `heroBp` of its life within `r`).
   * Wolves in packs (`wolves`). `hpBp`, `dmgBp`: the wolves over the chapter's commons.
   */
  avalanche: {
    wedges: 4,
    stand: sec(2),
    reach: 1150,
    fall: sec(90),
    again: sec(60),
    touches: 2,
    giantD: 130,
    giantDmgBp: 6000,
    rocks: { every: sec(6), warn: sec(2), r: 800, heroBp: 1600 },
    wolves: { n: 4, first: sec(8), every: sec(13) },
    hpBp: 10000,
    dmgBp: 17500,
  },
  /**
   * The Ventisquero's cubs (chapter 5, every act; docs/sendas/guerrero.md §5): balls of snow that walk, `d` seconds of D
   * each, and burst on what they reach: the hero `heroBp` of its life and `push` hexes; a person, `blows`.
   */
  cub: { d: 3, heroBp: 800, push: 1, blows: 2 },
  /**
   * The ledge of the wind (chapter 5, act 1; battle/crag.ts): Harl walks the ledge ahead at `pace` a tick, waiting while
   * the hero is more than `wait` hexes behind; gusts toward the drop every `every` (± `jitter`) from `first`, seen `warn`
   * before. A gust catching the hero in the open (not in a niche of the wall) pushes it `push` hexes to the drop's lip
   * and strikes `heroBp` of its life, `stun` on its knees. Harl and Wenn know the ledge: they press
   * into a niche when it warns. Cubs come at the walkers from ahead and behind (`cubs`). Won with Harl at the pass's
   * mouth (within `reach`).
   */
  crag: {
    pace: 34,
    wait: 4,
    first: sec(8),
    every: sec(9),
    jitter: sec(2),
    warn: sec(3),
    push: 2,
    heroBp: 2400,
    stun: sec(1),
    cubs: { n: 2, first: sec(5), every: 114 },
    reach: 1400,
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * The camp (chapter 5, act 2; battle/line.ts): what came in through the crack, camped round Ruy's stone (`foes`, and
   * `ogres` shadow ogres of `ogreD` seconds of D); cleared, Dalmo and the farms come up the shepherds' path with the
   * stretcher, and Ruy's line is laid: eleven slots, ten shields, the diary's pages one every `page` (in the seed's
   * order). Taking a shield or setting it is `put` standing by it (`reach`), carrying at `carryBp`. Cubs come one at a
   * time every `every` from `first` (after the clearing) and knock a shield back to the stretcher. All ten right wins;
   * all ten in and the hero says so, the line stays as it is (`line`: how many right).
   */
  camp: {
    foes: [
      ['skeleton', 3],
      ['slinger', 2],
    ] as [NightmareType, number][],
    ogres: 2,
    ogreD: 40,
    page: sec(9),
    put: sec(1),
    reach: 1100,
    carryBp: 8000,
    cubs: { first: sec(14), every: sec(16) },
    hpBp: 10000,
    dmgBp: 5000,
  },
  /**
   * Ruy's beam (chapter 5, act 3; battle/beam.ts; §4 «El balancín»): the number on the beam starts at `start` and goes
   * up one for each `per` of snow the wind puts on the cornice (one every `wind`), to `end`, when the wind stops. The
   * basket, `slots` places, must stay under the number and no more than `gap` under it: at it or over, the cornice falls
   * at once; too light, the beam creaks `creak` and gives (not before the first snow). Stones of `stones` at the foot;
   * one in the hands, taken or set `put` standing by it. Cubs climb the basket (`climb`) and throw a stone off. Bands of
   * what came through the crack come down the pass (`bands`). The wind stopped, `settle` to set the basket: ready at the
   * number less one (the number at the end can't be met before it, the basket would be at it).
   */
  beam: {
    start: 8,
    end: 12,
    per: 3,
    wind: sec(24),
    gap: 3,
    slots: 4,
    stones: [1, 2, 3],
    creak: sec(6),
    settle: sec(15),
    put: sec(1),
    reach: 1100,
    carryBp: 7500,
    climb: sec(3),
    cubs: { n: 2, first: sec(10), every: sec(20) },
    bands: { first: sec(24), every: sec(24), n: [2, 3] as [number, number] },
    kinds: ['skeleton', 'wolf'] as NightmareType[],
    hpBp: 10000,
    dmgBp: 15500,
  },
  /**
   * The Ventisquero (chapter 5, act 4; battle/storm.ts; §5): `d` seconds of D. It starts `start` hexes from the line
   * (`startLine` with Ruy's line laid right) and comes on in charges of `step` hexes, every `every[0]` (`every[1]` under
   * `mid` of its life), its two footprints marked as one ends, the next at least `foot` later. A wedge driven into a
   * footprint (`stand` still by it, within `reach`; `hand` carried, a pile of `pile` by the line, one more every
   * `restock`) trips it: back `back` hexes (never past its start), exposed `exposed` (`exposedBp` of every blow). A charge
   * not cut strikes `chargeBp` of the hero's life and pushes it `chargePush`; between charges it sweeps (`sweep`). Cubs
   * (`n` a band, more under `mid`) go for the line: one touching it pushes that shield `shove` quarters of a hex back
   * (`banner` in the middle while Harl's banner stands), and a gap lets one through as if it crossed `gapShove`. At `call`
   * it calls the avalanche (and the farms come): its shadow `warn` before, crouching the last `crouch`. At `breath` it
   * breathes `ticks` in the middle of the pass: Ruy's cornice, the stone set within the breath, falls `fall` later for
   * `cornBp` of its life (a breath without it lets out `cubs` cubs). At `harl`, Harl's shield. Wenn reads a name every
   * `read`; Dalmo stops one cub in two on the left; the farmers' four torches. Scars: `scarBp` of its life each.
   */
  storm: {
    d: 180,
    start: 10,
    startLine: 11,
    step: 2,
    every: [sec(15), sec(12)] as [number, number],
    mid: 6000,
    foot: sec(6),
    stand: sec(2),
    reach: 700,
    hand: 2,
    pile: 6,
    restock: sec(20),
    back: 3,
    exposed: sec(4),
    exposedBp: 13000,
    chargeBp: 4000,
    chargePush: 3,
    sweep: { bp: 2000, warn: sec(3), r: 2200, every: sec(4) },
    cubs: { n: [3, 5] as [number, number], every: sec(20), first: sec(8) },
    first: sec(20),
    shove: 2,
    banner: 1,
    gapShove: 2,
    call: { at: 6000, warn: sec(20), crouch: sec(6), heroBp: 3000 },
    breath: { at: 3000, ticks: sec(6), fall: sec(3), cornBp: 1500, cubs: 4 },
    harl: { at: 1000, stand: sec(2) },
    lift: sec(2),
    read: sec(25),
    wennHits: 8,
    dalmoHits: 12,
    torches: 4,
    torchOut: sec(10),
    stayedBp: 8000,
    scarBp: 800,
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * The canals (the paladin's chapter 1, acts 1 and 3; battle/canals.ts, battle/canal-layout.ts; docs/sendas/paladin.md
   * §4, «Los canales»): fine canals cut in the glass from the sea down to the altars' pools, a stone at each fork that
   * shuts one mouth and leaves the other (moved standing still `stand` by it, within `reach`). The water comes back with
   * each wave, every `every` from `first`, the sea seen `warn` before; it runs down the canals as the stones leave it, a
   * cell every `flow` ticks, and what it ran stays wet `wet`. A pool fills with `fill` waves. A line burnt on the glass
   * cuts the canal it crosses `cut`. The crías never come onto water: a hero in it they wait out at its edge. The first
   * eye lit, the act ends `lit` later. `hpBp`, `dmgBp`: the crías over the chapter's commons.
   */
  canal: { every: sec(20), warn: sec(5), first: sec(14), flow: 2, wet: sec(5), fill: 2, stand: sec(2), reach: 1150, cut: sec(8), lit: sec(3), hpBp: 11000, dmgBp: 16000 },
  /**
   * Ser Marit on the field (the paladin's path): slow, sword and shield; what comes within `guard` of where she keeps she
   * goes at, at `speed`, and strikes every `every`, `hitD` seconds of D a blow, within `reach`. Each nightmare at her
   * (or at the second altar) lands a blow every `blows` (a cría strikes about that often; the generic person's is
   * SENDA.npc.every).
   */
  marit: { every: sec(2), hitD: 1, reach: 1150, guard: 2400, speed: 38, blows: sec(2) },
  /**
   * The water that goes (chapter 1, act 3, the test; battle/canals.ts; §4, «Prueba»): rounds of `big` great waves (they
   * climb to the high pool) and `small` small ones (only the low), SENDA.canal.every apart; `drop` after the last great
   * one the sea goes down below the high pool (seen `warn` before: an open mouth lets it all out); `ebb` after the last
   * small one it goes for good, and `settle` later both pools full win it. `rounds` rounds at most (the second with
   * `more` crías in each band). The plough (`plow`): the Arado, `d` seconds of D, out at `at`; every `every` it lowers
   * its blades `warn` and runs `len` hexes straight at `speed`, burning three lines at once (its way and one hex either
   * side) that strike `lineBp` of the life of whoever steps on them (once every `sting`) and cut the canal they cross.
   * Its own blow is a common ogre's times `plow.dmgBp` (its danger is the lines). `hpBp`, `dmgBp`: the crías over the
   * chapter's commons.
   */
  ebb: {
    big: 3,
    small: 3,
    drop: sec(6),
    warn: sec(5),
    ebb: sec(10),
    settle: sec(5),
    rounds: 2,
    more: 1,
    hpBp: 9000,
    dmgBp: 7000,
    plow: { d: 70, at: sec(16), every: sec(14), warn: sec(2), len: 6, speed: 230, lineBp: 1500, sting: sec(1), dmgBp: 5500 },
  },
  /**
   * The second altar (chapter 1, act 2; battle/kindle.ts): held SendaFight's `time` while it lights. The glass drake (`d`
   * seconds of D) comes in at `in` and keeps off at `hover`; at `at` and every `every` after it sweeps a cone of fire
   * `range` long (`arc` angle steps either side, 1024 a turn) at Ser Marit, marked `warn` before: `bp` of the life of all
   * it catches. A hero within `cover` of her as it sweeps takes it for her (her shield is slow: «ponerse en medio»).
   */
  kindle: {
    drake: { d: 50, in: sec(48), at: sec(60), every: sec(9), warn: sec(3), range: 5200, arc: 120, bp: 2000, cover: 750, hover: 4300 },
    hpBp: 9000,
    dmgBp: 10000,
  },
  /**
   * The salt flats (the paladin's chapter 2; battle/salt.ts, battle/salt-layout.ts; docs/sendas/paladin.md §4, «Las
   * salinas» and «La sal de Marit»). Square pools of still water between dykes of salt, in fog: what stands on salt is
   * seen, what goes through the water is not (the fights' nightmares, all of them: the fog hides them).
   *
   * - `fog` (act 1): Ser Marit follows the hero to the gate house in the middle at `speed`, `follow` behind, and strikes
   *   what she sees; won with her within `done` of its door.
   * - `sluice` (act 2): a gate is turned standing still `turn` by it (within `reach`); its water runs `flow`, then the
   *   levels move (down a step: until the upper is dry or the lower full; along a step: level, the odd mark staying
   *   with the fuller), and the gate locks. Every `back` the sea comes in under the dykes (seen `warn` before): the pools
   *   as they were, the gates free; `lose` of those with the altar under water lose it. The altar's pool dry, its eye
   *   shows and the act ends `dry` later. Drowned climb out of a full pool by the hero from `drowned.first`, every
   *   `drowned.every`, and `drowned.back` more with each return of the sea (each `drowned.d` seconds of D). Nuño comes out to help after `nuno` gates.
   * - `hold` (act 3, the test): the altar held while it lights (the fight's time). Ser Marit throws salt where she is
   *   told, one every `every` (the first ready at `first`; it lands `throw` later): a circle of `radius` that lasts
   *   `last` on salt or a dyke, `wet` on water or the altar's mud (it melts). What is in a circle is seen and goes
   *   `slowBp` slower, and stays salted (seen) `salted` after. The stalkers come in bands along two of four ways between
   *   the pools (`band`: the first at `first`, then every `every`, `n` a band), one way swapped for another at `swap`.
   *   The old stalker (`old`: `d` seconds of D, out at `at`) leaps at Marit from within `range` every `every`, its spot
   *   marked `warn` before: `bp` of her life and `bleed` blows more, one each `bleedEvery`; a hero within `cover` of the
   *   spot takes it for her (`heroBp` of its own life). It stays on her `linger`, then slinks back into the fog.
   *
   * `hpBp`, `dmgBp`: each act's nightmares over the chapter's commons.
   */
  salt: {
    fog: { speed: 44, follow: 1300, done: 1900, hpBp: 10000, dmgBp: 10000 },
    sluice: { turn: sec(2), reach: 1150, flow: sec(3), back: sec(40), warn: sec(5), lose: 3, dry: sec(2), drowned: { first: sec(6), every: 121, back: 2, d: 10 }, nuno: 2, hpBp: 10000, dmgBp: 10000 },
    hold: {
      every: sec(25),
      first: sec(2),
      throw: sec(1),
      radius: 1400,
      last: sec(45),
      wet: sec(4),
      salted: sec(14),
      slowBp: 2000,
      band: { first: sec(10), every: sec(17), n: 3 },
      swap: sec(75),
      old: { d: 90, at: sec(55), every: sec(16), warn: sec(3), linger: sec(3), range: 5200, bp: 1200, bleed: 3, bleedEvery: sec(2), cover: 950, heroBp: 3500 },
      hpBp: 6500,
      dmgBp: 15000,
    },
  },
  /**
   * The bridge's altar (the paladin's chapter 3; battle/arch.ts, battle/arch-layout.ts; docs/sendas/paladin.md §4, «El
   * aljibe del puente»). A stone bridge of one arch in the rain, the fourth altar in its middle over a cistern carved in
   * the arch; stairs come up from the plates at both ends. Ser Marit keeps the lower end, Ser Varo and his two knights
   * the upper one. Every act:
   *
   * - `aura`: the paladin's (heroes.ts PALADIN.devotion.radius): a person this near a standing hero is covered, and of
   *   its blows only `coverBp` count (a fraction kept from one blow to the next). From beside the altar it reaches both
   *   ends; from anywhere else, one.
   * - `rain`: the heavy rain falls on one half of the bridge at a time, `every` a side (the first from the seed), its
   *   curtain seen moving `warn` before.
   * - `bands`: nightmares come up the stairs (each act's `n` a band; a band by both ends brings its `both` a side), seen
   *   in the clouds below `warn` before they set foot on the stairs.
   * - `lever`: a gate or a gargoyle's lever (by the cistern) is worked standing `turn` within `reach` of it.
   * - `sweep`: a gargoyle opened spends `drop` marks (it won't open with fewer) and sweeps its stairs: what is on them is
   *   pushed `push` down, `pace` a tick, and lies `down`.
   * - `ends` (act 1): `count` bands, the first at `first`, then every `every`; won with all of them out and down.
   * - `gutters` (act 2): the cistern holds `cap` marks, from `start`. With the gate of the side where it rains open and
   *   the other shut it rises one every `step`; with the dry side's open it falls one every `step`; a nightmare on the
   *   rain side's gutter stops what comes in. `blockers` of each band go to stand on their end's gutter. Won full.
   * - `vigil` (act 3, the test): the cistern starts at `start`; the altar burns while it holds `lit` or more (below it,
   *   `dark` to fill it again, or it goes out and the act is lost). Fallen sentinels (`sentinel`: `d` seconds of D,
   *   their shield's front `arc` either side letting `frontBp` of a blow through; swept, they stand up side-on and it
   *   can't turn for `side`) lead every `every`-th band (`sentinel.every`) with an escort of `escort`; the others are
   *   commons. Two ash mammoths (`mammoth`: `d` seconds of D) out at `at`
   *   (the first by the lower end, the second by the upper), up the stairs in `climb`; at the stairs' head they lower
   *   their head for `lower`, then charge the altar at `charge` a tick and crush it. Swept while lowering their head:
   *   thrown to the foot with `sweepBp` of their life gone; swept otherwise, only pushed like the rest. Won with both down.
   *
   * `hpBp`, `dmgBp`: each act's nightmares over the chapter's commons.
   */
  arch: {
    aura: 3500,
    coverBp: 3000,
    rain: { every: sec(30), warn: sec(5) },
    bands: { warn: sec(3) },
    lever: { turn: sec(1), reach: 800 },
    sweep: { drop: 4, push: 4000, down: sec(2), pace: 450 },
    ends: { count: 14, first: sec(6), every: sec(22), n: 3, both: 3, kinds: ['skeleton', 'goblin', 'wolf'] as NightmareType[], hpBp: 10000, dmgBp: 10000 },
    gutters: { cap: 8, start: 0, step: sec(6), first: sec(8), every: sec(15), n: 5, both: 3, blockers: 2, kinds: ['skeleton', 'goblin'] as NightmareType[], hpBp: 13100, dmgBp: 12000 },
    vigil: {
      start: 8,
      lit: 4,
      dark: sec(15),
      first: sec(12),
      every: sec(32),
      n: 3,
      both: 2,
      escort: 1,
      kinds: ['skeleton', 'goblin'] as NightmareType[],
      sentinel: { d: 30, every: 2, arc: 200, frontBp: 4000, side: sec(8) },
      mammoth: { d: 120, at: [sec(140), sec(320)], climb: sec(40), lower: sec(3), charge: 260, sweepBp: 3334 },
      hpBp: 13600,
      dmgBp: 10000,
    },
  },
  /**
   * The monastery of the waterfall (the paladin's chapter 4; battle/monastery.ts, battle/monastery-layout.ts;
   * docs/sendas/paladin.md §4, «Las huellas» and «El monasterio»). One board for the three acts:
   *
   * - `lever`: a handful, a door's line or a gate is worked standing `turn` within `reach` of it.
   * - `porch` (act 1, the memory, played as Marit at seventeen): held for `ticks` while the Marea comes up the gorge, a
   *   band of about `n` (the seed's −1 to +1) every `every` from `first`. Ser Edda fights on the threshold (`edda`: every `every`, `hitD` seconds of
   *   D a blow, within `reach`; what comes within `guard` she goes at, at `speed`), and never falls. The squire within
   *   `cover` of her takes `coverBp` of a blow; past `far` a moment, Edda calls her (no oftener than `callEvery`). At
   *   `blowAt` she takes a blow meant for the squire.
   * - `cells` (act 2): `handfuls` of salt, a spot `radius` across; the refectory's sacks give them again standing
   *   `fetch`. Spectres seen in salt and `seen` after; their prints stay `prints`. A group of `n` out of the cell every
   *   `every` from `first`, at `pace` a tick along the corridors; at the altar they put it out and stay on Marit
   *   `linger`; she lights it again `relight` after.
   * - `cloister` (act 3, the test): the cistern holds `cap` marks from `start`; the sixth altar shows at `bare` or less
   *   (Marit lights it standing `light` there) and, at `reflect`, the young squire in the water. The waterfall adds a
   *   mark every `fall`. Each gate open takes its `take` (the orchards, the kitchen, the ravine) every `pulse`; the
   *   orchards take what the seed says (4 to 6) and shut. The kitchen's `snuffAt`-th mark out, and each `snuffEvery`
   *   after, puts out the fifth altar; `snuffs` of them lose it. The great sentinel (`boss`: `d` seconds of D) comes up
   *   from the ravine `rise` after it is first opened, else at `late`, and goes for the chapel; a hero within `meet` of it
   *   it turns on and fights. The siege: a band of `n` every `every` over the
   *   patio's wall (at `y`, `xs`), from the seed's first to its quiet, then `back` bands more from its return.
   *
   * `hpBp`, `dmgBp`: each act's nightmares over the chapter's commons.
   */
  monastery: {
    lever: { turn: sec(1), reach: 800 },
    porch: {
      ticks: sec(150),
      first: sec(5),
      every: sec(13),
      n: 7,
      kinds: ['skeleton', 'goblin', 'wolf', 'skeleton'] as NightmareType[],
      edda: { every: sec(1), hitD: 1, reach: 1200, guard: 2600, speed: 60 },
      cover: 1700,
      coverBp: 7000,
      far: 2600,
      callEvery: sec(8),
      blowAt: sec(75),
      hpBp: 10000,
      dmgBp: 37000,
    },
    cells: { handfuls: 4, radius: 650, fetch: sec(8), prints: sec(30), seen: sec(4), first: sec(18), every: sec(30), n: 3, pace: 55, linger: sec(14), relight: sec(8), hpBp: 10000, dmgBp: 10000 },
    cloister: {
      cap: 12,
      start: 12,
      bare: 2,
      fall: sec(10),
      pulse: sec(5),
      take: [1, 1, 3],
      snuffAt: 4,
      snuffEvery: 3,
      snuffs: 2,
      reflect: 4,
      light: sec(6),
      boss: { d: 140, rise: sec(20), late: sec(300), meet: 1800 },
      siege: { every: sec(15), n: 3, back: 6, y: 300, xs: [-3000, -1000, 1000, 3000], kinds: ['skeleton', 'goblin', 'skeleton', 'wolf'] as NightmareType[] },
      hpBp: 10000,
      dmgBp: 10000,
    },
  },
  /**
   * The seventh altar (docs/sendas/paladin.md, chapter 5; battle/seventh.ts). Distances in sim units (a hex is 1000).
   *
   * - `rubble` (act 1): the keep's stair under the fall's rubble, `piles` heaps, each cleared standing still `stand` within
   *   `reach` of it (Marit and Nuño carry). The old garrison's skeletons come up the holes, `n` every `every` from
   *   `first`; a bone singer at each of `singer`.
   * - `well` (act 3, the test; §4, «El séptimo»): the cistern's `cap` marks, the island bare at `bare` or fewer, the ring
   *   the fight needs at `ring` or more. Four gates (the yard, the barracks, the forge, the ravine) take `take` marks
   *   each opening, over `pulse`; a gate turned standing `turn` within `reach`. The spring, a mark every `spring`. The
   *   squires (`shield`): `n` every `every` from `first` out of the water, for Marit; their life `d` seconds of D, each
   *   blow `maritBp` of her life, one every `hitEvery`, `blows` of them and it comes apart; seen only in water or on salt.
   * - `fled` (act 4, §5): La Que Huyó's life `d` seconds of D. Her charge, straight at Marit along a line marked `warn`
   *   ahead: on the hero within `width` of it, `chargeBp` of his life (through armour and shields; devotion counts),
   *   else `maritBp` of Marit's; every `every` by phase (above 66 %, above 33 %, below). The reflection's (last phase)
   *   `reflectBp` of either. The slash: near the hero, `slash.bp` every `slash.every`. Hiding (`hide`): every `every`
   *   for `ticks` (the first phase), all the time (the second); trying in the last she stands `still` taking
   *   `stillBp`. The oath's bar: `oath` (with «Juro yo», only with the hero within `near` of the altar; Marit keeps
   *   `away` from it); after she falls, it fills in `calm`. Salt runnels filled standing `salt.stand`; with «Juro yo»
   *   Marit fills one every `salt.every`, the salt folk one every `folk` in the second phase. Gates: shut standing
   *   `gate.close`, she reopens one `gate.reopen` later (shaking `gate.warn` before); a quarter drains or fills in
   *   `gate.drain`. The squires again in the second phase. Varo comes down at `varoAt`. Each scar `scarBp`.
   */
  seventh: {
    rubble: { piles: 6, stand: sec(4), reach: 1100, first: sec(4), every: sec(10.25), n: 3, singer: [sec(14), sec(40)], hpBp: 10000, dmgBp: 11000 },
    well: {
      cap: 10,
      start: 10,
      bare: 4,
      ring: 2,
      take: [1, 1, 2, 3],
      pulse: sec(5),
      turn: sec(1),
      reach: 1000,
      spring: sec(20),
      shield: { d: 4, first: sec(4), every: sec(4), n: 1, maritBp: 1000, hitEvery: sec(2), blows: 3, pace: 80, reach: 950 },
      hpBp: 10000,
      dmgBp: 10000,
    },
    fled: {
      d: 180,
      chargeBp: 1000,
      maritBp: 2100,
      /** Marit's blows taken while she kneels (sworn together), bp: the branches' knob (docs/sendas/paladin.md §6). */
      kneelBp: 8500,
      reflectBp: 5000,
      warn: sec(3),
      every: [sec(7), sec(10), sec(5)],
      width: 650,
      dash: 6,
      linger: sec(2),
      pace: 95,
      slash: { bp: 400, every: 14, reach: 1300 },
      hide: { every: sec(16), ticks: sec(6) },
      still: { every: sec(12), ticks: sec(3), takenBp: 20000 },
      oath: sec(300),
      near: 2000,
      away: 4000,
      calm: sec(4),
      salt: { stand: sec(2), every: sec(20), folk: sec(20), reach: 1000, width: 380 },
      gate: { close: sec(3), reopen: sec(8), warn: sec(3), drain: sec(2), reach: 1000 },
      shield: { first: sec(4), every: sec(14), n: 1, maritBp: 600, blows: 2 },
      varoAt: 3333,
      folkHits: 10,
      scarBp: 800,
      hpBp: 10000,
      dmgBp: 10000,
    },
  },
  /**
   * The goat path (the artificer's chapter 1, act 1; battle/cornice.ts; docs/sendas/artifice.md §3). Grillo walks the
   * ledge at `pace` a tick while the hero is within `wait`, stopping short of a stone's shadow; he wins it within `reach`
   * of the door. Stones come off the nests from `first`, one every `gap` (from, to), seen `warn` before (dust and a
   * mark): on the hero `onHeroBp` of the time, else on the way ahead of Grillo; they strike `heroBp` of the hero's life
   * and `blows` of Grillo's within `r`. A slinger into a nest `ahead` rows or so ahead of Grillo every `every` from
   * `first`: it throws a stone every `reload` at Grillo within `range` (a blow) or at the hero (`heroBp`). Soot crows,
   * `n` from the sea every `every` from `first`. Grillo down crawls at `crawl` to the next rest and gets up `rise` after;
   * down `downs` times loses it. `hpBp`, `dmgBp`: the slingers and crows over the chapter's commons.
   */
  cornice: {
    pace: 24,
    wait: 2600,
    reach: 1300,
    stone: { first: sec(6), gap: [sec(4), sec(7)] as [number, number], warn: sec(3), r: 750, heroBp: 2200, blows: 3, onHeroBp: 4500 },
    sling: { first: sec(6), every: sec(8), reload: sec(2), range: 6500, heroBp: 700, ahead: 4 },
    crows: { first: sec(12), every: sec(16.25), n: 6 },
    crawl: 9,
    rise: sec(5),
    downs: 2,
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * The workshop's golems (the artificer's chapter 1, acts 2 and 3; battle/golems.ts; §3, «Gólem de taller»): `d` seconds
   * of D each; slow (`pace`, `turn` angle steps a tick); asleep at its post until the hero walks into its lamp (`lamp`:
   * `range`, `arc` either side, sweeping `sweep` either side of the post's facing every `period`). Awake it goes for the
   * nearest (the hero or the path's person) and raises its arm `warn` (3 s) before a blow in an arc `arc` either side of
   * where it faces, `reach` long: `heroBp` of the hero's life, `blows` of a person's; `rest` before the next. A blow from
   * within `back` angle steps of its back opens its boiler: it goes out `off`; from anywhere else a blow lands at
   * `frontBp` (its iron). The Overseer (`boss`, the act 3 boss): `d` seconds of D, two boilers that open only after
   * `capBp` of its life struck from behind each, open `open`; both open at once put it out for good. Struck from behind it
   * swings round on whoever did it and raises its arm at them. With the hero within `spin.r`, any side, a golem spins its
   * arms out `spin.warn` and strikes all round (`spin.heroBp`, `spin.blows`), every `spin.every` at most; the Overseer
   * stamps every `stamp.every` (`stamp.warn` before): the whole hall, `stamp.heroBp`.
   */
  golem: {
    d: 14,
    pace: 26,
    turn: 11,
    reach: 1350,
    arc: 160,
    warn: sec(3),
    heroBp: 2200,
    blows: 4,
    rest: sec(1),
    off: sec(8),
    back: 200,
    frontBp: 4000,
    lamp: { range: 3600, arc: 64, sweep: 150, period: sec(7) },
    spin: { every: sec(8), warn: sec(3), r: 3200, heroBp: 2000, blows: 1 },
    stamp: { every: sec(7), warn: sec(3), heroBp: 2650, blows: 0 },
    boss: { d: 70, heroBp: 2500, blows: 6, capBp: 1100, open: sec(10), pace: 24, turn: 7, reach: 1700, warn: sec(3), arc: 190 },
  },
  /**
   * The sleeping workshop (chapter 1, act 2; battle/workshop.ts): five halls down to the lift's landing (won within
   * `reach` of it, Dunna with the hero). A golem awake in a hall drops the grille of its stair until it is broken; the
   * grille's clang wakes the rest of the hall, one every `chain`.
   * Dunna follows and shoots (`dunna`: `hitD` seconds of D a bolt every `every`, the nearest awake within `range`).
   */
  workshop: { reach: 1500, chain: sec(6), dunna: { every: sec(2), hitD: 0.5, range: 5000 } },
  /**
   * The wheel and the belt (chapter 1, act 3, the test; battle/gears.ts; §4, puzzle 1): a gear taken or put standing
   * `stand` within `reach` of its slot; carrying one, the hero goes at `carryBp` (12, 24, 36 teeth). A golem woken every `every` from `first`, one more for each train that jams,
   * `cap` awake at most. The train right, the belt takes it `settle` later and the Overseer wakes. Dunna by the door,
   * shooting.
   */
  gears: { stand: sec(1), reach: 1100, carryBp: [8500, 7000, 5500], every: sec(25), first: sec(16), cap: 3, settle: sec(3), dunna: { every: sec(2), hitD: 0.5, range: 5500 } },
  /**
   * The cable's gargoyles (the artificer's chapter 2; battle/garg.ts): `d` seconds of D each (the soot crows `crowD`),
   * flying at `pace` a tick; at a person within `reach` a blow every `reload` (`heroBp` of the hero's life, `blows` of a
   * person's). Perched on the car's roof they peck the hero within `peckR` below them.
   */
  garg: { d: 2.5, crowD: 1, pace: 95, reach: 750, reload: sec(1.5), heroBp: 700, blows: 1, peckR: 1500 },
  /**
   * The crane (chapter 2, act 1; battle/crane.ts): Dunna lowers `pieces` pieces of Tormo's arms to the car, `lower` each;
   * with `tilt` perched on the arm it leans, the piece sways and Dunna stops the winch; swaying `fall`, it drops. Flocks
   * from `first`, one every `every`, `n` each, `perchBp` of them for the arm. Swaying, the piece sweeps the scaffold's
   * tip (`swing`: `warn` before, every `every`, within `r`). Dunna shoots from the winch.
   */
  crane: {
    pieces: 3,
    lower: sec(60),
    tilt: 3,
    fall: sec(10),
    flock: { first: sec(6), every: sec(9), n: 7, perchBp: 6500 },
    swing: { warn: sec(3), every: sec(2.5), r: 1100, heroBp: 1400 },
    dunna: { every: sec(2), hitD: 0.5, range: 4000 },
  },
  /**
   * The car over the abyss (chapter 2, act 2; battle/gondola.ts): `len` across at full speed; `stop` on the cable stop it;
   * stopped, a flock every `stopFlock`. Flocks of `n` gargoyles and `crows` soot crows from `first`, every `every`. The
   * deck tilts (`tilt`: warned 3 s, tipped `dur`, what is loose sliding `slide` a tick; every `every`, and at each pylon):
   * within `hold` of a hand-hold the hero holds on; past `fall` off the deck's middle it is over the edge.
   */
  gondola: {
    len: sec(150),
    stop: 5,
    stopFlock: sec(10),
    flock: { first: sec(5), every: sec(8), n: 4, crows: 3 },
    tilt: { first: sec(14), every: [sec(14), sec(20)] as [number, number], warn: sec(3), dur: sec(2), slide: 80 },
    pylons: [sec(50), sec(105)],
    hold: 750,
    fall: 1300,
    dunna: { every: sec(2), hitD: 0.5, range: 5000 },
  },
  /**
   * The gearbox (chapter 2, act 3, the test; battle/gearbox.ts; §4, puzzle 2): a gear changed standing `stand` by its slot,
   * the clutch pulled in `pull`; carrying one, the hero at `carryBp` (12, 18, 24, 36 teeth). Flocks onto the box. A wrong
   * pull: too fast slams the car into the pylon (`slamBp`), too slow or the wrong way brings `flock` more. Right, the car
   * comes in `haul` later. La Picotera (`boss`, `d` seconds of D) on the drum: a peck every `every` (`warn` 3 s, at the
   * hero within `reach`, else at a gear in the box: she knocks it out), a wing-beat every `every` that nothing on the
   * platform is out of, two gargoyles called every `every` (and `half` more once, at half her life).
   */
  gearbox: {
    stand: sec(3),
    pull: sec(1),
    reach: 1100,
    carryBp: [8500, 8000, 7000, 5500],
    flock: { first: sec(12), every: sec(22), n: 2, perchBp: 5000 },
    err: { slamBp: 1500, blows: 2, flock: 3 },
    haul: sec(3),
    boss: {
      d: 90,
      peck: { every: sec(5.5), warn: sec(3), r: 850, heroBp: 2000, reach: 3800 },
      beat: { every: sec(13.5), first: sec(15), warn: sec(3), heroBp: 1000, blows: 1 },
      call: { every: sec(20), n: 2, half: 3 },
    },
    dunna: { every: sec(2), hitD: 0.5, range: 5500 },
  },
  /**
   * The deep road (the artificer's chapter 3, act 1; battle/hondo.ts; docs/sendas/artifice.md §3): the Hondura's tunnel
   * down toward the heat, the vents across it blowing each on its own beat (`beat`: from, to, by the seed), warned
   * `warn` (the floor glowing, Grillo's lamp blinking), blowing `blow`: whoever is in the band as it blows takes
   * `heroBp` of the hero's life (`blows` of a person's; a nightmare `foeBp` of its life), once a blow. Grillo goes ahead
   * at `pace`, stops short of each vent and crosses as a blow ends, Dunna behind him (they know the beat); he waits for
   * the hero within `wait`, and holds back while a nightmare is within `hold` of him. Ash nightmares and oozes come up the road in bands (`foes`), ahead of Grillo. Won with the
   * three within `reach` of the Médula's mouth. `hpBp`, `dmgBp`: the road's nightmares over the chapter's commons.
   * `npcEvery`: in the three acts of the chapter, a nightmare at Dunna lands a blow on her every this long (as Marit's).
   */
  vents: {
    beat: [sec(7.5), sec(9.5)] as [number, number],
    warn: sec(3),
    blow: sec(3),
    heroBp: 2500,
    blows: 2,
    foeBp: 3500,
    pace: 70,
    wait: 2800,
    hold: 2600,
    reach: 1600,
    npcEvery: sec(2),
    foes: { first: sec(5), every: sec(11), n: 3 },
    dunna: { every: sec(2), hitD: 0.5, range: 4500 },
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * The river of embers (act 2; battle/brasa.ts): Dunna unjams the old dwarven trip-hammer on the bank for `work` (a
   * blow stops her a moment, SENDA.npc.flinch); the magma rises in pulses (`pulse`: from `first` every `every`, warned
   * `warn`, `n` spots of `r`, the first on the hero, the rest about the bank, never on Dunna's: `heroBp` of the hero's
   * life, `blows` of a person's). Nightmares for her (`foes`: `n` every `every`), out of the two tunnels, or off the
   * river right by her `riverBp` of the time.
   */
  ember: {
    work: sec(90),
    pulse: { first: sec(7), every: sec(6), warn: sec(3), n: 4, r: 1100, heroBp: 3000, blows: 2 },
    foes: { first: sec(3), every: sec(9), n: 3, riverBp: 3300 },
    dunna: { every: sec(2), hitD: 0.5, range: 4500 },
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * The trip-hammer's cams (act 3, the test; battle/cams.ts; §4, puzzle 3): a cam put or taken standing `stand` by its
   * slot (3 s), the lever pulled in `pull`; the shaft turns once, a face under the hammer every `face`; `chips` chips
   * and the stone is no good. The magma's pulses (`pulse`: one on the cam the hero works, or on the hero; one on the
   * shaft) and the ash nightmares
   * off the river (`foes`). La Tenaza (`boss`: `d` seconds of D), a slag crab out of the river at `out`: it goes for the
   * hammer's wheel (or the hero within `chase`), turning `turn` a tick at `pace`; its pincers open `warn` (3 s) before a
   * snap `reach` long in an arc `arc` either side (`heroBp`, `blows`); struck within `front` of its face, a blow lands at
   * `frontBp` (the pincers cover it); within `gripR` of the wheel it grabs it for `grip` (the shaft still), then lets go
   * and comes again `again` later. Its splash (`splash`, warned 3 s) reaches the whole bank. Dunna by the stone.
   */
  cams: {
    stand: sec(3),
    pull: sec(1),
    reach: 1100,
    face: sec(1),
    chips: 3,
    pulse: { first: sec(9), every: sec(6), warn: sec(3), r: 1000, heroBp: 2500, blows: 1 },
    foes: { first: sec(20), every: sec(22), n: 2 },
    boss: {
      d: 120,
      out: sec(6),
      pace: 24,
      turn: 9,
      chase: 2600,
      reach: 1500,
      arc: 190,
      warn: sec(3),
      heroBp: 2500,
      blows: 3,
      rest: sec(1.5),
      front: 230,
      frontBp: 2500,
      gripR: 1500,
      grip: sec(10),
      again: sec(16),
      splash: { first: sec(16), every: sec(14), warn: sec(3), heroBp: 1250, blows: 0 },
    },
    dunna: { every: sec(2), hitD: 0.5, range: 5000 },
  },
  /**
   * The night of the promise (docs/sendas/artifice.md, chapter 4, act 1, the memory; battle/struts.ts): Grada's great
   * stair hangs from four loose struts. A tap on a strut sends one of Bruni's two old golems there (the nearer free
   * one, at `golem.pace`); it sets its back to it in `golem.set`, and Bruni comes behind and wedges it for `caulk`
   * (20 s). A golem struck while it holds lets go: the stair shakes (`sway` of the hero's life wherever it stands, once
   * every `swayGap` at most), Bruni's wedging there goes `lossBp` back, and the golem sets its back again `golem.back`
   * later. Felled (its blows in the act's npcs), Bruni lights it again where it lies in `light` (15 s). The Marea climbs
   * the cliff in bands (`foes`: `n` from `first`, one more every `grow`), up the ledges at the rim: `golemBp` of them go
   * for the golems (the common hunt), the rest for the houses, the hero in their way. One in the houses is a house
   * lost; `houses` and the act is lost. The quarrymen at their doors strike what comes (`pick`). Grillo points his lamp
   * at the loosest strut not yet wedged. The stair groans (`groan`: from `first` every `every`, warned `warn`) and sheds
   * stone over the whole ledge: `bp` of the hero's life for each strut loose (neither held nor wedged), wherever it
   * stands. `npcEvery`: a nightmare at a golem lands a blow every this long.
   */
  struts: {
    caulk: sec(20),
    light: sec(15),
    lossBp: 5000,
    sway: 2000,
    swayGap: sec(2),
    houses: 5,
    reach: 900,
    npcEvery: sec(2),
    golem: { pace: 55, set: sec(1.5), back: sec(3) },
    bruni: { pace: 75 },
    foes: { first: sec(5), every: sec(7), n: 3, grow: sec(40), golemBp: 6000 },
    groan: { first: sec(14), every: sec(15), warn: sec(3), bp: 1150 },
    pick: { every: sec(2), hitD: 0.3, range: 1300 },
    hpBp: 10000,
    dmgBp: 9000,
  },
  /**
   * The carts (chapter 4, act 2; battle/carts.ts): up the glacier's tongue into the dwarven mine, blue galleries and
   * rails. Ink beetles sleep on the rails where they cross the way, their shells shut (`shellBp` of a blow gets
   * through; nobody walks past them). A cart frozen at the top of each rail's slope comes down once its brake is worked
   * (`brake`, standing by it within `reach`): it runs the rail at `pace`, cracks the shell of every beetle on it and
   * strikes what else is there (`foeBp` of a nightmare's life, `heroBp` of the hero's). Each runs once; spent, a new one
   * is built at the slope's top `again` (40 s) later. A beetle cracked wakes and fights (`beetle`: its slam warned
   * `warn`, 3 s, `heroBp`), its life `hpBp` of a common beetle's. The ice cracks (`crack`: `n` spots from `first` every
   * `every`, warned `warn`, the first on the hero) and shades come down the galleries (`foes`). Won with the hero at
   * the drill hall's door within `door`, the way clear; lost with Dunna or the hero down.
   */
  carts: {
    brake: sec(1),
    reach: 1100,
    pace: 220,
    again: sec(40),
    shellBp: 400,
    foeBp: 6000,
    heroBp: 3000,
    door: 1500,
    beetle: { hpBp: 12000, wake: 2600, reach: 1400, arc: 200, warn: sec(3), heroBp: 1300, blows: 2, rest: sec(3) },
    crack: { first: sec(8), every: sec(7), warn: sec(3), n: 2, r: 1000, heroBp: 1500, blows: 2 },
    foes: { first: sec(6), every: sec(18), n: 2 },
    dunna: { every: sec(2), hitD: 0.5, range: 4500 },
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * The drills (chapter 4, act 3, the test; battle/drills.ts; §4, puzzle 4): the mill at the mouth gives `mill` (3) of
   * force to a shaft along the roof; belts come down from it to four drills, each with its clutch (in or out, worked
   * standing by it `clutch`), its force (1 or 2) and its arrow. At the belts' station two levers (`lever`): I turns the
   * belt of drills 1 and 2 round, II that of 3 and 4. Drills in asking more than `mill` jam the shaft: the jolt throws
   * every clutch out, shakes the hall (`jam.heroBp`) and frees a nightmare out of the ice. A drill turning against its
   * arrow doesn't cut, but asks its force all the same. A wall falls after `cut` (6 s) cut with its whole force. The
   * roof sheds ice (`ice`: one on what the hero works, one on a drill in). La Barrena (`boss`: `d` seconds of D), out of
   * the broken drills at `out`, goes for a drill in (or the hero within `chase`): its slash warned `warn` (3 s); a drill it
   * reaches stops `stall` (8 s, that wall's cut starts over). Its spin (`spin`, warned 3 s) throws ice through the whole
   * hall. Won with both walls down and La Barrena down.
   */
  drills: {
    mill: 3,
    clutch: sec(1),
    lever: sec(1.5),
    reach: 1100,
    cut: sec(6),
    jam: { heroBp: 1500 },
    ice: { first: sec(10), every: sec(7), warn: sec(3), r: 1000, heroBp: 2500, blows: 1 },
    foes: { first: sec(24), every: sec(30), n: 2 },
    boss: {
      d: 135,
      out: sec(8),
      pace: 30,
      turn: 10,
      chase: 2600,
      reach: 1500,
      arc: 190,
      warn: sec(3),
      heroBp: 2000,
      blows: 3,
      rest: sec(1.5),
      stall: sec(8),
      stallR: 1300,
      spin: { first: sec(18), every: sec(14), warn: sec(3), heroBp: 2100, blows: 0 },
    },
    dunna: { every: sec(2), hitD: 0.5, range: 5000 },
  },
  /**
   * The knees (the artificer's chapter 5, act 1; battle/knees.ts; the lesson of chapter 1): Tormo's hall coming down,
   * Tormo walking after Nadd, whom Dunna leads by the hand round the hall's way (`people`, sim units a tick; Tormo
   * `tormo`, `lockSlow` less for each joint locked). Tormo starts `gap` behind them and takes them within `grab`. His
   * knees and elbows are gear trains in sight; each has two or three free axles, each touching two gears of its train;
   * a gear put on the one that touches two turning opposite ways jams the joint (standing still by it `stand`, within
   * `reach`). Put anywhere else, it sparks and Tormo lunges `lunge` after Nadd. His stomp (`stomp`, warned 3 s) strikes
   * the ring from `inR` to `outR` round his feet; he rests `rest` after. The gears lie in two heaps by the hall's wall:
   * the hero takes one (`take`) and carries it (`carryBp` of its pace). Each of his steps shakes the hall (`quake`,
   * warned 3 s, everywhere): `bp` of the hero's life for each joint still free. The roof drops stone (`debris`), shades
   * come out of the cracks (`foes`). Nadd and Dunna take 8 common blows together. Won with the four
   * locked (`fall` later: on his knees); lost with Nadd taken, them down or the hero down.
   */
  knees: {
    stand: sec(1.5),
    reach: 1300,
    gap: 9000,
    grab: 1300,
    people: 12,
    tormo: 24,
    lockSlow: 4,
    lunge: 1600,
    take: sec(1),
    carryBp: 8000,
    stomp: { first: sec(9), every: sec(12), warn: sec(3), rest: sec(2), inR: 1100, outR: 2800, heroBp: 3000, blows: 1 },
    debris: { first: sec(9), every: sec(8), warn: sec(3), r: 1000, heroBp: 1800, blows: 1 },
    quake: { first: sec(12), every: sec(12), warn: sec(3), bp: 450 },
    foes: { first: sec(10), every: sec(17), n: 1 },
    npcEvery: sec(2),
    fall: sec(2),
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * The winch (chapter 5, act 2; battle/torno.ts; the lesson of chapter 2): Tormo climbs to the Raja over Grada, the
   * cable's car's cable hooked on his harness. The winch's box: A (the motor, `input`), B with its two places (B1 meshes
   * with A, B2 with C), C (the drum). Its speed is `input`·A/B1·B2/C; its pull, the inverse: the box must give the
   * slowest the shelf allows (its five gears), what Tormo's mark asks. Taking or putting a gear standing still `stand`
   * by it (carrying one slows the hero, `carryBp` by its teeth); the lever (`pull`) pulls. Pulled too fast, the cable
   * slips: Tormo climbs a span and the cable takes `slipBlows`. Pulled right, it hauls him `haul` and he lets go. Not
   * held, he climbs a span every `climb`. He starts `spans` from the Raja: there, lost. The gargoyles off the abyss
   * perch on the cable (`flock`) and cut it, a blow each every `peck`: `cable` blows and it is lost. The Raja sheds
   * stone on the ledge (`rocks`, warned 3 s) and, every so often, a shower of it over the whole ledge (`shower`, warned
   * 3 s, nowhere out of it; every `haul` while it hauls him, his fingers torn out of it). Dunna by the winch, with her bolts.
   */
  torno: {
    input: 60,
    stand: sec(1),
    pull: sec(1.5),
    reach: 1100,
    carryBp: [9000, 8000, 7000],
    spans: 3,
    climb: sec(21),
    haul: sec(14),
    slipBlows: 3,
    cable: 30,
    peck: sec(3),
    flock: { first: sec(10), every: sec(18), n: 3, perchBp: 8500 },
    rocks: { first: sec(8), every: sec(7), warn: sec(3), r: 1000, heroBp: 2000, blows: 1 },
    shower: { first: sec(14), every: sec(10), haul: sec(5), warn: sec(3), heroBp: 1100 },
    garg: { d: 2.5, crowD: 1, pace: 95, reach: 750, reload: sec(1.5), heroBp: 600, blows: 1, peckR: 1500 },
    dunna: { every: sec(2), hitD: 0.5, range: 5500 },
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * The shell (chapter 5, act 3; battle/shell.ts; the lesson of chapter 4): Tormo tied in the workshop's yard, the
   * nightmare under the stone of his shoulders and back. Nadd's line of drills with its own mill (`mill`, 3): four drills
   * with their force, arrow and clutch, two belts (I for drills 1 and 2, II for 3 and 4), and the chest's drill on the
   * same clutch and belt as one of them, its arrow the other way: in with it, it asks its force (`chestForce`); turning
   * its arrow's way, it cuts the chest and the heart cracks (`cracks`: lost at the third; the clutch is thrown out).
   * Each stone asks its force (2 or 3) for `cut`; asking more than the mill jams (`jamBp`, every clutch out, a cub
   * free). Tormo writhes and turns his other side to the line every `twist` (warned 3 s; his arms sweep `sweepR` round
   * him): which stone each drill reaches changes. Every twist the cable he is tied with whips the whole yard (`whipBp`
   * of the hero's life for each stone still on, and one more); it holds `holds` twists, then he is loose: lost. A stone that falls bursts over the whole yard (`shards`, warned 3 s, nowhere out of it). Ash
   * cubs (`cubs`, and `out` out of each stone) go for the drills in; one that reaches a drill stops it `stall`.
   */
  shell: {
    mill: 3,
    chestForce: 1,
    clutch: sec(1),
    lever: sec(1.5),
    reach: 1100,
    cut: sec(6),
    cracks: 3,
    jamBp: 1500,
    twist: { first: sec(22), every: sec(20), warn: sec(3), sweepR: 2300, heroBp: 2400, whipBp: 1000, holds: 5 },
    shards: { warn: sec(3), r: 2600, heroBp: 1800 },
    cubs: { first: sec(12), every: sec(12), n: 3, out: 2, stall: sec(8), stallR: 900, hpBp: 9000 },
    dunna: { every: sec(2), hitD: 0.5, range: 5500 },
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * Tormo (chapter 5, act 4, the path's enemy; battle/plates.ts; §5): what got into him glows in one plate at a time
   * (six, seven with the old dust). Blows on the glowing plate from its side (within `side` of its face) are blows on
   * the nightmare (`d` seconds of D, even over the plates); the hero's blows anywhere else are blows on Tormo's iron
   * (`ironD`): at 0 he breaks and it is lost. The yard's three crossbows (worked by Grada's quarrymen) turn to the plate
   * when it faces them, `boltD` each every `every`; a turret or a bot does only the plate's share.
   * Phase 1 (plates 1–2): he walks at the hero (`pace`); his fist (`fist`, warned 3 s, 1 hex), his stamp every so
   * often (`stamp`, a ring). Phase 2 (3–4): rocks off the cliff (`rocks`, warned 3 s; on a crossbow it stops `jam`);
   * a plate going out lets out cubs (`cubs`) that go for Nadd; the quarrymen stand before him with their picks. Phase 3
   * (5–7): the plate moves to another every `move` (`grilloMove` with Grillo's dust) unless it is struck, Tormo turning
   * it away; once a plate, his hammer (`hammer`, both hands, warned 3 s, 2 hexes). Soft time: from `softAt` the yard's
   * edge falls (`soft`, on the hero), faster from `softFast`. A plate going out breathes its black dust over the whole
   * yard `dust.warn` later (`dust.heroBp`, nowhere out of it). Each scar of a try lost past half leaves a plate dark.
   */
  plates: {
    d: 210,
    ironD: 70,
    side: 200,
    every: sec(1),
    boltD: 0.2,
    jam: sec(10),
    pace: 34,
    turn: 8,
    fist: { reach: 1300, r: 1000, warn: sec(3), heroBp: 2000, blows: 2, rest: sec(2) },
    stamp: { first: sec(9), every: sec(9), warn: sec(3), r: 2900, heroBp: 1500, blows: 1 },
    rocks: { first: sec(4), every: sec(5), warn: sec(3), r: 1000, heroBp: 2000, blows: 1 },
    cubs: { n: 3, hpBp: 9000 },
    move: sec(6),
    grilloMove: sec(5),
    hammer: { at: sec(3), warn: sec(3), r: 2000, heroBp: 4500, blows: 3 },
    dust: { warn: sec(3), heroBp: 2200 },
    softAt: sec(600),
    softFast: sec(780),
    soft: { every: sec(10), fast: sec(5), warn: sec(3), r: 1100, heroBp: 2000 },
    pick: { every: sec(2), hitD: 0.3, range: 1300 },
    dunna: { every: sec(2), hitD: 0.5, range: 5500 },
    end: sec(3),
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * The ranger's path, chapter 1 (docs/sendas/explorador.md, «Las piedras de Aster»; battle/cairns.ts, echo.ts,
   * fallen.ts). The knots (`cairns`, act 1): standing `read` by a cairn reads its cord (or brings a false one down,
   * waking `wrong` slingers and crows, `more` of each again with every one); slingers off the rim and soot crows the
   * while. The fog (`echo`, act 2): Sela's shout goes out at `speed` a tick to `far` and comes back off the dead ends'
   * walls; one shout every `cool`; the hero sees `sight` into the fog; crows out of it, and `dead` more at a dead end's
   * wall or a shout in one; in the fog the bow reaches `rangeBp` of its range (what can't be seen can't be aimed at). The fallen cairn (`fallen`, act 3, the test): `stand` on a base settles it; a wrong one wakes
   * a band and sends the Roller rolling; the Roller (`roller`, `d` seconds of D) comes in at `at`, curls every `every`
   * (`warn`, 3 s) and rolls straight at `speed` up to `len`, striking `heroBp` and pushing `push`; a standing cairn in
   * its way stuns it `stun`. `hpBp`, `dmgBp`: each act's nightmares over the chapter's common ones.
   */
  aster: {
    cairns: { read: sec(3), reach: 1100, sling: { first: sec(3), every: sec(9), n: 3 }, crows: { first: sec(8), every: sec(12), n: 5 }, wrong: { sling: 3, crows: 4, more: 1 }, hpBp: 12000, dmgBp: 21000 },
    echo: { speed: 250, far: 7000, cool: sec(3), sight: 2400, rangeBp: 5000, reach: 1300, crows: { first: sec(4), every: sec(5), n: 4, dead: 7 }, hpBp: 15000, dmgBp: 15000 },
    fallen: {
      read: sec(3),
      reach: 1100,
      stand: sec(3),
      sling: { first: sec(5), every: sec(12), n: 3 },
      crows: { first: sec(10), every: sec(16), n: 5 },
      wrong: { sling: 4, crows: 6 },
      roller: { d: 70, at: sec(12), pace: 55, bite: { every: sec(2), reach: 1100, heroBp: 900 }, every: sec(10), warn: sec(3), speed: 300, len: 14000, r: 900, heroBp: 1800, push: 1000, stun: sec(3), rest: sec(1) },
      hpBp: 12000,
      dmgBp: 16000,
    },
  },
  /** The ranger's path, chapter 2 (sendas-ranger.ts `GALO`; battle/cliff.ts): the cornice, the wall, the night on the wall. */
  galo: GALO,
};

/**
 * Ruy's line (docs/sendas/guerrero.md §4, «La línea de Ruy»): the ten shields by name, in the order Kel reads them
 * (the order they fell), and its eleven slots (1 by the fire to 11 under the snow's cornice; 6 is Harl's).
 */
export const LINE_NAMES = ['nim', 'joss', 'lisbet', 'ari', 'gerd', 'tome', 'sanne', 'fermin', 'juana', 'ruy'] as const;
export const LINE_SLOTS = 11;
export const LINE_HARL = 6;

/**
 * The large wedges' slopes (docs/sendas/guerrero.md §4): three rows of three rocks, each `<lane><grain>` (lanes 1 to
 * 7, `>` toward the higher lane, `<` the lower), rows from the top. Drawn once from 30 000 by
 * tools (the check is `laneSolve` in packages/sim battle/wedge-layout.ts, held by a test): each wants all four wedges
 * and has one answer only. The first is the document's example. A fight takes one, or its mirror, by its seed.
 */
export const AVALANCHE_SLOPES: readonly string[] = [
  '3>5>7<|4>6<7<|2>5>6<', '2<4>5<|2<3<4<|2<3<6>', '1<3<5<|2<4<7<|2<3<4<', '2>3<5<|1<4<7<|1>3<6<',
  '2<4<5<|1<3<4<|3<4>5<', '2<3>5>|1<4>6<|2>4>5>', '1<5<7>|3<4<7>|1<3<7<', '2<3>5>|4>5<6>|2<3>5>',
  '3>4>5>|2<4>6<|1>2>5>', '2>3<5<|4<5<6<|1>3<6>', '3>4>6>|1<4>5>|1<2>5>', '3<4<5<|4<6>7>|3<6<7>',
  '1<3<5<|3>4<5<|1>3<6<', '3>6<7>|2<4>5>|2<4>5>', '5<6>7>|1<3<4<|1<3<4>', '3<5<6>|4<6<7<|3<4<7>',
  '3>4<7<|1<4>5>|1>2>5>', '3>4>5>|2>3>4>|1>4>5>', '3<5<6>|2<4<7>|3<4>7<', '2>3<5<|2<4<7>|3<4<5<',
  '1>3>5<|1<4>5>|3>5>7>', '1>3>7<|3>4>5>|2>4>5>', '3>6<7<|3>4>5>|2>4>5>', '3>5<7<|3<4<5<|3<5<6>',
  '2>4<5<|1>3<4<|2>3<6>', '2<5<6<|3<4<6>|3<5<7>', '4>5<6<|3<4<7<|2>3<7<', '1<2<5<|1<3<4<|3<6<7>',
  '3<4>5<|2<4<5<|3<4<6>', '2>3>7>|2>4>5>|5>6>7<', '3>5>6<|1>4>7<|4<5>6>', '1>3>5>|1<2<4>|1>5>7<',
  '3>5>6<|1>4>7>|4<5>6<', '4>5<6>|3<4<6<|1>3<7>', '2>3<5<|2<4<5<|3<4>6<', '3>5<7<|4>5>7<|5>6<7>',
  '1<3<5<|3>4<7<|3<5<6>', '3<5<6>|1<4<7<|2>3<5<', '3>5>6<|4>6<7>|2<5>6>', '1<3>4<|2>4>5>|2>4>5>',
  '2>3<5<|1<2<4<|3<4>6<', '3>5<7<|3<4<7<|2>3<4>', '3>6>7>|3>4>5>|2<4>5>', '1<3>5>|2<3>4>|4<5>7>',
  '2<5<7>|3<4<7>|2>3<4>', '1<5<6<|2<3<4<|1<3<4>', '3>5<6>|1<4>5>|1<5>6<', '5<6>7>|1<3<4<|3<6>7<',
  '3>5>7>|1<4>6<|1<4<5>', '2<5<7>|2>3<4<|2>3<6<', '2>3>4>|1<4>5>|2>4>5>', '2<5<6>|3<4<7>|1<3<5<',
  '2<3>5<|2<4>5>|2<5>6<', '2<3>6>|4>5>7>|4<5>6<', '2<4>5<|3<4<6<|2>3<7<', '2>4<5<|3<4<7>|1<3<5<',
  '5<6<7<|3<4<6>|3<4>7<', '1<5<6<|1<3<4<|2<3<7<', '3<5<7>|3>4<5<|3<6>7>', '1>3<5<|4<5<6<|3<4<6>',
  '3<5<6>|1<4<6>|3<4>6<', '2>3<5<|4<6>7<|1>3<4<', '3>5>6>|1<4>7<|2>5>6>', '2<5<6>|3<4<7>|1<3<6<',
  '2<3>4>|4>5>6>|1<4>5>', '1>3>6<|1>4>5>|2>5>6>', '3>5>6<|1<3>4>|1<5>6>', '3>5>7<|1<4>7<|1<5>7<',
  '3<4>5<|3>4<7>|3<4<6>', '3<4>5<|4<5<7>|2<3<7>', '2>3>7<|4>5>6>|4>5>7<', '3>5<6<|2<3<4<|2>3<4<',
  '1<3>5<|2>3<4<|1<3<6>', '1>2<3>|4>5>6<|1<2<5>', '2<3>6>|1<4>5>|5>6<7<', '3>4>7>|4>5>6<|1<3>5>',
  '1<3>6>|4>5>7>|2>3>5>', '1>3>5>|1>2<4>|1>4>5>', '3>4>5>|4>6>7<|2<5>6>', '4>5<6>|3<4<7>|1>3<5<',
  '3>4>7<|1>4>5>|4>5>6<', '3<4>5<|1>4<6>|1<3<6<', '3>4<5>|1>4>5<|1>4>5>', '2<3>5<|4>5>7<|2>4<5>',
  '3>4>5<|1<3<4<|1>3<6>', '1>3>6>|4>5>7<|2<5>7<', '4>5<6<|2<3<4<|3<4<5<', '3<5<7<|1<4<6<|1>3<4<',
  '3<5<7>|4<6>7>|1>3<6>', '3>5>7<|1>4>6<|1>3>5>', '3<5<7<|4<6>7>|3<4>6<', '1>3>5<|2<4>5>|1>2>5>',
  '3>5>7>|4>5<6>|1<5>6>', '1<5<6<|2>3<4<|3<6<7<', '1<3>5<|4>5>7>|1<4>5>', '4<5<7<|1<3<4<|1>3<7<',
  '3>4<5>|4>6<7<|3>5>6<', '1<3>5>|4>6<7>|1>5>6<', '2<5<6>|3<4<5<|1>3<4>', '3<5<7<|1>4<7>|3<4>7<',
  '3>5<6<|4>5>7>|1<5>7<', '3<5<7>|2>4<7>|1>3<4<', '3<5<7>|2<4<7<|2>3<7>', '3>5>6<|2<4>6<|1>2<5>',
  '5<6>7<|2<3<4<|2>3<7<', '3<5<6<|2>4<6<|1>2>3<', '3<5<6<|2<4<7>|2<3<7>', '1<2<5<|2>3<4<|1>3<5<',
  '3<5<6<|1<4<5<|1<3<4<', '3<4<5<|2<4<6<|1<3<7>', '1<3>5>|4>6<7<|1<5>7<', '3>5>6>|4>5<7<|1<5>6<',
  '1>3>5>|2<4>7<|1<4<5>', '3>5>7<|1>4>5<|4<5>7>', '1<3<5<|2>4<7>|2>3<6>', '2>4>5<|3<4<6<|2<3<7<',
  '3<4<5<|2<4<6<|2<3<7<', '1>3>5>|1<4>7>|3>5>6>', '1>3>5>|2>4>6<|5>6>7>', '1<4>5<|1<3<4<|2<3<7<',
  '3<5<7>|1>3>4<|1>3<4>', '1<3>5>|2<4>6<|1<2<5>', '1>3<5<|1<4<5<|1>2>3<', '3>4<5<|2<4>5>|2<5>6<',
  '3<5<6>|2>4<7>|3<5<6>', '3>4>5<|3<4<7<|3<4>7>', '1>3>7<|4>5>7>|1>2>5>', '3<5<6>|1>4<6>|3<4>7<',
  '1<2<5<|2<3<4<|2<3<4<', '3>4>5>|2<4>7<|2>5>6<', '3>4>6>|4>5>6<|1>2>5>', '3>5<7<|1>4>5>|4<5>6<',
  '1<5<6>|3<4<5<|1<3<6<', '2<3>4<|1<4>5>|4>5>7<', '3>5<7>|2>3<4<|3<6<7>',
];

/** A camera key of a cinematic: from where and at what (hex coordinates and height in hexes), and how long to the next (ms). */
export interface SendaCam {
  from: [q: number, r: number, h: number];
  at: [q: number, r: number, h: number];
  ms: number;
}

/** Someone standing in a cinematic: a person of the path, the hero (`hero`), or a nightmare kind. */
export interface SendaFigure {
  who: string;
  q: number;
  r: number;
  /** Where it looks, in sixths of a turn (0 along +q; fractions turn it part of the way). */
  facing?: number;
  pose?: 'stand' | 'sit' | 'kneel' | 'walk' | 'fight' | 'lie';
  /** A walker: it sets off this long after the scene opens (ms), at this pace (sim units a tick), for this long (ms). */
  delay?: number;
  speed?: number;
  walkMs?: number;
  /** Only with these marks (the warrior's chapter 5: Wenn by the fire if she climbed, Dalmo if she stayed). */
  if?: SagaCond;
}

export type SendaSky = 'day' | 'dusk' | 'night' | 'dawn';
export type SendaWeather = 'clear' | 'snow' | 'wind' | 'storm' | 'fog' | 'rain';

/**
 * A scene's cinematic with the game's engine (docs/SENDAS.md, «Decisiones para construir»): the place, its light and
 * weather, the camera moving between keys, the figures in it; the lines go below and it can be skipped.
 */
export interface SendaCine {
  place: string;
  sky?: SendaSky;
  weather?: SendaWeather;
  cam: SendaCam[];
  figures?: SendaFigure[];
  /** A campfire burning there (the campfire's scenes). */
  fire?: { q: number; r: number };
  /** Seen through a spyglass (a round view, its rim dark): the opening of the warrior's path. */
  scope?: true;
  /** Buildings standing in the place (the bastion's barracks). */
  structures?: { type: StructureType; q: number; r: number }[];
  /** The counterweight's three spans coming down together as it opens (the warrior's chapter 2 close). */
  spans?: true;
  /** A memory: the scene cold and grey (the warrior's chapter 3, Ruy's second night). */
  memory?: true;
  /** The fort's roof tipping over the far yard as it opens (the warrior's chapter 3: Fermín's «¡Ahora!»). */
  tip?: true;
  /** Ruy's line standing across the pass, its shields dark until their names are said (the warrior's chapter 5). */
  line?: true;
  /** The line whole and red, Harl's shield in its gap (the warrior's chapter 5 close). */
  whole?: true;
  /**
   * A video made once for it (docs/DECISIONS.md, 2026-11-03: the paths' cinematics go to pregenerated video), by key
   * (apps/client campaign/cinematics.ts `SENDA_VIDEOS`); while there is none for the key, this scene with the engine.
   */
  video?: string;
  /**
   * The paladin's shore with its pools full and its eyes open (the chapter 1 close: the line of still water between them).
   */
  lit?: true;
  /** The monastery's dam opened: the white wall of water down the ravine and the gorge, the wheel turning alone (the paladin's chapter 4). */
  flood?: true;
  /** The Order's torches round the keep at the summit, seen from the gorge (the paladin's chapter 4 close). */
  torches?: true;
  /** The seventh altar's eye open over it (the paladin's chapter 5 close and epilogue). */
  eye?: true;
  /** Nadd's workshop broken into: its door down, Tormo's chest open and empty (the artificer's chapter 4 close). */
  broken?: true;
  /** Olmo's last face on the chamber's wall, its mouth carved by the hero (the druid's chapter 5 close). */
  face?: true;
  /** The Grandsire breathing deep, its amber turned green (the druid's chapter 5, healed). */
  breath?: true;
  /** Another cinematic in its place when the hero's marks say so (the druid's chapter 5: the Grandsire fallen). */
  swap?: { if: SagaCond; cine: SendaCine };
  /**
   * Where the scene goes when the bastion lacks a building it stands in (docs/SENDAS.md, «Siempre hay dónde estar»): the
   * paladin's Temple, else the yard before the keep.
   */
  alt?: { unless: StructureType; cine: SendaCine };
}

/** One line: who says it (a person of the path, `narrator` or `hero`) and the marks it needs. */
export interface SendaLine {
  id: string;
  who: string;
  if?: SagaCond;
  /** Said, it lights this slot of Ruy's line red (the warrior's chapter 5, the eleven names; 1 to 11). */
  light?: number;
  /** Said, the scene's own moment (the paladin's chapter 5 speech: `kneel`, the knights and Nuño on one knee, the water trembling). */
  cue?: string;
}

/** A scene: lines (some only after a mark), its cinematic, and the marks it sets when played through. */
export interface SendaScene {
  kind: 'scene';
  id: string;
  lines: SendaLine[];
  cine?: SendaCine;
  set?: string[];
}

/** A campfire question: its answer's lines and the mark asking it sets. */
export interface SendaQuestion {
  id: string;
  lines: SendaLine[];
  set?: string[];
  if?: SagaCond;
}

/** The campfire between acts 2 and 3 (docs/SENDAS.md, «Los PNJs»): three questions, SENDA.asks of them asked. */
export interface SendaFire {
  kind: 'fire';
  id: string;
  /** Who sits at it. */
  with: string[];
  questions: SendaQuestion[];
  cine?: SendaCine;
}

/** The one personal decision of a path (chapter 4): two options, each with its marks. */
export interface SendaChoice {
  kind: 'choice';
  id: string;
  lines: SendaLine[];
  options: { id: string; set: string[] }[];
  cine?: SendaCine;
}

/**
 * A person of the path on the field (battle/senda.ts): their life in blows of the chapter's common nightmares; what
 * they do (`follow` the hero, `work` at `spots` one after another for `ticks` each, `wait` at `at`); `vital`: their
 * falling loses the act (the default).
 */
export interface SendaNpcDef {
  id: string;
  does: 'follow' | 'work' | 'wait';
  hits: number;
  at?: { q: number; r: number };
  /** Where it works, one after another (absent on a field with `dig`: the field's spots, from the seed). */
  spots?: { q: number; r: number }[];
  ticks?: number;
  vital?: boolean;
  /** Never struck: someone waiting out of the fight (Wenn on her mule, at the clearing's edge). */
  safe?: true;
  /** Works only with the hero this near (hexes): «llegar a cada escudo y aguantar al lado». */
  near?: number;
  /** Blows in one piece of work that make it start over (absent: every blow does, after a short stop). */
  drop?: number;
  /** How far behind the hero it follows (hexes). */
  follow?: number;
  /** Drawn as this miniature in this fight (packages/render models/sendas.ts): Wenn waiting on her mule. */
  model?: string;
  /** Stands this high over the ground (sim units, a hex 1000): Fermín on the fort's roof. */
  lift?: number;
  /** On the field only with these marks (the warrior's chapter 5: Wenn, if she climbed). */
  if?: SagaCond;
  /** A thing to keep, not a person (the paladin's second altar): its life in blows and its name, drawn by the place. */
  prop?: true;
}

/**
 * A line said during a fight, fired by the sim (once): at the start, at the goal's half, when the boss comes in, when a
 * person is first hit (`npc`), or `t` ticks in. `who` says it (a person of the path, or the hero's own memory face).
 */
export interface SendaSay {
  on: 'start' | 'half' | 'bossIn' | 'npcHit' | 'at' | 'cue';
  t?: number;
  who: string;
  line: string;
  npc?: string;
  if?: SagaCond;
  /**
   * A cue of the fight's own (`on: 'cue'`): `dig<n>` a person starts the n-th work, `done<n>` finishes it, `plant` the
   * first shield goes into a gap, `push` the rock is pushed, `fall` the cornice comes down.
   */
  cue?: string;
  /** Said this long after its moment (ticks). */
  delay?: number;
}

/**
 * How a fight is won: lasting `ticks`, felling its boss, the people's work done `need` times, or every nightmare
 * down. It is lost if the hero falls, a vital person falls, or the ground's own rule loses it (the node).
 */
export type SendaGoal =
  | { kind: 'time'; ticks: number }
  | { kind: 'boss' }
  | { kind: 'work'; need: number }
  | { kind: 'clear' }
  | { kind: 'wedge' }
  /** The span wound up (the hooks), Kel across (the windbreak), the three spans level (the counterweight). */
  | { kind: 'winch' }
  | { kind: 'cross' }
  | { kind: 'weight' }
  /** The mule at the fort's door (the icy climb); the old troll down (the roof that tips). */
  | { kind: 'door' }
  | { kind: 'troll' }
  /** Kel at the ice cave's mouth (the loose slope); at Fermín and Juana (the ice cave); the avalanche past the cave (the large wedges). */
  | { kind: 'mouth' }
  | { kind: 'hall' }
  | { kind: 'avalanche' }
  /** Harl at the pass's mouth (the ledge of the wind); Ruy's line laid (the camp); the wind stopped (the beam); the Ventisquero broken (the pass). */
  | { kind: 'crag' }
  | { kind: 'line' }
  | { kind: 'beam' }
  | { kind: 'storm' }
  /** The paladin's: the altar's pool full (the canals); both pools full as the water goes (the water that goes). */
  | { kind: 'pool' }
  | { kind: 'ebb' }
  /** Ser Marit at the gate house (the salt flats' fog); the altar's pool dry (the gates). */
  | { kind: 'house' }
  | { kind: 'drain' }
  /** The bridge's altar: every band down (the two ends), the cistern full (the gutters), both mammoths down (the night). */
  | { kind: 'ends' }
  | { kind: 'cistern' }
  | { kind: 'mammoth' }
  /** The monastery: the Marea passed (the memory at the door), the right cell shut (the spectres), the sixth altar lit and the great sentinel down (the cloister). */
  | { kind: 'tide'; ticks: number }
  | { kind: 'cell' }
  | { kind: 'sixth' }
  /** The seventh altar: the stair cleared (the rubble), the cistern reached (the descent's last hall), the island bare with the ring kept (the well), La Que Huyó down with the oath sworn (the fight). */
  | { kind: 'rubble' }
  | { kind: 'reach' }
  | { kind: 'ring' }
  | { kind: 'fled' }
  /** Nadd's workshop: Grillo at the door (the goat path), at the lift with Dunna (the sleeping workshop), the belt on and the Overseer out (the wheel and the belt). */
  | { kind: 'grillo' }
  | { kind: 'tormo' }
  | { kind: 'lift' }
  /** The cable: the three pieces down to the car (the crane), the car across (the abyss), the train right and La Picotera down (the gearbox). */
  | { kind: 'crane' }
  | { kind: 'span' }
  | { kind: 'haul' }
  /** The heart: the three at the Médula's mouth (the deep road), the trip-hammer free (the river), the stone off the rock and La Tenaza down (the cams). */
  | { kind: 'marrow' }
  | { kind: 'unjam' }
  | { kind: 'stone' }
  /** The eyes: the four struts wedged (the memory), the drill hall reached (the carts), both walls down and La Barrena down (the drills). */
  | { kind: 'struts' }
  | { kind: 'drillHall' }
  | { kind: 'geode' }
  /** Tormo: his four joints locked (the knees), pulled off the Raja (the winch), the three stones off (the shell), free (the plates). */
  | { kind: 'knees' }
  | { kind: 'raja' }
  | { kind: 'shell' }
  | { kind: 'free' }
  /** The Long Hunt (the barbarian's chapter 1): twelve of the pack down with the herd kept (the corral), the Grey Wolf's tracks followed to their end (the rain, the ledge). */
  | { kind: 'herd' }
  | { kind: 'trail' }
  /** The Long Hunt's chapter 2: the last firm arch reached in the wind (the arches), the vertebra with Sigrun's three marks (the leaps), the boat in the water with the hero in it (the den). */
  | { kind: 'way' }
  | { kind: 'leaps' }
  | { kind: 'boat' }
  /** The Long Hunt's chapter 3: the bank of big floes reached in the Wolf's wake (the wake), six big waves passed (the swell), the old howler down (the test). */
  | { kind: 'wake' }
  | { kind: 'waves' }
  | { kind: 'elder' }
  /** The Long Hunt's chapter 4: three bands with the camp kept (the floes), the ice broken where her last track ends (the lagoon), the Ash Seal down with the fire lit (the memory). */
  | { kind: 'floes' }
  | { kind: 'lagoon' }
  | { kind: 'seal' }
  /** The Long Hunt's chapter 5: the cave's mouth reached with Ulla (the climb), two mouths shut (the mouths), the ash bed reached (the bed), the Grey Wolf down (the fight). */
  | { kind: 'climb' }
  | { kind: 'mouths' }
  | { kind: 'bed' }
  | { kind: 'wolf' }
  /** The cleric's last road (content/camino.ts): the stretcher at the end of its way; the chapel's lamp lit. */
  | { kind: 'litter' }
  | { kind: 'lamp' }
  /** Its chapter 3: the night in the corral over (the clock). */
  | { kind: 'night' }
  /** Its chapter 4: the dawn with the altar's lamp lit (the memory of Aud's vigil); the altar and the door lit at once (the nave). */
  | { kind: 'dawn' }
  | { kind: 'mirrors' }
  /** Its chapter 5: the six lamps lit (the lamps), the procession down (the atrium), the candle lit at dawn (the Doliente). */
  | { kind: 'lamps' }
  | { kind: 'procession' }
  | { kind: 'mourn' }
  /** The Circle's wood (the druid's chapter 1): up to Olmo's clearing (the thaw), the face found (the clearing held), the roots on the islet (the bridge). */
  | { kind: 'glade' }
  | { kind: 'face' }
  | { kind: 'bridge' }
  /** The Slope of the kilns (the druid's chapter 2): the kilns covered, the strip opened, the cone opened by the counterfire. */
  | { kind: 'kilns' }
  | { kind: 'strip' }
  | { kind: 'cone' }
  /** The autumn wood (the druid's chapter 3): Ceda at Olmo's maple (under the leaves), the rot stopped before the houses (the memory), the rot out (what lies below). */
  | { kind: 'maple' }
  | { kind: 'houses' }
  | { kind: 'rot' }
  /** The Grandsire's crown (the druid's chapter 4): up to the top (the boughs), Hob's leg bandaged (the hollow), the sap running (the test). */
  | { kind: 'crown' }
  | { kind: 'bandage' }
  | { kind: 'sap' }
  /** Inside the Grandsire (the druid's chapter 5): the mouths open and clean (the buds below), the net cut (what lies below), the Carcoma down. */
  | { kind: 'deepMouths' }
  | { kind: 'net' }
  | { kind: 'carcoma' }
  /** The ranger's chapter 1: the third cairn found (the knots), out of the Furrow with Sela (the fog), on the fallen cairn's base (the test). */
  | { kind: 'cairns' }
  | { kind: 'echo' }
  | { kind: 'fallen' }
  /** The ranger's chapter 2: the anchor driven (the cornice), Aster's ledge reached (the wall), the wall's foot (the night). */
  | { kind: 'cliffEdge' }
  | { kind: 'cliffWall' }
  | { kind: 'cliffNight' }
  /** The burnt library (the mage's chapter 1): the catalog reached (the cellars), the three lecterns open, the cabinet's three seals broken at once. */
  | { kind: 'catalog' }
  | { kind: 'locks' }
  | { kind: 'cabinet' };

/** A band of nightmares on a path's own clearing: `n` of a kind at a spot of the rim, every so often, from `from` to `until` (ticks). */
export interface SendaFoes {
  kind: NightmareType;
  n: number;
  from: number;
  every: number;
  until: number;
}

/** A fight: where, what comes, how it is won, who is there and what they say. */
export interface SendaFight {
  kind: 'fight';
  id: string;
  /**
   * The ground it is built on: a saga's incursion clearing held or with a boss, crypt rooms, a clearing of its own
   * (`field`: its `radius`, its `foes`, the people's `dig` spots), the ledge of the wedges (`wedge`, SENDA.wedge), or the
   * cut bridge's: the rim and its hooks (`hooks`, SENDA.hooks), the platform in the wind (`gusts`, SENDA.gust), the
   * counterweight (`weights`, SENDA.weight); the fort's: the icy climb (`slope`, SENDA.slope), the yards under the roof
   * that tips (`roof`, SENDA.roof: the second night remembered with Fermín on it, or the test with the old troll); the
   * avalanche's: the loose slope (`loose`, SENDA.loose), the ice cave (`cave`, SENDA.cave), the large wedges
   * (`avalanche`, SENDA.avalanche). The paladin's Way of the Altars: the glass shore's canals (`canals`, SENDA.canal), the
   * water that goes (`ebb`, SENDA.ebb), the second altar held under the drake (`kindle`, SENDA.kindle); the salt flats
   * in the fog (`fog`), their gates (`sluice`) and the altar held with Marit's salt (`salt`), SENDA.salt.
   */
  mode: 'hold' | 'boss' | 'descent' | 'field' | 'wedge' | 'hooks' | 'gusts' | 'weights' | 'slope' | 'roof' | 'loose' | 'cave' | 'avalanche' | 'crag' | 'camp' | 'beam' | 'storm' | 'canals' | 'ebb' | 'kindle' | 'fog' | 'sluice' | 'salt' | 'ends' | 'gutters' | 'vigil' | 'porch' | 'cells' | 'cloister' | 'rubble' | 'well' | 'fled' | 'cornice' | 'workshop' | 'gears' | 'crane' | 'gondola' | 'gearbox' | 'vents' | 'ember' | 'cams' | 'struts' | 'carts' | 'drills' | 'knees' | 'torno' | 'shell' | 'plates' | 'soot' | 'lecterns' | 'cabinet' | 'cairns' | 'echo' | 'fallen' | 'cliffEdge' | 'cliffWall' | 'cliffNight' | 'pens' | 'tracks' | 'brink' | 'arches' | 'leaps' | 'den' | 'wake' | 'swell' | 'elder' | 'floes' | 'lagoon' | 'seal' | 'climb' | 'mouths' | 'deepMouths' | 'ashbed' | 'wolf' | 'street' | 'mirrors' | 'limpets' | 'ridge' | 'catwalk' | 'chains' | 'col' | 'frost' | 'corral' | 'audVigil' | 'nave' | 'lamps' | 'procession' | 'renave' | 'mourn' | 'thaw' | 'sprouts' | 'rootBridge' | 'kilns' | 'firebreak' | 'counterfire' | 'leaves' | 'blight' | 'underroots' | 'boughs' | 'hollow' | 'sap' | 'deepnet' | 'carcoma';
  radius?: number;
  foes?: SendaFoes[];
  /** Things to dig out of the snow on a field: how many, and how far out from the middle (hexes). */
  dig?: { n: number; near: number; far: number };
  /** The renderer's dressing and light. */
  place?: string;
  night?: boolean;
  weather?: SendaWeather;
  land?: 'quarry' | 'forest' | 'crystal' | 'altar' | 'burnClearing';
  /** Nightmares: × the incursion budget (hold, boss) or × the crypt's room points (descent). */
  budgetBp: number;
  rooms?: number;
  bestiary?: Partial<Record<NightmareType, number>>;
  inkBp?: number;
  /** Its boss, with its life in seconds of D (the reference hero's damage a second at the chapter's scale). */
  boss?: { kind: EnemyKind; tier: 1 | 2 | 3; d: number };
  goal: SendaGoal;
  npcs?: SendaNpcDef[];
  says?: SendaSay[];
  /** A memory: played as this character (same kit, level and gear; its miniature and name; no legacy, no feats). */
  memory?: string;
  /** The chapter's test: its last won replay is kept. */
  trial?: true;
  /** The path's enemy: remembers its wounds from one try to the next (SENDA.scars). */
  foe?: true;
  /** Numbers the fight leaves that the story keeps (chapter 5: the line from 0 to 10, the beam ready or not). */
  keep?: string[];
  /** Of those, the ones that also set a mark of their name when not 0 (chapter 4: a shield lost to the avalanche). */
  marks?: string[];
  /** Each scar of the path's enemy, in bp of its life, when not SENDA.scars.hpBp (La Que Huyó's: 8 %). */
  scarBp?: number;
}

export type SendaAct = SendaScene | SendaFire | SendaChoice | SendaFight;

export interface SendaChapter {
  id: string;
  /** The hero's level it opens at (SENDA.levels by its place when absent). */
  level?: number;
  acts: SendaAct[];
  /** Who says its `closed` line when the tries run out. */
  closedBy: string;
  /** Acts before this one, once reached, stay won: a close sends back here (chapter 5), not to the first. */
  keep?: string;
  /** The look's piece it gives (the last chapter: the whole look). */
  piece?: string;
  /** Beginning it sends the group a push with the hero's name (chapter 5), once per round of tries. */
  call?: true;
  /** XP for it (SENDA.xp by its place when absent). */
  xp?: number;
}

/**
 * How a person of the path is drawn (its miniature in the scenes, on the field and in its portrait): one of the sagas'
 * people (`npc`), or a hero's miniature of a class at a tier (1 to 4) wearing a look (heroLook's string). Stage 2 adds
 * the paths' own miniatures here.
 */
export interface SendaCast {
  npc?: NpcId;
  cls?: HeroClass;
  tier?: 1 | 2 | 3 | 4;
  look?: string;
  /** The path's own miniature (packages/render models/sendas.ts), by name. */
  model?: string;
  /** A voice without a figure (a diary, a letter): no face, its name over the line. */
  voice?: true;
  /** Its face from this miniature instead (Tessa lies on her stretcher: her portrait is of her standing). */
  portrait?: string;
}

export interface SendaDef {
  id: string;
  cls: HeroClass;
  chapters: SendaChapter[];
  /**
   * How many chapters the path has by its design (docs/sendas/*.md), written or not: the «Capítulo n de N» counts these,
   * so a path playable up to chapter 1 still reads as one of five. The written ones are `chapters`.
   */
  planned?: number;
  /** Its people's miniatures, by who (`senda.<id>.npc.<who>`). */
  cast?: Record<string, SendaCast>;
  /** Only to try the pipes (no real path yet): a path for a class whose own isn't written. */
  test?: true;
}

/** The path's chapters by its design, for «Capítulo n de N» (never fewer than the written ones). */
export const sendaChapters = (def: SendaDef): number => Math.max(def.planned ?? 0, def.chapters.length);

/**
 * How many of a path's chapters can be played now: its class's SENDA_READY (a test path: all of its own), never past the
 * written ones. 0: the class's path is off.
 */
export const sendaReady = (def: SendaDef): number => Math.min(def.test ? def.chapters.length : (SENDA_READY[def.cls] ?? 0), def.chapters.length);

const ln = (id: string, who: string, cond?: SagaCond): SendaLine => (cond ? { id, who, if: cond } : { id, who });
/** A line of the eleven names (the warrior's chapter 5): said, it lights its slot of the line. */
const nm = (id: string, who: string, light: number, cond?: SagaCond): SendaLine => ({ ...ln(id, who, cond), light });
/** The warrior's chapter 4 decision, as chapter 5 reads it: Wenn climbed with Harl, or stayed with Kel. */
const W: SagaCond = { flag: 'wennClimbed' };
const S: SagaCond = { flag: 'wennStayed' };

/**
 * The warrior's path, La Compañía del Paso (docs/sendas/guerrero.md): Wenn, Harl's widow, wants the eleven shields of the
 * Company home; Kel, her grand-nephew, carries them. Chapter 1, «Once nombres»: the opening's cinematic (a spyglass up
 * the Spine to a line of red dots in the snow) and the barracks; the pines below (Kel digs three shields out while the
 * hero keeps the wolves off him); the guard house (four rooms down to the well's patio and its ogre, the first page of
 * Ruy's diary nailed to the door); the campfire with Wenn; and the test, the ledge (the wedges, small: three shields in
 * six gaps of a low wall, the column of sixteen sent under the cornice). Chapter 2, «El puente cortado»: the gorge and
 * the bridge Ari and Gerd cut; the hooks on this side's edge (Kel winds the span up, the hero cuts the ropes it can);
 * the windbreak on the platform in the middle (covers planted against gusts that change side); the platform's scene
 * and the campfire with Kel; and the test, the counterweight (three pans pulling each other, the shell serpent under
 * the stone), closing with the spans down and Dalmo crossing them. Chapter 3, «La noche en el fuerte»: the icy climb to
 * the buried fort (rocks, wolves, the peat troll and Kel's torch, Dalmo by the mule); Sanne and Tomé at the cold hearth;
 * the second night remembered as Ruy (Fermín's roof); the campfire with Wenn and Dalmo; and the test, the roof that
 * tips, with the old troll; closing at dawn with the diary's torn pages. Chapter 4, «El alud»: the loose slope (Kel hauls
 * the stretcher up while fronts come down; a rock's lee to hide in), the ice cave (fallen sentinels to get round, Fermín
 * and Juana at its end), the campfire with Wenn and Kel, and the test, the large wedges (seven lanes, nine rocks with
 * their grain, four wedges); the second avalanche that buries Kel, Harl alive, and the path's one decision.
 */
const WARRIOR: SendaDef = {
  id: 'warrior',
  cls: 'warrior',
  planned: 5,
  cast: {
    brann: { npc: 'brann' },
    wenn: { model: 'wenn' },
    kel: { model: 'kel' },
    diary: { voice: true },
    diary1: { voice: true },
    farmer: { model: 'farmer' },
    farmer2: { model: 'farmer2' },
    dalmo: { model: 'dalmo' },
    // Chapter 3: the second night remembered, played as Ruy, with the six still standing.
    ruy: { model: 'ruy' },
    fermin: { model: 'fermin' },
    juana: { model: 'juana' },
    sanne: { model: 'sanne' },
    tome: { model: 'tome' },
    harl: { model: 'harl' },
    diary2: { voice: true },
    // Chapter 4: Ruy's margin note, Dalmo's mule with the stretcher, Fermín and Juana in the ice, old Harl (a stranger first).
    diary4: { voice: true },
    mule: { model: 'muleLoad' },
    pair: { model: 'frozenPair' },
    stranger: { model: 'harlOld' },
    harlOld: { model: 'harlOld' },
    // Chapter 5: the torn pages of day three, the line written under them, the farms' woman, the words in Harl's shield.
    diary3: { voice: true },
    diary5: { voice: true },
    farmwife: { model: 'farmwife' },
    harlShield: { voice: true },
  },
  chapters: [
    {
      id: 'names',
      closedBy: 'wenn',
      piece: 'ribbon',
      acts: [
        {
          // A spyglass at dawn, up the Spine over the clouds to a line of red dots in the snow.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-warrior-1',
            place: 'spineFar',
            sky: 'dawn',
            scope: true,
            cam: [
              // From over the sea of cloud: the whole massif first, then up its face to the pass and its red dots.
              { from: [-33.3, 66.7, 11.5], at: [6.67, -13.3, 8.17], ms: 4200 },
              { from: [-30.6, 61.1, 11.5], at: [6.67, -13.3, 10.67], ms: 4200 },
              { from: [-22.2, 44.4, 11.1], at: [6.9, -13.8, 9.83], ms: 3800 },
              { from: [-17.2, 34.4, 10.67], at: [6.9, -13.8, 9.5], ms: 3600 },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'brann'), ln('l2', 'brann'), ln('l3', 'wenn'), ln('l4', 'wenn'), ln('l5', 'wenn'), ln('l6', 'kel')],
          cine: {
            place: 'barracks',
            sky: 'dawn',
            weather: 'clear',
            cam: [
              // Wide, from the yard's far side: the hall, its tower and the dawn sky over the curtain wall.
              { from: [-6, 8.7, 2.0], at: [3.1, -3.9, 2.5], ms: 7000 },
              { from: [-1, 3.2, 1.3], at: [0.6, -0.2, 0.8], ms: 9000 },
              { from: [1.5, 2.4, 1.1], at: [0.2, -0.4, 0.85], ms: 9000 },
            ],
            figures: [
              { who: 'brann', q: -1, r: 0, facing: 0.4, pose: 'stand' },
              { who: 'wenn', q: 1, r: -1, facing: 2.4, pose: 'stand' },
              { who: 'kel', q: 2, r: -1, facing: 2.8, pose: 'stand' },
              { who: 'hero', q: 0, r: 1, facing: 5.2, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'fight',
          id: 'pines',
          mode: 'field',
          place: 'pines',
          weather: 'wind',
          radius: 6,
          budgetBp: 10000,
          dig: { n: 3, near: 2, far: 4 },
          foes: [
            { kind: 'goblin', n: 2, from: sec(4), every: sec(6), until: sec(600) },
            { kind: 'wolf', n: 4, from: sec(10), every: sec(18), until: sec(600) },
          ],
          goal: { kind: 'work', need: 3 },
          npcs: [
            { id: 'kel', does: 'work', hits: 15, ticks: sec(12), near: 2, drop: 3 },
            { id: 'wenn', does: 'wait', hits: 1, at: { q: -6, r: 3 }, safe: true, vital: false, model: 'wennMule' },
          ],
          says: [
            { on: 'cue', cue: 'done1', who: 'kel', line: 'l1' },
            { on: 'npcHit', npc: 'kel', who: 'kel', line: 'l2' },
            { on: 'cue', cue: 'dig3', who: 'kel', line: 'l3', delay: sec(3) },
          ],
        },
        {
          kind: 'scene',
          id: 'edge',
          lines: [ln('l1', 'wenn'), ln('l2', 'wenn')],
          cine: {
            place: 'pines',
            sky: 'day',
            weather: 'wind',
            cam: [
              // Inside the clearing, off the wood's edge (from out there the camera flew through a fir's trunk).
              { from: [1.4, 2.6, 1.7], at: [-1.2, 1.2, 0.7], ms: 8000 },
              { from: [0.9, 2.4, 1.25], at: [-1.3, 1.3, 1.25], ms: 9000 },
            ],
            figures: [
              { who: 'wenn', q: -2, r: 2, facing: 0.2, pose: 'stand' },
              { who: 'kel', q: -1, r: 1, facing: 3.4, pose: 'kneel' },
              { who: 'hero', q: 0, r: 1, facing: 3.1, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'fight',
          id: 'house',
          mode: 'descent',
          place: 'guardhouse',
          rooms: 2,
          budgetBp: 10500,
          bestiary: { skeleton: 3, slinger: 2 },
          boss: { kind: 'ogre', tier: 1, d: 40 },
          goal: { kind: 'boss' },
          npcs: [{ id: 'kel', does: 'follow', hits: 15, follow: 2 }],
          says: [
            { on: 'bossIn', who: 'kel', line: 'l1' },
            { on: 'bossIn', who: 'wenn', line: 'l2', delay: sec(3) },
          ],
        },
        {
          kind: 'scene',
          id: 'page',
          lines: [ln('l1', 'diary1'), ln('l2', 'diary')],
          cine: {
            place: 'guardhouse',
            sky: 'day',
            cam: [
              { from: [0.5, 1.6, 1.5], at: [1.1, -2.2, 0.9], ms: 8000 },
              { from: [0.9, 0.2, 1.05], at: [1.3, -2.3, 1.05], ms: 9000 },
            ],
            figures: [
              { who: 'hero', q: 0, r: -1, facing: 1.7, pose: 'stand' },
              { who: 'kel', q: 1, r: -1, facing: 2.0, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['wenn'],
          questions: [
            { id: 'harl', lines: [ln('l1', 'wenn'), ln('l2', 'wenn')], set: ['askedHarl'] },
            { id: 'now', lines: [ln('l1', 'wenn'), ln('l2', 'wenn')] },
            { id: 'order', lines: [ln('l1', 'wenn'), ln('l2', 'wenn')] },
          ],
          cine: {
            place: 'guardhouseFire',
            sky: 'night',
            cam: [
              { from: [1.1, 0.5, 1.15], at: [-0.3, 0, 0.35], ms: 9000 },
              { from: [0.8, 0.7, 0.8], at: [-0.5, 0.2, 0.45], ms: 9000 },
            ],
            figures: [
              { who: 'wenn', q: -1, r: 1, facing: 5, pose: 'sit' },
              { who: 'kel', q: -1, r: 0, facing: 0, pose: 'sit' },
              { who: 'hero', q: 1, r: -1, facing: 2, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          kind: 'fight',
          id: 'ledge',
          mode: 'wedge',
          place: 'ledge',
          weather: 'wind',
          budgetBp: 10000,
          goal: { kind: 'wedge' },
          npcs: [
            { id: 'kel', does: 'wait', hits: 1, at: { q: 1, r: 0 }, safe: true, vital: false },
            { id: 'wenn', does: 'wait', hits: 1, at: { q: -1, r: 1 }, safe: true, vital: false },
          ],
          says: [
            { on: 'cue', cue: 'plant', who: 'kel', line: 'l1' },
            { on: 'cue', cue: 'fall', who: 'kel', line: 'l2', delay: sec(1) },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [ln('l1', 'wenn'), ln('l2', 'kel'), ln('l3', 'wenn'), ln('l4', 'narrator'), ln('l5', 'kel'), ln('l6', 'wenn'), ln('l7', 'kel')],
          cine: {
            place: 'ledge',
            sky: 'dusk',
            weather: 'wind',
            cam: [
              { from: [-2, 9, 3.6], at: [1.5, -1, 0.4], ms: 9000 },
              { from: [-2.6, 6.4, 1.3], at: [0, 3.3, 0.45], ms: 9000 },
              { from: [-1.0, 3.2, 0.95], at: [1.6, 6.2, 0.2], ms: 9000 },
            ],
            figures: [
              { who: 'wenn', q: -1, r: 3, facing: 5.4, pose: 'stand' },
              { who: 'kel', q: 0, r: 3, facing: 5, pose: 'kneel' },
              { who: 'hero', q: 1, r: 2, facing: 4.6, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 2, «El puente cortado» (level 24): the gorge between two plates of the shell, the Furrows far below.
      id: 'bridge',
      closedBy: 'kel',
      piece: 'ring',
      acts: [
        {
          // The wind drives snow over the gorge; the Furrows dark green far below; the bridge's two stumps hanging;
          // high above, the ice that shuts the pass split black from end to end.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-warrior-2',
            place: 'chasmFar',
            sky: 'day',
            weather: 'wind',
            cam: [
              // High over the gorge from the west, looking down along it into the mist and the Furrows.
              { from: [-13.5, -9.6, 13.2], at: [14.1, -11.6, -16.8], ms: 5200 },
              { from: [-8.8, -5.8, 9.8], at: [10.8, -11.6, -15.2], ms: 4600 },
              // Down to the edge: the old posts and the span hanging, the rock across with its own stump.
              { from: [-5.4, 2.4, 2.5], at: [4.8, -9.6, -1.8], ms: 4600 },
              // And up: the pass high over the far side, its ice split.
              { from: [-1.6, 1.4, 3.4], at: [33.7, -67.4, 13.8], ms: 4200 },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'wenn'), ln('l2', 'kel'), ln('l3', 'wenn')],
          cine: {
            place: 'rim',
            sky: 'day',
            weather: 'wind',
            cam: [
              { from: [-5, 6, 2.6], at: [1, -3, 0.2], ms: 8000 },
              { from: [-3.5, 3.4, 1.3], at: [1.2, -2.6, 0.4], ms: 9000 },
              { from: [-1.8, 2.2, 1.0], at: [1, -3.4, 0.2], ms: 9000 },
            ],
            figures: [
              { who: 'wenn', q: -1, r: 0, facing: 5.2, pose: 'stand' },
              { who: 'kel', q: 0, r: -1, facing: 5.0, pose: 'stand' },
              { who: 'hero', q: 1, r: -1, facing: 5.3, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'fight',
          id: 'hooks',
          mode: 'hooks',
          place: 'rim',
          weather: 'wind',
          budgetBp: 10000,
          goal: { kind: 'winch' },
          npcs: [
            { id: 'kel', does: 'wait', hits: 15, at: { q: 1, r: -2 } },
            { id: 'wenn', does: 'wait', hits: 1, at: { q: -5, r: 5 }, safe: true, vital: false, model: 'wennMule' },
          ],
          says: [
            { on: 'cue', cue: 'winch', who: 'kel', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'ogre', who: 'kel', line: 'l2', delay: sec(1) },
          ],
        },
        {
          kind: 'fight',
          id: 'wind',
          mode: 'gusts',
          place: 'span',
          weather: 'wind',
          budgetBp: 10000,
          goal: { kind: 'cross' },
          npcs: [{ id: 'kel', does: 'wait', hits: 15, at: { q: -5, r: 10 } }],
        },
        {
          kind: 'scene',
          id: 'look',
          lines: [ln('l1', 'kel'), ln('l2', 'wenn'), ln('l3', 'wenn')],
          cine: {
            place: 'span',
            sky: 'day',
            weather: 'wind',
            cam: [
              // Behind them at the platform's north end, looking past them up the Spine to the pass.
              { from: [3.2, -4.4, 4.2], at: [24, -56, 14], ms: 8000 },
              { from: [3.8, -4.4, 1.9], at: [26, -58, 15], ms: 9000 },
              // Round to their faces, turned up.
              { from: [3.5, -9.5, 1.1], at: [5.4, -6.6, 1.0], ms: 9000 },
            ],
            figures: [
              { who: 'kel', q: 5, r: -7, facing: 4.6, pose: 'stand' },
              { who: 'wenn', q: 4, r: -7, facing: 4.7, pose: 'stand' },
              { who: 'hero', q: 5, r: -6, facing: 4.5, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['kel'],
          questions: [
            { id: 'guard', lines: [ln('l1', 'kel'), ln('l2', 'kel')], set: ['askedKel'] },
            { id: 'height', lines: [ln('l1', 'kel'), ln('l2', 'kel')] },
            { id: 'harl', lines: [ln('l1', 'kel')] },
          ],
          cine: {
            place: 'spanFire',
            sky: 'night',
            cam: [
              { from: [-2.6, 7.6, 1.05], at: [-4.3, 7, 0.35], ms: 9000 },
              { from: [-3.0, 7.9, 0.75], at: [-4.5, 7.2, 0.45], ms: 9000 },
            ],
            figures: [
              { who: 'kel', q: -5, r: 8, facing: 5, pose: 'sit' },
              { who: 'wenn', q: -5, r: 7, facing: 0, pose: 'sit' },
              { who: 'hero', q: -3, r: 6, facing: 2, pose: 'sit' },
            ],
            fire: { q: -4, r: 7 },
          },
        },
        {
          kind: 'fight',
          id: 'weights',
          mode: 'weights',
          place: 'weights',
          weather: 'wind',
          budgetBp: 10000,
          goal: { kind: 'weight' },
          npcs: [{ id: 'kel', does: 'wait', hits: 15, at: { q: -1, r: 2 } }],
          says: [
            { on: 'cue', cue: 'wrong', who: 'kel', line: 'l1' },
            { on: 'cue', cue: 'level', who: 'kel', line: 'l2', delay: sec(1) },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'wenn'),
            ln('l2', 'kel'),
            ln('l3', 'wenn'),
            ln('l4', 'narrator'),
            ln('l5', 'narrator'),
            ln('l6', 'farmer'),
            ln('l7', 'dalmo'),
            ln('l8', 'wenn'),
            ln('l9', 'dalmo'),
          ],
          cine: {
            place: 'weights',
            sky: 'dusk',
            weather: 'wind',
            spans: true,
            cam: [
              // High over the platform: the three spans coming down together over the drop, the gantry's stays taut.
              { from: [8.2, 1.9, 7.3], at: [4.8, -11.6, -1], ms: 6000 },
              // Lower, along the bridge: three men from the farms crossing it with a mule.
              { from: [6.9, -3.9, 2.6], at: [5.3, -12.5, 0.1], ms: 8500 },
              // Behind the people at the lip, as they come off it: Dalmo, Wenn.
              { from: [-0.9, 4.3, 2.0], at: [1.5, -4.0, 0.7], ms: 9000 },
              { from: [-3.9, 3.7, 1.7], at: [1.9, -3.9, 0.7], ms: 9000 },
            ],
            figures: [
              { who: 'wenn', q: 0, r: -1, facing: 5.1, pose: 'stand' },
              { who: 'kel', q: 1, r: -2, facing: 4.9, pose: 'kneel' },
              { who: 'hero', q: -1, r: -1, facing: 5.2, pose: 'stand' },
              { who: 'dalmo', q: 8, r: -18, facing: 1.5, pose: 'walk', delay: 4400, speed: 92, walkMs: 11600 },
              { who: 'farmer', q: 9, r: -20, facing: 1.5, pose: 'walk', delay: 4400, speed: 92, walkMs: 12400 },
              { who: 'farmer2', q: 10, r: -22, facing: 1.5, pose: 'walk', delay: 4400, speed: 92, walkMs: 13200 },
            ],
          },
        },
      ],
    },
    {
      // Chapter 3, «La noche en el fuerte» (level 30): the fort at the foot of the pass, half buried, its roof on an axis.
      id: 'fort',
      closedBy: 'dalmo',
      piece: 'pauldron',
      acts: [
        {
          // Dusk over the ice: a small fort buried to its middle, its gabled roof bowed under the snow; from the roof,
          // far off on the slope across, the lights of the high farms of Musgoviejo.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-warrior-3',
            place: 'fortFar',
            sky: 'dusk',
            weather: 'wind',
            cam: [
              // From down the slope: the fort buried on its col, the pass's ice above it.
              { from: [-11.4, 10.8, 3.4], at: [0.96, -1.92, 1.08], ms: 5200 },
              // Closer: the gabled roof bowed under the snow, the hearth's yard.
              { from: [-6.9, 6.25, 2.33], at: [0.24, -0.48, 1.33], ms: 3200 },
              { from: [-6.2, 5.6, 2.2], at: [0.24, -0.48, 1.3], ms: 3400 },
              // From the ridge, south across the valley: the high farms' lights.
              { from: [0.48, -0.96, 2.67], at: [-14.4, 28.9, 1.5], ms: 5600 },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'dalmo'), ln('l2', 'wenn'), ln('l3', 'dalmo')],
          cine: {
            place: 'fortClimb',
            sky: 'dusk',
            weather: 'wind',
            cam: [
              // Below the foot of the trail, looking up it to the fort at the top.
              { from: [-4.75, 9.5, 3.0], at: [2.89, -5.77, 1.9], ms: 8000 },
              { from: [-3.43, 10.2, 0.92], at: [-2.92, 7.5, 0.62], ms: 9000 },
              // From up the trail: Dalmo looking up at the fort.
              { from: [-2.53, 5.39, 0.9], at: [-3, 7, 1.0], ms: 9000 },
            ],
            figures: [
              { who: 'dalmo', q: -3, r: 7, facing: 4.7, pose: 'stand' },
              { who: 'wenn', q: -4, r: 8, facing: 0.1, pose: 'stand' },
              { who: 'kel', q: -2, r: 8, facing: 1.3, pose: 'stand' },
              { who: 'hero', q: -1, r: 7, facing: 2.8, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'fight',
          id: 'slope',
          mode: 'slope',
          // (Not `slope`: El Párpado 2's place is called so, and the board drew the Order's post over it.)
          place: 'fortClimb',
          weather: 'wind',
          budgetBp: 11000,
          goal: { kind: 'door' },
          npcs: [
            { id: 'kel', does: 'wait', hits: 15 },
            { id: 'mule', does: 'wait', hits: 15, model: 'wennMule' },
            { id: 'dalmo', does: 'wait', hits: 15, vital: false },
          ],
          says: [
            { on: 'cue', cue: 'troll', who: 'dalmo', line: 'l1', delay: sec(1) },
            { on: 'cue', cue: 'mend', who: 'wenn', line: 'l2' },
          ],
        },
        {
          // The last room: Sanne and Tomé sitting against the wall, the shields across their knees.
          kind: 'scene',
          id: 'tomb',
          lines: [ln('l1', 'kel'), ln('l2', 'dalmo'), ln('l3', 'wenn')],
          cine: {
            place: 'fortTomb',
            sky: 'night',
            cam: [
              // From the door: the cold hearth in the north wall, the two sitting either side of it.
              // (Simple framing: a slow push in from the door; the scene waits for its video.)
              { from: [-1.1, 2.2, 1.0], at: [0.96, -1.92, 0.33], ms: 12000 },
              { from: [-0.75, 1.5, 0.85], at: [0.96, -1.92, 0.33], ms: 12000 },
            ],
            figures: [
              { who: 'kel', q: 1, r: 0, facing: 4.63, pose: 'stand' },
              { who: 'dalmo', q: -1, r: -1, facing: 4.75, pose: 'kneel' },
              { who: 'wenn', q: 0, r: 0, facing: 3.92, pose: 'stand' },
              { who: 'hero', q: 0, r: 1, facing: 4.5, pose: 'stand' },
            ],
          },
        },
        {
          // Ruy's diary opened on the fort's table: the scene goes cold and grey, sixty years back. Eleven places, five
          // of them empty; the six left say where they go tomorrow.
          kind: 'scene',
          id: 'table',
          lines: [ln('l1', 'ruy'), ln('l2', 'fermin'), ln('l3', 'juana'), ln('l4', 'sanne')],
          cine: {
            place: 'fortTable',
            sky: 'night',
            memory: true,
            cam: [
              // The hall from its corner: the long table, six at it, five places empty; the fire in the north wall.
              { from: [1.9, 1.55, 1.05], at: [-0.45, -0.5, 0.35], ms: 9000 },
              // Ruy at the table's end, the diary open before him.
              { from: [-1.33, 1.15, 0.88], at: [-1.9, -0.19, 0.62], ms: 9000 },
              // Across the table to Fermín and Juana.
              { from: [-0.67, 1.35, 0.83], at: [0.5, -1, 0.62], ms: 9000 },
            ],
            figures: [
              { who: 'ruy', q: -2, r: 0, facing: 4.5, pose: 'sit' },
              { who: 'fermin', q: 0, r: -1, facing: 1.5, pose: 'sit' },
              { who: 'juana', q: 1, r: -1, facing: 1.5, pose: 'sit' },
              { who: 'sanne', q: -1, r: 0, facing: 4.5, pose: 'sit' },
              { who: 'tome', q: 0, r: 0, facing: 4.5, pose: 'sit' },
              { who: 'harl', q: 1, r: -2, facing: 1.5, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'fight',
          id: 'night',
          mode: 'roof',
          place: 'fortPast',
          night: true,
          weather: 'wind',
          budgetBp: 11000,
          memory: 'ruy',
          goal: { kind: 'time', ticks: SENDA_NIGHT_TICKS },
          npcs: [
            { id: 'sanne', does: 'wait', hits: 30, vital: false },
            { id: 'tome', does: 'wait', hits: 30, vital: false },
            { id: 'fermin', does: 'wait', hits: 1, safe: true, vital: false, lift: 2200 },
            { id: 'juana', does: 'wait', hits: 1, safe: true, vital: false },
            { id: 'harl', does: 'wait', hits: 1, safe: true, vital: false },
          ],
          says: [
            { on: 'at', t: sec(14), who: 'juana', line: 'l1' },
            { on: 'cue', cue: 'gather', who: 'fermin', line: 'l2' },
            { on: 'cue', cue: 'gather', who: 'ruy', line: 'l3', delay: sec(2) },
            { on: 'cue', cue: 'now', who: 'fermin', line: 'l4' },
            { on: 'cue', cue: 'through', who: 'sanne', line: 'l5' },
            { on: 'cue', cue: 'through', who: 'tome', line: 'l6', delay: sec(2) },
            { on: 'at', t: sec(150), who: 'harl', line: 'l7' },
          ],
        },
        {
          // Won: the memory's last of it, Ruy and Harl alone at the door (the roof Fermín tipped still settling).
          kind: 'scene',
          id: 'door',
          lines: [ln('l1', 'ruy'), ln('l2', 'harl'), ln('l3', 'ruy'), ln('l4', 'harl'), ln('l5', 'ruy')],
          cine: {
            place: 'fortPast',
            sky: 'night',
            weather: 'wind',
            memory: true,
            tip: true,
            cam: [
              // Over the hearth's yard, past the ridge: Fermín's roof swinging over the far yard, its snow pouring off.
              { from: [-8.79, 6.74, 4.83], at: [1.84, 0.48, 0.5], ms: 5200 },
              // Ruy and Harl alone by the door.
              { from: [-5.47, 3.27, 0.79], at: [-2.97, 1.44, 0.67], ms: 9000 },
              { from: [-5.0, 0.9, 0.85], at: [-3, 2, 0.95], ms: 9000 },
            ],
            figures: [
              { who: 'ruy', q: -3, r: 1, facing: 1.0, pose: 'stand' },
              { who: 'harl', q: -3, r: 2, facing: 4.6, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['wenn', 'dalmo'],
          questions: [
            { id: 'words', lines: [ln('l1', 'wenn'), ln('l2', 'wenn')], set: ['askedLastWords'] },
            { id: 'grave', lines: [ln('l1', 'dalmo'), ln('l2', 'dalmo')], set: ['askedDalmo'] },
            { id: 'shield', lines: [ln('l1', 'dalmo'), ln('l2', 'dalmo')] },
          ],
          cine: {
            place: 'fortHearth',
            sky: 'night',
            cam: [
              // Behind them on the bench, the great hearth burning in front.
              { from: [-0.3, 1.1, 1.0], at: [0.96, -1.92, 0.4], ms: 9000 },
              // From the fire's side, their faces in its light.
              { from: [2.6, -0.9, 1.0], at: [-0.2, -1.0, 0.4], ms: 9000 },
            ],
            figures: [
              { who: 'wenn', q: 0, r: -1, facing: 4.5, pose: 'sit' },
              { who: 'dalmo', q: 1, r: -1, facing: 4.2, pose: 'sit' },
              { who: 'kel', q: -1, r: -1, facing: 4.8, pose: 'sit' },
              { who: 'hero', q: -2, r: -1, facing: 5.0, pose: 'sit' },
            ],
          },
        },
        {
          kind: 'fight',
          id: 'roof',
          mode: 'roof',
          place: 'fort',
          night: true,
          weather: 'wind',
          budgetBp: 11000,
          goal: { kind: 'troll' },
          says: [
            { on: 'cue', cue: 'windHearth', who: 'kel', line: 'l1' },
            { on: 'at', t: sec(200), who: 'kel', line: 'l2' },
            { on: 'at', t: sec(204), who: 'wenn', line: 'l3' },
            { on: 'at', t: sec(290), who: 'wenn', line: 'l4' },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [ln('l1', 'diary2'), ln('l2', 'wenn'), ln('l3', 'kel'), ln('l4', 'kel'), ln('l5', 'wenn'), ln('l6', 'narrator')],
          cine: {
            place: 'fort',
            sky: 'dawn',
            weather: 'clear',
            cam: [
              // The hearth's yard at dawn from over the south wall: the snow the roof threw, the sky clearing.
              { from: [-5.6, 6.6, 2.6], at: [-4.03, 2.4, 0.5], ms: 9000 },
              // Kel on his knees with the diary, Wenn over him.
              { from: [-4.3, 5.2, 0.75], at: [-3.82, 2.98, 0.54], ms: 9000 },
              { from: [-4.2, 1.6, 0.85], at: [-4.99, 2.98, 0.92], ms: 9000 },
            ],
            figures: [
              { who: 'wenn', q: -5, r: 3, facing: 0, pose: 'stand' },
              { who: 'kel', q: -3, r: 3, facing: 3.0, pose: 'kneel' },
              { who: 'hero', q: -4, r: 4, facing: 4.0, pose: 'stand' },
              { who: 'dalmo', q: -6, r: 4, facing: 5.0, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 4, «El alud» (level 36): the loose slope under the pass, the ice cave where Fermín and Juana stayed, the
      // large wedges against the avalanche; the second avalanche that buries Kel, Harl alive, and the one decision.
      id: 'avalanche',
      closedBy: 'wenn',
      piece: 'wedge',
      acts: [
        {
          // From below: a huge white slope under a cloudless sky; a crack; a line opens in the snow up high; higher
          // still the pass's ice split open like a mouth.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-warrior-4',
            place: 'slideFar',
            sky: 'day',
            weather: 'clear',
            cam: [
              { from: [-1.5, 12, 1.2], at: [0.5, -6, 3.5], ms: 5200 },
              { from: [-1.2, 10.5, 1.6], at: [1.5, -9, 6.5], ms: 5600 },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'diary4'), ln('l2', 'kel'), ln('l3', 'dalmo')],
          cine: {
            place: 'loose',
            sky: 'day',
            weather: 'wind',
            cam: [
              // At the foot of the slope, looking up it: the train, Wenn on her rock.
              { from: [-5.6, 11.6, 2.4], at: [-1.5, 3, 0.9], ms: 9000 },
              { from: [-5.2, 10.4, 1.3], at: [-3, 7.4, 0.8], ms: 9000 },
            ],
            figures: [
              { who: 'wenn', q: -6, r: 8, facing: 0.4, pose: 'sit' },
              { who: 'kel', q: -3, r: 7, facing: 4.7, pose: 'stand' },
              { who: 'mule', q: -2, r: 7, facing: 4.7, pose: 'stand' },
              { who: 'dalmo', q: -1, r: 7, facing: 3.4, pose: 'stand' },
              { who: 'hero', q: -4, r: 8, facing: 0.2, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'fight',
          id: 'loose',
          mode: 'loose',
          place: 'loose',
          weather: 'wind',
          budgetBp: 12000,
          goal: { kind: 'mouth' },
          npcs: [
            { id: 'kel', does: 'wait', hits: 15 },
            { id: 'mule', does: 'wait', hits: 1, safe: true, vital: false, model: 'muleLoad' },
            { id: 'dalmo', does: 'wait', hits: 1, safe: true, vital: false },
          ],
          says: [
            { on: 'cue', cue: 'shadow', who: 'kel', line: 'l1' },
            { on: 'cue', cue: 'giant', who: 'dalmo', line: 'l2', delay: sec(1) },
          ],
        },
        {
          kind: 'fight',
          id: 'cave',
          mode: 'cave',
          place: 'iceCave',
          budgetBp: 12000,
          goal: { kind: 'hall' },
          npcs: [
            { id: 'kel', does: 'follow', hits: 15, follow: 2 },
            { id: 'dalmo', does: 'follow', hits: 1, safe: true, vital: false, follow: 3 },
            { id: 'pair', does: 'wait', hits: 1, safe: true, vital: false, model: 'frozenPair' },
          ],
          says: [
            { on: 'cue', cue: 'room1', who: 'kel', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'room1', who: 'dalmo', line: 'l2', delay: sec(8) },
            { on: 'cue', cue: 'open3', who: 'kel', line: 'l3', delay: sec(1) },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['wenn', 'kel'],
          questions: [
            { id: 'shields', lines: [ln('l1', 'wenn'), ln('l2', 'wenn')] },
            { id: 'forgive', lines: [ln('l1', 'wenn'), ln('l2', 'wenn')], set: ['askedForgive'] },
            { id: 'after', lines: [ln('l1', 'kel'), ln('l2', 'kel')], set: ['askedKelAfter'] },
          ],
          cine: {
            place: 'caveMouth',
            sky: 'night',
            cam: [
              // Up the slope from the fire, the cave's mouth dark behind them.
              { from: [-0.7, 4.0, 1.25], at: [-3.7, 6.35, 0.35], ms: 9000 },
              { from: [-4.4, 4.4, 1.1], at: [-3.6, 6.2, 0.4], ms: 9000 },
            ],
            figures: [
              { who: 'wenn', q: -5, r: 6, facing: 0, pose: 'sit' },
              { who: 'kel', q: -3, r: 6, facing: 3, pose: 'sit' },
              { who: 'hero', q: -4, r: 5, facing: 1, pose: 'sit' },
            ],
            fire: { q: -4, r: 6 },
          },
        },
        {
          kind: 'fight',
          id: 'wedges',
          mode: 'avalanche',
          place: 'avalanche',
          weather: 'wind',
          budgetBp: 12000,
          goal: { kind: 'avalanche' },
          npcs: [{ id: 'kel', does: 'wait', hits: 1, safe: true, vital: false }],
          says: [
            { on: 'cue', cue: 'plant', who: 'kel', line: 'l1', delay: sec(1) },
            { on: 'cue', cue: 'front1', who: 'kel', line: 'l2' },
            { on: 'cue', cue: 'touch1', who: 'kel', line: 'l3', delay: sec(1) },
            { on: 'cue', cue: 'split', who: 'kel', line: 'l4' },
          ],
          keep: ['shieldLost'],
          marks: ['shieldLost'],
          trial: true,
        },
        {
          // The lowest point (no fight): the second avalanche from the slope nobody watched, Dalmo going down with the
          // shields, an old man digging with his hands beside the hero.
          kind: 'scene',
          id: 'low',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'narrator'),
            ln('l3', 'narrator', { flag: 'shieldLost' }),
            ln('l4', 'dalmo'),
            ln('l5', 'wenn'),
            ln('l6', 'dalmo'),
            ln('l7', 'narrator'),
            ln('l8', 'narrator'),
            ln('l9', 'stranger'),
            ln('l10', 'narrator'),
            ln('l11', 'kel'),
            ln('l12', 'wenn'),
            ln('l13', 'wenn'),
          ],
          cine: {
            place: 'caveMouth',
            sky: 'dusk',
            weather: 'wind',
            cam: [
              // Up the slope, looking down at the buried mouth.
              { from: [-1.0, 2.9, 2.7], at: [-3.75, 7.5, 0.5], ms: 9000 },
              // Closer, on the hole in the snow.
              { from: [-1.6, 4.3, 2.2], at: [-4.0, 7.3, 0.5], ms: 9000 },
              { from: [-1.4, 4.0, 2.0], at: [-4.0, 7.1, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'kel', q: -4, r: 7, facing: 1, pose: 'lie' },
              { who: 'harlOld', q: -3, r: 7, facing: 3, pose: 'kneel' },
              { who: 'hero', q: -4, r: 6, facing: 1, pose: 'kneel' },
              { who: 'wenn', q: -5, r: 7, facing: 0, pose: 'stand' },
              { who: 'dalmo', q: -2, r: 6, facing: 0.3, pose: 'walk', delay: 9000, speed: 22, walkMs: 14000 },
              { who: 'mule', q: -1, r: 5, facing: 0.3, pose: 'walk', delay: 9000, speed: 22, walkMs: 14000 },
            ],
          },
        },
        {
          kind: 'choice',
          id: 'choice',
          lines: [ln('l1', 'harlOld'), ln('l2', 'harlOld'), ln('l3', 'wenn')],
          options: [
            { id: 'climbed', set: ['wennClimbed'] },
            { id: 'stayed', set: ['wennStayed'] },
          ],
          cine: {
            place: 'caveMouth',
            sky: 'dusk',
            weather: 'wind',
            cam: [
              { from: [-1.6, 4.3, 2.2], at: [-4.0, 7.2, 0.6], ms: 9000 },
              { from: [-4.6, 4.4, 2.0], at: [-3.6, 7.0, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'kel', q: -4, r: 7, facing: 1, pose: 'lie' },
              { who: 'harlOld', q: -3, r: 7, facing: 3, pose: 'kneel' },
              { who: 'wenn', q: -5, r: 7, facing: 0, pose: 'stand' },
              { who: 'hero', q: -4, r: 6, facing: 1, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'wenn', { flag: 'wennClimbed' }),
            ln('l2', 'kel', { flag: 'wennClimbed' }),
            ln('l3', 'wenn', { flag: 'wennStayed' }),
            ln('l4', 'harlOld', { flag: 'wennStayed' }),
            ln('l5', 'narrator'),
          ],
          cine: {
            place: 'caveMouth',
            sky: 'dusk',
            weather: 'clear',
            cam: [
              // The four at the cave's mouth in the last light.
              { from: [-1.3, 3.85, 1.33], at: [-3.9, 6.9, 0.67], ms: 9000 },
              // Behind them, up the slope to the pass they climb tomorrow.
              { from: [-3.8, 6.7, 0.85], at: [4.3, -7.7, 2.5], ms: 9000 },
            ],
            figures: [
              { who: 'kel', q: -4, r: 7, facing: 4.5, pose: 'sit' },
              { who: 'harlOld', q: -3, r: 6, facing: 4.5, pose: 'stand' },
              { who: 'wenn', q: -5, r: 7, facing: 4.7, pose: 'stand' },
              { who: 'hero', q: -4, r: 6, facing: 4.5, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      id: 'pass',
      closedBy: 'harlOld',
      piece: 'company',
      call: true,
      // Lost to the Ventisquero, a closed chapter opens again at its fight: the ledge, the line and the beam stay as they were.
      keep: 'storm',
      acts: [
        {
          // The pass: an icy throat between two plates, a strip of sky; the ice that shut it, split; in the middle a
          // shield with a red cloth the wind won't leave still; higher up, the snow gathering into something that stands.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-warrior-5',
            place: 'passFar',
            sky: 'day',
            weather: 'wind',
            cam: [
              { from: [-6.5, 13, 1.2], at: [0, 0, 1.4], ms: 5200 },
              { from: [-5.2, 10.5, 1.6], at: [2.5, -5, 4.2], ms: 5600 },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [
            ln('l1', 'harlOld'),
            ln('l2', 'harlOld'),
            ln('l3', 'diary3'),
            ln('l4', 'diary5'),
            ln('l5', 'narrator'),
            ln('l6', 'harlOld'),
            ln('l7', 'wenn', W),
            ln('l8', 'harlOld', W),
            ln('l9', 'wenn', W),
            ln('l10', 'narrator', S),
          ],
          cine: {
            place: 'crag',
            sky: 'day',
            weather: 'wind',
            cam: [
              { from: [-2.5, 9.8, 1.6], at: [-4, 7.4, 0.8], ms: 9000 },
              { from: [-2.9, 9.2, 1.15], at: [-4.2, 7.5, 0.85], ms: 9000 },
            ],
            figures: [
              { who: 'harlOld', q: -4, r: 7, facing: 0.5, pose: 'stand' },
              { who: 'hero', q: -4, r: 8, facing: 4.6, pose: 'stand' },
              { who: 'wenn', q: -3, r: 8, facing: 3.4, pose: 'stand', if: W },
            ],
          },
        },
        {
          kind: 'fight',
          id: 'crag',
          mode: 'crag',
          place: 'crag',
          weather: 'wind',
          budgetBp: 12500,
          goal: { kind: 'crag' },
          npcs: [
            { id: 'harlOld', does: 'wait', hits: 12, model: 'harlBanner' },
            { id: 'wenn', does: 'wait', hits: 8, if: W },
          ],
          says: [
            { on: 'start', who: 'harlOld', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'warn1', who: 'harlOld', line: 'l3' },
            { on: 'cue', cue: 'half', who: 'harlOld', line: 'l2', if: S },
          ],
        },
        {
          kind: 'fight',
          id: 'camp',
          mode: 'camp',
          place: 'pass',
          weather: 'wind',
          budgetBp: 12500,
          goal: { kind: 'line' },
          npcs: [
            { id: 'harlOld', does: 'follow', hits: 12, follow: 2, model: 'harlBanner' },
            { id: 'dalmo', does: 'wait', hits: 1, safe: true, vital: false },
            { id: 'farmwife', does: 'wait', hits: 1, safe: true, vital: false },
            { id: 'farmer', does: 'wait', hits: 1, safe: true, vital: false },
            { id: 'farmer2', does: 'wait', hits: 1, safe: true, vital: false },
            { id: 'ruy', does: 'wait', hits: 1, safe: true, vital: false, model: 'ruyFrozen' },
          ],
          says: [
            { on: 'cue', cue: 'wake', who: 'harlOld', line: 'l1', delay: sec(1) },
            { on: 'cue', cue: 'clear', who: 'dalmo', line: 'l2', delay: sec(3) },
            { on: 'cue', cue: 'clear', who: 'dalmo', line: 'l3', delay: sec(9) },
            { on: 'cue', cue: 'clear', who: 'farmwife', line: 'l4', delay: sec(15) },
            { on: 'cue', cue: 'tenth', who: 'harlOld', line: 'l5', delay: sec(1) },
            { on: 'cue', cue: 'right', who: 'harlOld', line: 'l6' },
          ],
          keep: ['line'],
        },
        {
          kind: 'scene',
          id: 'dusk',
          lines: [ln('l1', 'wenn', W), ln('l2', 'harlOld', W), ln('l3', 'wenn', W), ln('l4', 'narrator', S)],
          cine: {
            place: 'passFire',
            sky: 'dusk',
            weather: 'wind',
            cam: [
              { from: [-4.5, 9.4, 1.6], at: [0, 7.8, 0.45], ms: 9000 },
              { from: [-3.2, 6.0, 1.4], at: [0, 7.8, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'wenn', q: -1, r: 8, facing: 0, pose: 'sit', if: W },
              { who: 'harlOld', q: 1, r: 7, facing: 2.2, pose: 'sit' },
              { who: 'dalmo', q: 0, r: 9, facing: 4.4, pose: 'sit', if: S },
              { who: 'hero', q: -1, r: 9, facing: 5.4, pose: 'sit' },
            ],
            fire: { q: 0, r: 8 },
          },
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['harlOld', 'wenn', 'dalmo'],
          questions: [
            { id: 'forgive', lines: [ln('l1', 'wenn')], set: ['askedWennForgive'], if: W },
            { id: 'letters', lines: [ln('l1', 'harlOld'), ln('l2', 'harlOld')], if: W },
            { id: 'say', lines: [ln('l1', 'harlOld')], set: ['askedWennSay'], if: S },
            { id: 'shield', lines: [ln('l1', 'dalmo'), ln('l2', 'dalmo')], set: ['askedDalmoShield'], if: S },
            { id: 'farms', lines: [ln('l1', 'dalmo')] },
          ],
          cine: {
            place: 'passFire',
            sky: 'night',
            cam: [
              { from: [-3.2, 6.0, 1.5], at: [0, 7.8, 0.45], ms: 9000 },
              { from: [-4.5, 9.4, 1.4], at: [0, 7.8, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'wenn', q: -1, r: 8, facing: 0, pose: 'sit', if: W },
              { who: 'harlOld', q: 1, r: 7, facing: 2.2, pose: 'sit' },
              { who: 'dalmo', q: 0, r: 9, facing: 4.4, pose: 'sit' },
              { who: 'hero', q: -1, r: 9, facing: 5.4, pose: 'sit' },
            ],
            fire: { q: 0, r: 8 },
          },
        },
        {
          kind: 'fight',
          id: 'beam',
          mode: 'beam',
          place: 'pass',
          night: true,
          weather: 'wind',
          budgetBp: 12500,
          goal: { kind: 'beam' },
          npcs: [{ id: 'harlOld', does: 'wait', hits: 12, model: 'harlBanner' }],
          says: [
            { on: 'start', who: 'harlOld', line: 'l1', delay: sec(1) },
            { on: 'cue', cue: 'creak', who: 'harlOld', line: 'l2' },
            { on: 'cue', cue: 'early', who: 'harlOld', line: 'l3', delay: sec(1) },
            { on: 'cue', cue: 'ready', who: 'harlOld', line: 'l4', delay: sec(1) },
          ],
          keep: ['beam'],
          marks: ['beam'],
        },
        {
          // The speech, before the Ventisquero, facing the line: the eleven names said one by one, each shield lit red.
          kind: 'choice',
          id: 'speech',
          lines: [
            ln('l1', 'harlOld'),
            ln('l2', 'harlOld'),
            ln('l3', 'wenn', W),
            ln('l4', 'farmwife', S),
            ln('l5', 'harlOld'),
            ln('l6', 'narrator'),
            ln('l7', 'harlOld'),
            nm('l8', 'farmwife', 1),
            nm('l9', 'dalmo', 2),
            nm('l10', 'farmer', 3),
            nm('l11', 'farmer2', 4),
            nm('l12', 'farmer', 5),
            ln('l13', 'narrator'),
            nm('l14', 'wenn', 6, W),
            ln('l15', 'narrator', S),
            nm('l16', 'harlOld', 7),
            nm('l17', 'farmer2', 8),
            nm('l18', 'farmer', 9),
            nm('l19', 'farmer2', 10),
            nm('l20', 'farmer', 11),
            ln('l21', 'harlOld'),
          ],
          options: [
            { id: 'all', set: ['speechAll'] },
            { id: 'eleven', set: ['speechEleven'] },
          ],
          cine: {
            place: 'pass',
            sky: 'day',
            weather: 'wind',
            line: true,
            cam: [
              // From the pass, low, along the line's face: the shields and their crosses, the people behind them; then up,
              // the whole line against the farms' torches.
              { from: [-5.3, 3.6, 1.0], at: [-2.2, 7.4, 0.8], ms: 12000 },
              { from: [1.2, 3.6, 1.1], at: [-5.2, 7.4, 0.8], ms: 16000 },
              { from: [-1.1, 2.2, 2.6], at: [-3.8, 7.6, 0.7], ms: 16000 },
            ],
            figures: [
              { who: 'harlOld', q: -4, r: 8, facing: 4.7, pose: 'stand' },
              { who: 'hero', q: -5, r: 9, facing: 4.7, pose: 'stand' },
              { who: 'wenn', q: -3, r: 8, facing: 4.7, pose: 'stand', if: W },
              { who: 'dalmo', q: -7, r: 9, facing: 4.7, pose: 'stand' },
              { who: 'farmwife', q: -2, r: 9, facing: 4.7, pose: 'stand' },
              { who: 'farmer', q: -1, r: 9, facing: 4.7, pose: 'stand' },
              { who: 'farmer2', q: -6, r: 9, facing: 4.7, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'fight',
          id: 'storm',
          mode: 'storm',
          place: 'pass',
          weather: 'storm',
          budgetBp: 12500,
          goal: { kind: 'storm' },
          npcs: [
            { id: 'harlOld', does: 'wait', hits: 1, safe: true, vital: false, model: 'harlBanner' },
            { id: 'wenn', does: 'wait', hits: 8, vital: false, if: W },
            { id: 'dalmo', does: 'wait', hits: 12, vital: false },
            { id: 'farmwife', does: 'wait', hits: 1, safe: true, vital: false, model: 'farmTorch' },
            { id: 'farmer', does: 'wait', hits: 1, safe: true, vital: false, model: 'farmTorch2' },
            { id: 'farmer2', does: 'wait', hits: 1, safe: true, vital: false, model: 'farmTorch3' },
          ],
          says: [
            { on: 'cue', cue: 'trip1', who: 'harlOld', line: 'l1' },
            { on: 'cue', cue: 'call', who: 'farmwife', line: 'l2', delay: sec(4) },
            { on: 'cue', cue: 'call', who: 'dalmo', line: 'l3', delay: sec(8) },
            { on: 'cue', cue: 'breathReady', who: 'harlOld', line: 'l4' },
            { on: 'cue', cue: 'breath', who: 'harlOld', line: 'l5' },
            { on: 'cue', cue: 'harl', who: 'harlOld', line: 'l6', delay: sec(1) },
            { on: 'cue', cue: 'read', who: 'wenn', line: 'l7' },
          ],
          foe: true,
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'harlShield'),
            ln('l3', 'harlOld'),
            ln('l4', 'wenn', { flag: 'askedLastWords', notFlag: 'wennStayed' }),
            ln('l5', 'wenn', W),
            ln('l6', 'narrator', W),
            ln('l7', 'narrator', S),
            ln('l8', 'wenn', S),
            ln('l9', 'wenn', { flag: 'askedLastWords', notFlag: 'wennClimbed' }),
            ln('l10', 'harlOld', S),
          ],
          cine: {
            place: 'pass',
            sky: 'dawn',
            weather: 'clear',
            line: true,
            whole: true,
            cam: [
              { from: [-5.6, 4.6, 1.6], at: [-3.5, 7.1, 0.4], ms: 10000 },
              { from: [-2.4, 4.8, 1.4], at: [-3.8, 7.3, 0.5], ms: 10000 },
            ],
            figures: [
              { who: 'harlOld', q: -4, r: 8, facing: 4.4, pose: 'stand' },
              { who: 'wenn', q: -5, r: 8, facing: 0.6, pose: 'stand', if: W },
              { who: 'hero', q: -3, r: 8, facing: 3.4, pose: 'stand' },
              { who: 'dalmo', q: -6, r: 9, facing: 0.8, pose: 'stand' },
            ],
          },
        },
        {
          // The bastion's gate in the first snowfall: eleven shields over the arch, the farms, the recruits, Kel reading.
          kind: 'scene',
          id: 'epilogue',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'kel'),
            ln('l3', 'harlOld'),
            ln('l4', 'wenn'),
            ln('l5', 'kel', { flag: 'speechAll' }),
            ln('l6', 'dalmo', { flag: 'speechAll' }),
            ln('l7', 'kel', { flag: 'speechEleven' }),
            ln('l8', 'narrator', { flag: 'speechEleven' }),
            ln('l9', 'kel', { flag: 'askedKelAfter' }),
            ln('l10', 'dalmo', { flag: 'askedDalmoShield' }),
            ln('l11', 'dalmo', { notFlag: 'askedDalmoShield' }),
            ln('l12', 'wenn'),
            ln('l13', 'dalmo'),
            ln('l14', 'wenn', { flag: 'wennClimbed', notFlag: 'askedWennForgive' }),
            ln('l15', 'wenn', { flag: 'askedWennForgive' }),
            ln('l16', 'wenn', S),
            ln('l17', 'narrator'),
          ],
          cine: {
            video: 'senda-warrior-5-end',
            place: 'barracks',
            sky: 'day',
            weather: 'snow',
            cam: [
              { from: [-6, 8.7, 2.0], at: [3.1, -3.9, 2.5], ms: 9000 },
              { from: [-1, 3.2, 1.3], at: [0.6, -0.2, 0.8], ms: 12000 },
            ],
            figures: [
              { who: 'kel', q: 0, r: 0, facing: 2.6, pose: 'stand' },
              { who: 'harlOld', q: 1, r: 0, facing: 2.8, pose: 'stand' },
              { who: 'wenn', q: 1, r: -1, facing: 2.4, pose: 'stand' },
              { who: 'dalmo', q: -2, r: 1, facing: 0.6, pose: 'stand' },
              { who: 'hero', q: 0, r: 1, facing: 5.2, pose: 'stand' },
            ],
          },
        },
      ],
    },
  ],
};

/**
 * The paths written so far: the warrior's (all five), and a test path to try the pipes with (the tests' own; a class
 * walks it only where no path of its own is written).
 */
/** Ser Edda as the campaign knows her (docs/sendas/paladin.md §2): world facts the sim reads off the campaign. */
const EDDA_UNSEEN: SagaCond = { flag: '@edda.unseen' };
const EDDA_STANDING: SagaCond = { flag: '@edda.standing' };
const EDDA_FALLEN: SagaCond = { flag: '@edda.fallen' };
const EDDA_RELIC: SagaCond = { flag: '@edda.relic' };

/**
 * The paladin's path, La Vía de los Altares (docs/sendas/paladin.md): Ser Marit, Ser Edda's squire, who was to swear at
 * the seventh altar the week Somnhold fell and went away, walks the Way of the first Vigil with the hero to swear at
 * last. Chapter 1, «El ojo abierto» (level 18): the Temple at dawn (the yard before the keep where there is none) and
 * Brann knowing her; the first altar on the Burn's glass shore (the canals, small: three stones, the water each wave
 * brings, the crías that burn lines on the glass and never come onto water); the watchtower of the first Vigil and the
 * second altar held while it lights (the glass drake's cone at Marit, a hero beside her takes it); the campfire on the
 * glass at night; and the test, the water that goes (two pools, the high one's mouth shut in time, the Arado's three
 * burnt lines), closing on the order nailed to the altar: Ser Varo's.
 */
const PALADIN: SendaDef = {
  id: 'paladin',
  cls: 'paladin',
  planned: 5,
  cast: {
    brann: { npc: 'brann' },
    marit: { model: 'marit' },
    // The first Vigil speaks by its altars' inscriptions, in stone.
    vigil: { voice: true },
    // Chapter 2: the salt flats. The old salt-woman; Ser Varo (first seen as «a knight», he names himself), his squire
    // Nuño and one of his two knights; the salt folk; and, in the fog, the young squire who is not Marit.
    salinera: { model: 'salinera' },
    knight: { model: 'varo' },
    varo: { model: 'varo' },
    nuno: { model: 'nuno' },
    orderKnight: { model: 'orderKnight' },
    salter: { model: 'salter' },
    salter2: { model: 'salter2' },
    fled: { model: 'fled' },
    // Chapter 3: the bridge in the rain. Ser Varo's two knights at the upper end (one figure, twice).
    orderKnight2: { model: 'orderKnight' },
    // Chapter 4: the monastery of the waterfall. The memory, played as Marit at seventeen, by Ser Edda as she was (never
    // the Unsleeping); Brother Tadeo (first seen as «a monk», Marit names him).
    maritYoung: { model: 'maritYoung' },
    edda: { model: 'eddaYoung' },
    monk: { model: 'tadeo' },
    tadeo: { model: 'tadeo' },
    // Chapter 5: the seventh altar. La Que Huyó, Marit's own nightmare: the young squire she was, in white.
    runaway: { model: 'fled' },
  },
  chapters: [
    {
      id: 'eye',
      closedBy: 'marit',
      piece: 'glassEye',
      acts: [
        {
          // The bastion's Temple at dawn: a grey-haired woman in worn armour kneels before its altar and can't get up
          // alone; beyond it, toward the Burn, the sky still black.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-paladin-1',
            place: 'temple',
            sky: 'dawn',
            cam: [
              { from: [-1.2, 7.4, 3.2], at: [0.6, -1.6, 1.1], ms: 5200 },
              { from: [-0.6, 4.2, 1.7], at: [0.4, -0.8, 0.75], ms: 5200 },
              { from: [1.8, 1.9, 1.05], at: [-0.2, -0.4, 0.62], ms: 4800 },
            ],
            figures: [{ who: 'marit', q: 0, r: 0, facing: 4.5, pose: 'kneel' }],
            structures: [{ type: 'temple', q: 0, r: -3 }],
            alt: {
              unless: 'temple',
              cine: {
                place: 'keepYard',
                sky: 'dawn',
                cam: [
                  { from: [-1.2, 7.4, 3.2], at: [0.6, -1.6, 1.4], ms: 5200 },
                  { from: [-0.6, 4.2, 1.7], at: [0.4, -0.8, 0.85], ms: 5200 },
                  { from: [1.8, 1.9, 1.05], at: [-0.2, -0.4, 0.62], ms: 4800 },
                ],
                figures: [{ who: 'marit', q: 0, r: 0, facing: 4.5, pose: 'kneel' }],
              },
            },
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'brann'), ln('l2', 'marit'), ln('l3', 'marit'), ln('l4', 'marit'), ln('l5', 'brann'), ln('l6', 'marit')],
          cine: {
            place: 'temple',
            sky: 'dawn',
            cam: [
              { from: [-3.4, 6.6, 2.6], at: [0.4, -1.2, 1.0], ms: 8000 },
              { from: [-1.4, 3.3, 1.35], at: [0.3, -0.2, 0.8], ms: 9000 },
              { from: [1.6, 2.6, 1.15], at: [-0.3, 0, 0.8], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: 0, r: 0, facing: 1.6, pose: 'stand' },
              { who: 'brann', q: -1, r: 1, facing: 0.2, pose: 'stand' },
              { who: 'hero', q: 1, r: 1, facing: 2.4, pose: 'stand' },
            ],
            structures: [{ type: 'temple', q: 0, r: -3 }],
            alt: {
              unless: 'temple',
              cine: {
                place: 'keepYard',
                sky: 'dawn',
                cam: [
                  { from: [-3.4, 6.6, 2.6], at: [0.4, -1.2, 1.2], ms: 8000 },
                  { from: [-1.4, 3.3, 1.35], at: [0.3, -0.2, 0.8], ms: 9000 },
                  { from: [1.6, 2.6, 1.15], at: [-0.3, 0, 0.8], ms: 9000 },
                ],
                figures: [
                  { who: 'marit', q: 0, r: 0, facing: 1.6, pose: 'stand' },
                  { who: 'brann', q: -1, r: 1, facing: 0.2, pose: 'stand' },
                  { who: 'hero', q: 1, r: 1, facing: 2.4, pose: 'stand' },
                ],
              },
            },
          },
        },
        {
          // The first altar on the Burn's glass shore at sunrise: the canals, small (§4).
          kind: 'fight',
          id: 'shore',
          mode: 'canals',
          place: 'glassShore',
          budgetBp: 10000,
          foes: [
            { kind: 'wyrmling', n: 1, from: sec(6), every: sec(10), until: sec(900) },
            { kind: 'wyrmling', n: 2, from: sec(26), every: sec(26), until: sec(900) },
          ],
          goal: { kind: 'pool' },
          npcs: [{ id: 'marit', does: 'wait', hits: 40 }],
          says: [
            { on: 'cue', cue: 'cria', who: 'marit', line: 'l1', delay: sec(1) },
            { on: 'cue', cue: 'lit', who: 'vigil', line: 'l2' },
          ],
        },
        {
          kind: 'scene',
          id: 'tower',
          lines: [ln('l1', 'marit'), ln('l2', 'marit'), ln('l3', 'marit', { flag: '@quemadura.faro' })],
          cine: {
            place: 'watchtower',
            sky: 'day',
            cam: [
              { from: [-4.6, 7.4, 3.4], at: [1.4, -3.6, 3.2], ms: 8000 },
              { from: [-2.2, 3.6, 1.45], at: [0.6, -0.6, 0.95], ms: 9000 },
              { from: [1.8, 2.4, 1.1], at: [-0.2, 0.4, 0.85], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: 0, r: 1, facing: 4.7, pose: 'stand' },
              { who: 'hero', q: 1, r: 1, facing: 3.6, pose: 'stand' },
            ],
          },
        },
        {
          // The second altar at the foot of the first Vigil's watchtower, among molten ballistas: held while it lights.
          kind: 'fight',
          id: 'watch',
          mode: 'kindle',
          place: 'watchtower',
          radius: 6,
          budgetBp: 10000,
          foes: [
            { kind: 'wyrmling', n: 1, from: sec(4), every: sec(14), until: sec(95) },
            { kind: 'crow', n: 5, from: sec(16), every: sec(24), until: sec(95) },
            { kind: 'wyrmling', n: 2, from: sec(40), every: sec(30), until: sec(95) },
          ],
          goal: { kind: 'time', ticks: sec(90) },
          npcs: [
            { id: 'altar', does: 'wait', hits: 40, at: { q: 0, r: 0 }, prop: true },
            { id: 'marit', does: 'wait', hits: 40, at: { q: -1, r: 1 } },
          ],
          says: [{ on: 'cue', cue: 'cone', who: 'marit', line: 'l1' }],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['marit'],
          questions: [
            { id: 'why', lines: [ln('l1', 'marit')], set: ['askedWhy'] },
            {
              id: 'edda',
              lines: [ln('l1', 'marit'), ln('l2', 'marit'), ln('l3', 'marit', EDDA_UNSEEN), ln('l4', 'marit', EDDA_STANDING), ln('l5', 'marit', EDDA_FALLEN), ln('l6', 'marit', EDDA_RELIC)],
              set: ['askedEdda'],
            },
            { id: 'oath', lines: [ln('l1', 'marit')], set: ['askedOath'] },
          ],
          cine: {
            place: 'glassNight',
            sky: 'night',
            cam: [
              // From over the fire's far side, high enough that the two sit round it and the glass holds the stars.
              { from: [2.8, 3.2, 2.3], at: [-0.3, -0.2, 0.3], ms: 9000 },
              { from: [-1.4, 3.4, 1.7], at: [0.1, -0.3, 0.35], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: -1, r: 1, facing: 5.4, pose: 'sit' },
              { who: 'hero', q: 1, r: -1, facing: 2.2, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: back on the shore with the water going; two pools and the high one's mouth (§4, «Prueba»).
          kind: 'fight',
          id: 'ebb',
          mode: 'ebb',
          place: 'glassShore',
          budgetBp: 10000,
          foes: [
            { kind: 'wyrmling', n: 1, from: sec(8), every: sec(18), until: sec(900) },
            { kind: 'wyrmling', n: 2, from: sec(45), every: sec(50), until: sec(900) },
          ],
          goal: { kind: 'ebb' },
          npcs: [{ id: 'marit', does: 'wait', hits: 40 }],
          says: [{ on: 'cue', cue: 'drop', who: 'marit', line: 'l1' }],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [ln('l1', 'narrator'), ln('l2', 'marit'), ln('l3', 'marit'), ln('l4', 'marit'), ln('l5', 'narrator')],
          cine: {
            place: 'glassShore',
            sky: 'dawn',
            lit: true,
            cam: [
              // Wide over both eyes open and the still water between them, the sea behind; then down to Marit at the paper.
              { from: [2.2, 7.4, 4.4], at: [-3.6, 1.8, 0.3], ms: 9000 },
              { from: [0.2, 5.8, 1.8], at: [-3.0, 3.2, 0.7], ms: 9000 },
              { from: [0.4, 4.6, 1.25], at: [-2.4, 4.4, 0.95], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: -3, r: 4, facing: 0.2, pose: 'stand' },
              { who: 'hero', q: -2, r: 3, facing: 2.3, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 2, «Las salinas» (level 24): the white coast of the salt flats in fog, four houses of the salt, the
      // gate house in the middle, the third altar under water in a pool of the top step.
      id: 'salt',
      closedBy: 'marit',
      piece: 'saltHem',
      acts: [
        {
          // A white coast flat to the horizon, square pools mirroring the sky, four low stone houses; the fog comes off
          // the sea and wipes it out. A figure in a squire's armour, still in the fog, that is not Marit.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-paladin-2',
            place: 'saltFlats',
            sky: 'day',
            cam: [
              { from: [-9, 12, 6.4], at: [3, -6, 0], ms: 5400 },
              { from: [-4, 9, 2.6], at: [2, -2, 0.4], ms: 5200 },
              { from: [-1, 3, 1.3], at: [6, -9, 0.9], ms: 5200 },
            ],
            figures: [{ who: 'fled', q: 7, r: -9, facing: 4.5, pose: 'stand' }],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'salinera'), ln('l2', 'salinera'), ln('l3', 'marit'), ln('l4', 'salinera')],
          cine: {
            // At the flats' edge by the coast, the four houses of the salt behind the old salt-woman.
            place: 'saltFlats',
            sky: 'day',
            cam: [
              { from: [-2.6, 4.2, 2.8], at: [-4.4, 10.5, 0.6], ms: 8000 },
              { from: [-3.0, 6.0, 1.5], at: [-4.3, 9.4, 0.9], ms: 9000 },
              { from: [-4.8, 10.4, 1.3], at: [-3.4, 6.8, 0.9], ms: 9000 },
            ],
            figures: [
              { who: 'salinera', q: -4, r: 9, facing: 4.5, pose: 'stand' },
              { who: 'salter', q: -5, r: 9, facing: 4.2, pose: 'stand' },
              { who: 'marit', q: -4, r: 7, facing: 1.5, pose: 'stand' },
              { who: 'hero', q: -3, r: 7, facing: 1.4, pose: 'stand' },
            ],
          },
        },
        {
          // Across the flats in the fog to the gate house in the middle: what treads on salt is seen.
          kind: 'fight',
          id: 'fog',
          mode: 'fog',
          place: 'saltFlats',
          budgetBp: 10000,
          foes: [
            { kind: 'stalker', n: 1, from: sec(2), every: sec(3.5), until: sec(600) },
            { kind: 'stalker', n: 2, from: sec(9), every: sec(10), until: sec(600) },
          ],
          goal: { kind: 'house' },
          npcs: [{ id: 'marit', does: 'wait', hits: 40 }],
          says: [
            { on: 'cue', cue: 'prints', who: 'marit', line: 'l1', delay: sec(1) },
          ],
        },
        {
          kind: 'scene',
          id: 'door',
          lines: [ln('l1', 'knight'), ln('l2', 'varo'), ln('l3', 'marit'), ln('l4', 'varo'), ln('l5', 'varo')],
          cine: {
            // At the gate house's door (to the coast): Varo before it, his knight and Nuño by him; Marit and the hero
            // coming up the dyke.
            place: 'saltFlats',
            sky: 'day',
            cam: [
              { from: [-1.0, 10.5, 2.6], at: [-2.5, 4.5, 0.9], ms: 8000 },
              { from: [-1.4, 7.6, 1.3], at: [-2.4, 4.4, 1.0], ms: 9000 },
              { from: [-3.4, 4.4, 1.25], at: [-2.6, 7.0, 1.0], ms: 9000 },
            ],
            figures: [
              { who: 'varo', q: -2, r: 4, facing: 1.5, pose: 'stand' },
              { who: 'orderKnight', q: -3, r: 5, facing: 1.2, pose: 'stand' },
              { who: 'nuno', q: -2, r: 5, facing: 1.7, pose: 'stand' },
              { who: 'marit', q: -3, r: 7, facing: 4.5, pose: 'stand' },
              { who: 'hero', q: -4, r: 7, facing: 4.4, pose: 'stand' },
            ],
          },
        },
        {
          // The altar under water in a pool of the top step: empty it pool by pool through the gates (§4, «Las salinas»).
          kind: 'fight',
          id: 'gates',
          mode: 'sluice',
          place: 'saltFlats',
          budgetBp: 10000,
          foes: [{ kind: 'stalker', n: 2, from: sec(14), every: sec(22), until: sec(600) }],
          goal: { kind: 'drain' },
          npcs: [
            { id: 'marit', does: 'wait', hits: 40 },
            { id: 'nuno', does: 'wait', hits: 15, vital: false },
            { id: 'varo', does: 'wait', hits: 1, safe: true, vital: false },
            { id: 'orderKnight', does: 'wait', hits: 1, safe: true, vital: false },
          ],
          says: [
            { on: 'cue', cue: 'dry', who: 'marit', line: 'l1', delay: sec(1) },
            { on: 'cue', cue: 'nuno', who: 'nuno', line: 'l2', delay: sec(2) },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['marit'],
          questions: [
            { id: 'left', lines: [ln('l1', 'marit')], set: ['askedLeft'] },
            { id: 'varo', lines: [ln('l1', 'marit'), ln('l2', 'marit')], set: ['askedVaro'] },
            { id: 'fog', lines: [ln('l1', 'marit')], set: ['askedFog'] },
          ],
          cine: {
            // By the gate house's wall on the coast's side, at night, the fog against it; the salt folk asleep on the
            // dyke; Varo apart, on guard, not looking.
            place: 'saltNight',
            sky: 'night',
            cam: [
              { from: [-3.0, 8.4, 1.7], at: [-3.0, 5.6, 0.4], ms: 9000 },
              { from: [-0.6, 7.2, 1.2], at: [-3.6, 5.8, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: -4, r: 6, facing: 0, pose: 'sit' },
              { who: 'hero', q: -2, r: 6, facing: 3, pose: 'sit' },
              { who: 'salter', q: -5, r: 6, facing: 1, pose: 'lie' },
              { who: 'salter2', q: -6, r: 6, facing: 5, pose: 'lie' },
              { who: 'varo', q: -1, r: 6, facing: 0.2, pose: 'stand' },
            ],
            fire: { q: -3, r: 6 },
          },
        },
        {
          // The test: the altar in the air, held while it lights; the fog back, thicker; Marit's salt (§4).
          kind: 'fight',
          id: 'hold',
          mode: 'salt',
          place: 'saltFog',
          budgetBp: 10000,
          goal: { kind: 'time', ticks: sec(150) },
          npcs: [
            { id: 'altar3', does: 'wait', hits: 40, prop: true },
            { id: 'marit', does: 'wait', hits: 40 },
          ],
          says: [
            { on: 'start', who: 'marit', line: 'l1' },
            { on: 'cue', cue: 'leap', who: 'marit', line: 'l2' },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [ln('l1', 'narrator'), ln('l2', 'narrator'), ln('l3', 'varo'), ln('l4', 'marit'), ln('l5', 'varo'), ln('l6', 'narrator')],
          cine: {
            place: 'saltFlats',
            sky: 'day',
            lit: true,
            cam: [
              // Over the drained pools stepping down to the altar's, its eye open; then to the fog where she stands.
              { from: [1.4, 0.6, 2.4], at: [4, -4, 0.7], ms: 9000 },
              { from: [2.4, -1.4, 1.6], at: [8, -10, 1.0], ms: 9000 },
              { from: [3.2, -2.4, 1.3], at: [1.2, -3.2, 1.0], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: 2, r: -3, facing: 0.4, pose: 'stand' },
              { who: 'hero', q: 3, r: -2, facing: 0.6, pose: 'stand' },
              { who: 'varo', q: 1, r: -2, facing: 0.2, pose: 'stand' },
              { who: 'salinera', q: 0, r: -1, facing: 0.5, pose: 'stand' },
              { who: 'fled', q: 8, r: -10, facing: 3.2, pose: 'walk', delay: 7000, speed: 40, walkMs: 6000 },
            ],
          },
        },
      ],
    },
    {
      // Chapter 3, «El altar del puente» (level 30): a stone bridge of one arch over the rift between two plates of the
      // shell, in the rain; the fourth altar in its middle over a cistern carved in the arch; stairs up from both ends.
      // The board runs along r: the lower end (+r, Marit's), the upper end (−r, Ser Varo's), the altar at (0, 0).
      id: 'bridge',
      closedBy: 'marit',
      piece: 'bridgeLantern',
      acts: [
        {
          // One arch over the rift in the rain, the altar on its cistern in the middle; at the upper end Varo's knights
          // with crowbars, waiting for the rain to stop to break it. The clouds pass beneath.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-paladin-3',
            place: 'rainBridge',
            sky: 'dusk',
            cam: [
              { from: [9, 4, 2.2], at: [0, 0, 0.6], ms: 5600 },
              { from: [6, -1, 1.2], at: [1, -3, 0.8], ms: 5200 },
              { from: [3, -2, 1.1], at: [2, -4, 0.9], ms: 5200 },
            ],
            figures: [
              { who: 'varo', q: 2, r: -4, facing: 1.5, pose: 'stand' },
              { who: 'orderKnight', q: 1, r: -4, facing: 1.4, pose: 'stand' },
              { who: 'orderKnight2', q: 3, r: -4, facing: 1.6, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'marit'), ln('l2', 'varo'), ln('l3', 'marit'), ln('l4', 'varo')],
          cine: {
            place: 'rainBridge',
            sky: 'dusk',
            cam: [
              { from: [6, 3, 2.4], at: [0, 0, 0.6], ms: 8000 },
              { from: [3, 2, 1.3], at: [-1, 1, 0.85], ms: 9000 },
              { from: [3, -1, 1.25], at: [0, -2, 0.85], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: -1, r: 2, facing: 4.5, pose: 'stand' },
              { who: 'hero', q: 0, r: 2, facing: 4.7, pose: 'stand' },
              { who: 'varo', q: 1, r: -2, facing: 1.5, pose: 'stand' },
              { who: 'nuno', q: 0, r: -2, facing: 1.4, pose: 'stand' },
              { who: 'orderKnight', q: 2, r: -3, facing: 1.5, pose: 'stand' },
              { who: 'orderKnight2', q: 1, r: -3, facing: 1.5, pose: 'stand' },
            ],
          },
        },
        {
          // The two ends: bands up the stairs of both, seen in the clouds below; the aura reaches both only from the
          // altar (§4, «El aljibe del puente»).
          kind: 'fight',
          id: 'ends',
          mode: 'ends',
          place: 'rainBridge',
          budgetBp: 10000,
          goal: { kind: 'ends' },
          npcs: [
            { id: 'altar4', does: 'wait', hits: 40, prop: true },
            { id: 'marit', does: 'wait', hits: 40 },
            { id: 'varo', does: 'wait', hits: 24, vital: false },
            { id: 'orderKnight', does: 'wait', hits: 18, vital: false },
            { id: 'orderKnight2', does: 'wait', hits: 18, vital: false },
          ],
          says: [
            { on: 'cue', cue: 'first', who: 'varo', line: 'l1', delay: sec(1) },
            { on: 'cue', cue: 'both', who: 'marit', line: 'l2', delay: sec(1) },
          ],
        },
        {
          // The cistern and the gargoyles: fill it with the rain through the gutter where it falls; a gargoyle sweeps
          // its stairs, but spends the water (§4).
          kind: 'fight',
          id: 'gutters',
          mode: 'gutters',
          place: 'rainBridge',
          budgetBp: 10000,
          goal: { kind: 'cistern' },
          npcs: [
            { id: 'marit', does: 'wait', hits: 30 },
            { id: 'varo', does: 'wait', hits: 24, vital: false },
            { id: 'orderKnight', does: 'wait', hits: 18, vital: false },
            { id: 'orderKnight2', does: 'wait', hits: 18, vital: false },
          ],
          says: [{ on: 'cue', cue: 'gargoyle', who: 'marit', line: 'l1', delay: sec(1) }],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['marit', 'nuno'],
          questions: [
            { id: 'night', lines: [ln('l1', 'marit')], set: ['askedNight'] },
            { id: 'next', lines: [ln('l1', 'marit'), ln('l2', 'narrator')], set: ['askedNextTime'] },
            { id: 'nuno', lines: [ln('l1', 'nuno'), ln('l2', 'nuno')], set: ['askedNuno'] },
          ],
          cine: {
            place: 'rainNight',
            sky: 'night',
            // The rain has stopped: the altar burning in the cistern beside the fire.
            lit: true,
            cam: [
              { from: [4.4, 4.6, 3.4], at: [-1, 2, 0.4], ms: 9000 },
              { from: [-5.6, 6.4, 2.6], at: [-0.6, 1.6, 0.45], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: -2, r: 2, facing: 0, pose: 'sit' },
              { who: 'hero', q: 0, r: 2, facing: 3, pose: 'sit' },
              { who: 'nuno', q: -1, r: 3, facing: 1.6, pose: 'stand' },
              { who: 'varo', q: 2, r: -4, facing: 1.5, pose: 'stand' },
              { who: 'orderKnight', q: 1, r: -4, facing: 2.5, pose: 'sit' },
            ],
            fire: { q: -1, r: 2 },
          },
        },
        {
          // The test: the night in the middle; the altar burns while the cistern holds half; two ash mammoths (§4).
          kind: 'fight',
          id: 'night',
          mode: 'vigil',
          place: 'rainNight',
          budgetBp: 10000,
          goal: { kind: 'mammoth' },
          npcs: [
            { id: 'altar4', does: 'wait', hits: 40, prop: true },
            { id: 'marit', does: 'wait', hits: 40 },
            { id: 'varo', does: 'wait', hits: 24, vital: false },
            { id: 'orderKnight', does: 'wait', hits: 18, vital: false },
            { id: 'orderKnight2', does: 'wait', hits: 18, vital: false },
          ],
          says: [
            { on: 'cue', cue: 'side', who: 'marit', line: 'l1', delay: sec(1) },
            { on: 'cue', cue: 'mammoth2', who: 'marit', line: 'l2', delay: sec(2) },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'narrator'),
            ln('l3', 'varo'),
            ln('l4', 'varo'),
            ln('l5', 'varo'),
            ln('l6', 'marit'),
            ln('l7', 'marit'),
            ln('l8', 'marit'),
            ln('l9', 'varo'),
            ln('l10', 'marit', { flag: 'askedEdda' }),
            ln('l11', 'narrator'),
          ],
          cine: {
            place: 'rainNight',
            sky: 'night',
            lit: true,
            cam: [
              // Down the upper stairs where the water ran, the knights alive at its head; then to the moon in the
              // cistern and Marit by it with a wet paper, still.
              { from: [9, -7, 4.6], at: [3, -6, -1.2], ms: 9000 },
              { from: [4.6, 3.4, 3.2], at: [0, 0.4, 0.3], ms: 9000 },
              { from: [3.4, 3.6, 2.2], at: [-0.6, 1, 0.7], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: -1, r: 1, facing: 0.6, pose: 'stand' },
              { who: 'hero', q: -1, r: 2, facing: 5.5, pose: 'stand' },
              { who: 'varo', q: 0, r: 1, facing: 3.2, pose: 'stand' },
              { who: 'nuno', q: 1, r: 1, facing: 3.4, pose: 'stand' },
              { who: 'orderKnight', q: 2, r: -4, facing: 2.2, pose: 'stand' },
              { who: 'orderKnight2', q: 3, r: -4, facing: 2.4, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 4, «El monasterio de la cascada» (level 36): a monastery of pale stone at the foot of a waterfall, in a
      // gorge; its old dam and wheel over the pond. The board runs along r: the gorge south (+r) down toward the Way, the
      // door, the patio, the cloister with its cistern, the chapel north (−r); the ravine along its east side.
      id: 'monastery',
      closedBy: 'marit',
      piece: 'eyeBrooch',
      acts: [
        {
          // The Way many years back, in the morning: a tall knight and a squire of seventeen climb a gorge together, a
          // waterfall at its end falling into a pond, a monastery of pale stone with an old dam at its foot. The knight
          // laughs with her eyes.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-paladin-4',
            place: 'gorgePast',
            sky: 'day',
            memory: true,
            cam: [
              { from: [-7, 14.9, 1.8], at: [8.7, -17.3, 5.7], ms: 5600 },
              { from: [-4.4, 11.7, 1.2], at: [-3.9, 7.7, 0.7], ms: 5200 },
              { from: [-4, 6, 0.9], at: [-4.6, 9.2, 0.7], ms: 5600 },
            ],
            figures: [
              { who: 'edda', q: -5, r: 9, facing: 4.5, pose: 'walk', speed: 18, walkMs: 9000 },
              { who: 'maritYoung', q: -4, r: 9, facing: 4.5, pose: 'walk', speed: 18, walkMs: 9000 },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'marit'), ln('l2', 'marit')],
          cine: {
            place: 'monastery',
            sky: 'dusk',
            cam: [
              { from: [-4.25, 11.2, 1.3], at: [-4.5, 8, 0.7], ms: 9000 },
              { from: [-6.2, 9.8, 0.9], at: [4.8, -9.6, 3.2], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: -5, r: 8, facing: 4.5, pose: 'stand' },
              { who: 'hero', q: -4, r: 8, facing: 4.6, pose: 'stand' },
            ],
          },
        },
        {
          // The memory (docs/SENDAS.md, emotion, piece 9): played as Marit at seventeen, the squire, by Ser Edda at the
          // monastery's door while a small Marea comes up the gorge (§4; battle/monastery.ts `porch`).
          kind: 'fight',
          id: 'door',
          mode: 'porch',
          place: 'gorgePast',
          budgetBp: 10000,
          memory: 'maritYoung',
          goal: { kind: 'tide', ticks: SENDA.monastery.porch.ticks },
          npcs: [{ id: 'edda', does: 'wait', hits: 999, vital: false }],
          says: [
            { on: 'at', t: sec(9), who: 'edda', line: 'l1' },
            { on: 'cue', cue: 'blow', who: 'edda', line: 'l2', delay: sec(1) },
            { on: 'at', t: SENDA.monastery.porch.ticks - sec(4), who: 'edda', line: 'l3' },
          ],
        },
        {
          // Won: the memory's last of it, by the pond, the squire and her knight.
          kind: 'scene',
          id: 'pond',
          lines: [ln('l1', 'maritYoung'), ln('l2', 'edda'), ln('l3', 'edda')],
          cine: {
            place: 'gorgePast',
            sky: 'day',
            memory: true,
            cam: [
              { from: [7.7, -14.6, 1.2], at: [12.8, -15, 0.5], ms: 9000 },
              { from: [9.1, -12.9, 0.7], at: [11.8, -14.6, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'edda', q: 12, r: -14, facing: 4.0, pose: 'stand' },
              { who: 'maritYoung', q: 11, r: -14, facing: 3.8, pose: 'sit' },
            ],
          },
        },
        {
          // The present again, at the door: an old monk opens and stays looking, a long while.
          kind: 'scene',
          id: 'monk',
          lines: [ln('l1', 'narrator'), ln('l2', 'monk'), ln('l3', 'monk'), ln('l4', 'marit'), ln('l5', 'tadeo')],
          cine: {
            place: 'monastery',
            sky: 'dusk',
            cam: [
              { from: [-0.9, 7.2, 1.4], at: [-1.35, 2.7, 0.7], ms: 9000 },
              { from: [-3.7, 5.1, 1.0], at: [-1.25, 2.5, 0.7], ms: 9000 },
              { from: [-0.7, 1.9, 0.9], at: [-2.3, 3.95, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'monk', q: -1, r: 2, facing: 1.5, pose: 'stand' },
              { who: 'marit', q: -3, r: 4, facing: 4.5, pose: 'stand' },
              { who: 'hero', q: -2, r: 4, facing: 4.6, pose: 'stand' },
            ],
          },
        },
        {
          // Inside: something puts out the fifth altar, spectres no one sees out of one of the cells; found with the
          // refectory's salt and shut with a line of it (§4, «Las huellas»; battle/monastery.ts `cells`).
          kind: 'fight',
          id: 'cells',
          mode: 'cells',
          place: 'monastery',
          budgetBp: 10000,
          goal: { kind: 'cell' },
          npcs: [
            { id: 'marit', does: 'wait', hits: 30 },
            { id: 'tadeo', does: 'wait', hits: 1, safe: true, vital: false },
          ],
          says: [{ on: 'at', t: sec(8), who: 'tadeo', line: 'l1' }],
        },
        {
          // The kitchen at night, with Tadeo and the waterfall's noise; Marit with the letter in her hand.
          kind: 'scene',
          id: 'kitchen',
          lines: [ln('l1', 'marit'), ln('l2', 'marit'), ln('l3', 'tadeo'), ln('l4', 'marit'), ln('l5', 'marit'), ln('l6', 'tadeo')],
          cine: {
            place: 'monasteryNight',
            sky: 'night',
            cam: [
              { from: [-1.9, 0.5, 1.8], at: [-5.2, 0, 0.17], ms: 9000 },
              { from: [-6.25, -0.67, 1.4], at: [-4.3, 0.29, 0.25], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: -5.9, r: 0.5, facing: 0.2, pose: 'sit' },
              { who: 'tadeo', q: -4.2, r: -0.5, facing: 2.8, pose: 'sit' },
              { who: 'hero', q: -4.6, r: 0.55, facing: 3.4, pose: 'sit' },
            ],
            fire: { q: -5, r: 0 },
          },
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['marit', 'tadeo'],
          questions: [
            { id: 'fault', lines: [ln('l1', 'marit')], set: ['toldNotHerFault'] },
            { id: 'love', lines: [ln('l1', 'marit')], set: ['askedLove'] },
            { id: 'shield', lines: [ln('l1', 'marit')], set: ['showedShield'], if: EDDA_RELIC },
          ],
          cine: {
            place: 'monasteryNight',
            sky: 'night',
            cam: [
              { from: [-1.9, 0.5, 1.8], at: [-5.2, 0, 0.17], ms: 9000 },
              { from: [-6.25, -0.67, 1.4], at: [-4.3, 0.29, 0.25], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: -5.9, r: 0.5, facing: 0.2, pose: 'sit' },
              { who: 'tadeo', q: -4.2, r: -0.5, facing: 2.8, pose: 'sit' },
              { who: 'hero', q: -4.6, r: 0.55, facing: 3.4, pose: 'sit' },
            ],
            fire: { q: -5, r: 0 },
          },
        },
        {
          // The test: the sixth altar at the bottom of the cloister's cistern, emptied by its gates while the siege
          // comes back and the waterfall keeps filling it (§4, «El monasterio»; battle/monastery.ts `cloister`).
          kind: 'fight',
          id: 'cloister',
          mode: 'cloister',
          place: 'monasteryDawn',
          budgetBp: 10000,
          goal: { kind: 'sixth' },
          npcs: [
            { id: 'marit', does: 'wait', hits: 40 },
            { id: 'tadeo', does: 'wait', hits: 15 },
          ],
          says: [{ on: 'cue', cue: 'reflect', who: 'fled', line: 'l1', delay: sec(1) }],
          trial: true,
        },
        {
          // The lowest point (no fight): the siege breaks the door and fills the gorge; Tadeo goes to the old dam's
          // wheel and lets the whole waterfall go. From the stair's first bend, only water.
          kind: 'scene',
          id: 'low',
          lines: [ln('l1', 'narrator'), ln('l2', 'tadeo'), ln('l3', 'marit'), ln('l4', 'tadeo'), ln('l5', 'tadeo'), ln('l6', 'narrator'), ln('l7', 'narrator')],
          cine: {
            place: 'monasteryDawn',
            sky: 'dawn',
            flood: true,
            cam: [
              // The dam over the pond, Tadeo at its wheel.
              { from: [9.2, -11.2, 3.2], at: [14.4, -15.4, 1.0], ms: 9000 },
              // The white wall down the ravine and the gorge.
              { from: [11.1, -3.9, 7.3], at: [-0.5, 7.7, -0.2], ms: 9000 },
              // From the stair's first bend: only water where the monastery was.
              { from: [2.5, 8.1, 4.0], at: [0, 0, 0.35], ms: 9000 },
            ],
            figures: [
              { who: 'tadeo', q: 14, r: -15, facing: 0, pose: 'stand' },
              { who: 'marit', q: 2, r: 6, facing: 4.9, pose: 'sit' },
              { who: 'hero', q: 3, r: 6, facing: 4.6, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'choice',
          id: 'choice',
          lines: [ln('l1', 'marit'), ln('l2', 'marit'), ln('l3', 'marit')],
          options: [
            { id: 'together', set: ['sworeTogether'] },
            { id: 'instead', set: ['sworeInstead'] },
          ],
          cine: {
            place: 'monasteryDawn',
            sky: 'dawn',
            cam: [
              { from: [3.4, 7.5, 3.7], at: [2.5, 6, 3.1], ms: 9000 },
              { from: [0.4, 7.3, 3.5], at: [2.5, 6, 3.1], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: 2, r: 6, facing: 4.9, pose: 'sit' },
              { who: 'hero', q: 3, r: 6, facing: 3.4, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'narrator', { flag: 'sworeTogether' }),
            ln('l2', 'marit', { flag: 'sworeTogether' }),
            ln('l3', 'narrator', { flag: 'sworeInstead' }),
            ln('l4', 'marit', { flag: 'sworeInstead' }),
            ln('l5', 'narrator'),
            ln('l6', 'marit'),
            ln('l7', 'narrator'),
          ],
          cine: {
            place: 'monasteryDawn',
            sky: 'dawn',
            torches: true,
            cam: [
              { from: [3.4, 7.6, 3.7], at: [2.5, 6, 3.2], ms: 9000 },
              // Up from the bend to the summit: the Order's torches round the keep.
              { from: [2.2, 8.3, 3.8], at: [12.1, -80.8, 90], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: 2, r: 6, facing: 4.4, pose: 'stand' },
              { who: 'hero', q: 3, r: 6, facing: 4.6, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      id: 'seventh',
      closedBy: 'marit',
      piece: 'wayTabard',
      call: true,
      // Lost to La Que Huyó, a closed chapter opens again at her fight: the stair, the descent and the cistern stay won.
      keep: 'fled',
      acts: [
        {
          // Somnhold at night from below, as someone coming up the Way would see it: the keep, at its foot a heap of old
          // rubble, and the Order's knights with torches.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-paladin-5',
            place: 'keepFar',
            sky: 'night',
            cam: [
              { from: [-8.5, 17, 0.5], at: [4.2, -8.4, 5.2], ms: 6000 },
              { from: [-5.8, 11.6, 0.9], at: [2.1, -4.2, 1.6], ms: 6000 },
            ],
            figures: [
              { who: 'orderKnight', q: -2, r: -2, facing: 4.4, pose: 'stand' },
              { who: 'orderKnight2', q: 3, r: -3, facing: 4.0, pose: 'stand' },
              { who: 'varo', q: 1, r: -2, facing: 1.5, pose: 'stand' },
            ],
          },
        },
        {
          // The bastion's yard at night: Varo before the rubble.
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'varo'), ln('l2', 'marit'), ln('l3', 'varo'), ln('l4', 'marit'), ln('l5', 'varo'), ln('l6', 'marit', { flag: 'askedOath' })],
          cine: {
            place: 'keepNight',
            sky: 'night',
            cam: [
              { from: [-1.6, 4.4, 1.3], at: [0.6, -1.4, 0.8], ms: 9000 },
              { from: [2.6, 1.6, 1.1], at: [-0.2, -0.6, 0.8], ms: 9000 },
            ],
            figures: [
              { who: 'varo', q: 1, r: -2, facing: 1.5, pose: 'stand' },
              { who: 'orderKnight', q: -1, r: -2, facing: 1.8, pose: 'stand' },
              { who: 'orderKnight2', q: 3, r: -3, facing: 1.4, pose: 'stand' },
              { who: 'marit', q: 0, r: 0, facing: 4.6, pose: 'stand' },
              { who: 'hero', q: 1, r: 0, facing: 4.4, pose: 'stand' },
              { who: 'nuno', q: -1, r: 1, facing: 4.8, pose: 'stand' },
            ],
          },
        },
        {
          // Act 1: the stair under the keep, opened from the fall's rubble while the old garrison comes up the holes
          // (battle/seventh.ts `rubble`).
          kind: 'fight',
          id: 'rubble',
          mode: 'rubble',
          place: 'keepNight',
          night: true,
          budgetBp: 12500,
          goal: { kind: 'rubble' },
          npcs: [
            { id: 'marit', does: 'follow', hits: 40, follow: 2 },
            { id: 'nuno', does: 'follow', hits: 1, safe: true, vital: false, follow: 3 },
            { id: 'varo', does: 'wait', hits: 1, safe: true, vital: false },
            { id: 'orderKnight', does: 'wait', hits: 1, safe: true, vital: false },
            { id: 'orderKnight2', does: 'wait', hits: 1, safe: true, vital: false },
          ],
          says: [
            { on: 'start', who: 'nuno', line: 'l1', delay: sec(4) },
            { on: 'cue', cue: 'stair', who: 'marit', line: 'l2', delay: sec(1) },
          ],
        },
        {
          // Act 2: down to the roots of the keep, rooms older than the fortress, eyes carved in their walls that light as
          // a paladin goes by (a crypt's descent, its last hall the cistern).
          kind: 'fight',
          id: 'roots',
          mode: 'descent',
          place: 'roots',
          rooms: 2,
          budgetBp: 9000,
          bestiary: { skeleton: 3, wraith: 2 },
          goal: { kind: 'reach' },
          npcs: [{ id: 'marit', does: 'follow', hits: 40, follow: 2 }],
          says: [{ on: 'at', t: sec(10), who: 'marit', line: 'l1' }],
        },
        {
          // At the cistern's edge, the stair above and the still water below. Nuño came down after them, with bread.
          kind: 'scene',
          id: 'edge',
          lines: [ln('l1', 'narrator'), ln('l2', 'nuno')],
          cine: {
            place: 'cistern7',
            sky: 'night',
            cam: [
              { from: [-1.6, 10.2, 1.7], at: [0, 0, 0.4], ms: 9000 },
              { from: [-5.2, 9.6, 1.2], at: [-4.3, 8.5, 0.35], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: -5.1, r: 9.0, facing: 0.3, pose: 'sit' },
              { who: 'nuno', q: -3.6, r: 9.1, facing: 3.2, pose: 'walk', speed: 14, walkMs: 2600 },
              { who: 'hero', q: -4.4, r: 7.9, facing: 1.4, pose: 'sit' },
            ],
            fire: { q: -4.3, r: 8.6 },
          },
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['marit', 'nuno'],
          questions: [
            { id: 'after', lines: [ln('l1', 'marit')], set: ['askedAfter'] },
            { id: 'nunoVaro', lines: [ln('l1', 'nuno')], set: ['askedNunoVaro'] },
            { id: 'edda', lines: [ln('l1', 'marit')], set: ['askedEddaWords'] },
          ],
          cine: {
            place: 'cistern7',
            sky: 'night',
            cam: [
              { from: [-2.6, 9.9, 1.3], at: [-4.4, 8.6, 0.3], ms: 9000 },
              { from: [-5.6, 7.4, 1.4], at: [-4.2, 8.7, 0.35], ms: 9000 },
            ],
            figures: [
              { who: 'marit', q: -5.1, r: 9.0, facing: 0.3, pose: 'sit' },
              { who: 'nuno', q: -3.7, r: 9.2, facing: 3.4, pose: 'sit' },
              { who: 'hero', q: -4.4, r: 7.9, facing: 1.4, pose: 'sit' },
            ],
            fire: { q: -4.3, r: 8.6 },
          },
        },
        {
          // Act 3, the test: the seventh altar on its island in the cistern, brought into the air by its four old gates
          // without emptying it (§4, «El séptimo»; battle/seventh.ts `well`). What is left keeps for the fight.
          kind: 'fight',
          id: 'well',
          mode: 'well',
          place: 'cistern7',
          night: true,
          budgetBp: 12500,
          goal: { kind: 'ring' },
          npcs: [{ id: 'marit', does: 'wait', hits: 40 }],
          says: [
            { on: 'start', who: 'marit', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'bare', who: 'marit', line: 'l2', delay: sec(1) },
          ],
          trial: true,
          keep: ['ring'],
        },
        {
          // The ones who come and the speech (docs/SENDAS.md, emotion, pieces 7 and 8): torches down the stair, the salt
          // folk with their salt; Varo's two knights; Marit at the foot of the seventh altar, played on the engine.
          kind: 'choice',
          id: 'speech',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'marit'),
            ln('l3', 'salinera'),
            ln('l4', 'marit'),
            ln('l5', 'nuno'),
            ln('l6', 'marit'),
            ln('l7', 'salinera'),
            ln('l8', 'marit'),
            { id: 'l9', who: 'narrator', cue: 'kneel' },
          ],
          options: [
            { id: 'noneDown', set: ['speechNoneDown'] },
            { id: 'allStay', set: ['speechAllStay'] },
          ],
          cine: {
            place: 'cistern7',
            sky: 'night',
            cam: [
              // Down the stair with the torches; then from the island, Marit against the people come down; then low on the
              // water, the knights' fists on their shields.
              { from: [1.6, 9.6, 2.4], at: [-3.6, 7.4, 0.8], ms: 12000 },
              { from: [0.8, -1.1, 0.9], at: [-2.8, 6.4, 0.9], ms: 14000 },
              { from: [-2.2, 4.2, 0.6], at: [-3.4, 6.9, 0.7], ms: 14000 },
            ],
            figures: [
              { who: 'marit', q: -0.6, r: 1.1, facing: 4.5, pose: 'stand' },
              { who: 'hero', q: 0.3, r: 1.2, facing: 4.6, pose: 'stand' },
              { who: 'salinera', q: -4.1, r: 8.1, facing: 1.5, pose: 'stand' },
              { who: 'salter', q: -3.3, r: 8.4, facing: 1.6, pose: 'stand' },
              { who: 'salter2', q: -4.9, r: 8.6, facing: 1.4, pose: 'stand' },
              { who: 'orderKnight', q: -4.2, r: 7.1, facing: 1.5, pose: 'stand' },
              { who: 'orderKnight2', q: -2.8, r: 7.1, facing: 1.5, pose: 'stand' },
              { who: 'nuno', q: -3.4, r: 6.7, facing: 1.5, pose: 'stand' },
            ],
          },
        },
        {
          // Act 4: La Que Huyó, Marit's own nightmare, comes for her across the cistern (§5; battle/seventh.ts `fled`).
          kind: 'fight',
          id: 'fled',
          mode: 'fled',
          place: 'cistern7',
          night: true,
          budgetBp: 12500,
          goal: { kind: 'fled' },
          npcs: [
            { id: 'marit', does: 'wait', hits: 40 },
            { id: 'nuno', does: 'wait', hits: 1, safe: true, vital: false },
            { id: 'orderKnight', does: 'wait', hits: 10, vital: false },
            { id: 'orderKnight2', does: 'wait', hits: 10, vital: false },
            { id: 'salinera', does: 'wait', hits: 10, vital: false },
            { id: 'salter', does: 'wait', hits: 10, vital: false },
            { id: 'varo', does: 'wait', hits: 12, vital: false },
          ],
          says: [
            { on: 'start', who: 'marit', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'line', who: 'nuno', line: 'l2' },
            { on: 'cue', cue: 'gone', who: 'nuno', line: 'l3' },
            { on: 'cue', cue: 'hide', who: 'runaway', line: 'l4' },
            { on: 'cue', cue: 'hide', who: 'marit', line: 'l5', delay: sec(3) },
            { on: 'cue', cue: 'varo', who: 'varo', line: 'l6' },
            { on: 'cue', cue: 'still', who: 'runaway', line: 'l7' },
          ],
          foe: true,
          trial: true,
          scarBp: SENDA.seventh.fled.scarBp,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'marit', { flag: 'sworeTogether' }),
            ln('l3', 'marit', { flag: 'sworeTogether' }),
            ln('l8', 'narrator', { flag: 'sworeInstead' }),
            ln('l4', 'marit', { flag: 'sworeInstead' }),
            ln('l5', 'marit', { flag: 'sworeInstead' }),
            ln('l6', 'varo'),
            ln('l7', 'varo'),
          ],
          cine: {
            place: 'cistern7',
            sky: 'night',
            eye: true,
            cam: [
              // Up at the eye of the seventh altar, open; then Varo at the cistern's edge, his cloak wet.
              { from: [0.9, 2.6, 1.1], at: [-0.4, 0.3, 1.0], ms: 10000 },
              { from: [-0.6, 3.4, 0.9], at: [-3.4, 7.0, 0.8], ms: 10000 },
            ],
            figures: [
              { who: 'marit', q: -0.6, r: 1.1, facing: 1.5, pose: 'stand', if: { flag: 'sworeInstead' } },
              { who: 'marit', q: -0.4, r: 0.9, facing: 1.5, pose: 'kneel', if: { flag: 'sworeTogether' } },
              { who: 'hero', q: 0.4, r: 1.0, facing: 2.0, pose: 'stand' },
              { who: 'varo', q: -3.5, r: 7.2, facing: 1.5, pose: 'stand' },
              { who: 'nuno', q: -2.7, r: 7.4, facing: 1.6, pose: 'stand' },
            ],
          },
        },
        {
          // The top of the keep in the morning sun; below in the yard the salt folk eat bread with Varo's knights.
          kind: 'scene',
          id: 'epilogue',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'narrator', { flag: '@temple' }),
            ln('l3', 'narrator', { notFlag: '@temple' }),
            ln('l4', 'marit'),
            ln('l5', 'varo'),
            ln('l6', 'marit'),
            ln('l7', 'marit', { flag: 'sworeTogether' }),
            ln('l8', 'marit', { flag: 'sworeTogether' }),
            ln('l11', 'nuno', { flag: 'speechNoneDown' }),
            ln('l12', 'nuno', { flag: 'speechAllStay' }),
            ln('l9', 'marit', { flag: 'sworeInstead' }),
            ln('l10', 'marit', { flag: 'sworeInstead' }),
            ln('l13', 'marit', { flag: 'askedLove' }),
            ln('l14', 'varo', { flag: 'toldNotHerFault' }),
            ln('l15', 'varo', { flag: 'askedNunoVaro' }),
            ln('l16', 'marit', { flag: 'askedVaro' }),
            ln('l17', 'narrator'),
          ],
          cine: {
            video: 'senda-paladin-5-end',
            place: 'keepTop',
            sky: 'dawn',
            cam: [
              { from: [2.6, 3.6, 1.6], at: [-0.6, -0.4, 0.9], ms: 10000 },
              // (Over Marit's shoulder and down, steep enough to clear the terrace's lip: the bread table in the yard.)
              { from: [-1.14, 0.29, 2.0], at: [6.1, -15.9, -7.6], ms: 12000 },
            ],
            figures: [
              { who: 'marit', q: 0, r: -1, facing: 4.5, pose: 'stand' },
              { who: 'varo', q: 1, r: -1, facing: 4.4, pose: 'stand' },
              { who: 'hero', q: 0, r: 1, facing: 4.6, pose: 'stand' },
              { who: 'nuno', q: -1, r: 1, facing: 4.8, pose: 'stand' },
            ],
          },
        },
      ],
    },
  ],
};

/**
 * The artificer's path, Tormo (docs/sendas/artifice.md): an old blind dwarf, Nadd, asks an artificer to finish the golem
 * he promised fifty-five years ago to hold up the cliff's wall over Grada. Chapter 1, «Los planos» (level 18): by night
 * a small bronze golem comes up the bastion's road with a letter at its neck and sits to wait under the guards' bows;
 * Hilde at the forge knows the mark (the yard where there is no forge); the goat path up the Flank Cliff behind Grillo
 * (stones off the nests, the slingers, the soot crows; Grillo crawls to the next rest when he falls); the workshop's
 * door, Dunna and Nadd; down through the sleeping workshop with Dunna (Nadd's old golems, their boiler in the back);
 * the campfire at Nadd's forge, with soup; and the test, the wheel and the belt (the gear train for the lift, golems
 * waking one by one, the Overseer); closing in Tormo's cavern, headless, and the unsigned letter of the clan.
 */
const ARTIFICER: SendaDef = {
  id: 'artificer',
  cls: 'artificer',
  planned: 5,
  cast: {
    grillo: { model: 'grillo' },
    hilde: { npc: 'hilde' },
    // Nadd's letter, read by the hero; the clan's letter, unsigned.
    letter: { voice: true },
    clanLetter: { voice: true },
    // Nadd's note in the margin of the cable's plan.
    note: { voice: true },
    girl: { model: 'dunna' },
    dunna: { model: 'dunna' },
    nadd: { model: 'nadd' },
    // The bastion's guards on the road at night (two of the garrison).
    sentry: { cls: 'warrior', tier: 1, look: 'longsword~common~shield~common~coif~hauberk~mailBoots' },
    sentry2: { cls: 'ranger', tier: 1, look: 'longbow~common~quiver~common~hood~jerkin~leatherBoots' },
    tormo: { model: 'tormoHull' },
    // Chapter 3: Adela of Grada (a woman with a pick on her shoulder, till Nadd names her) and her quarrymen; Brask (a
    // dwarf in a cloak, till Nadd names him) and the clan behind him.
    pickWoman: { model: 'adela' },
    adela: { model: 'adela' },
    quarry: { model: 'quarryman' },
    quarry2: { model: 'quarrywoman' },
    cloak: { model: 'brask' },
    brask: { model: 'brask' },
    clan: { model: 'clanDwarf' },
    clan2: { model: 'clanDwarf2' },
    // Chapter 4: the memory's people (young Nadd, Bruni his apprentice, Grillo new, the two old golems from the forge,
    // the girl with a pick who is Adela), and Grillo come back broken.
    naddYoung: { model: 'naddYoung' },
    bruni: { model: 'bruniYoung' },
    grilloNew: { model: 'grilloNew' },
    golem1: { model: 'oldGolem' },
    golem2: { model: 'oldGolem' },
    pickGirl: { model: 'adelaGirl' },
    grilloBroken: { model: 'grilloBroken' },
    // Chapter 5: Tormo whole and standing (his head on, his eyes lit), the quarrymen of Grada at the yard's crossbows.
    tormoUp: { model: 'tormo' },
    // (His states in the scenes: what got in looking out of him; tied in the yard; on his knees to Nadd once freed.)
    tormoDark: { model: 'tormoDark' },
    tormoTied: { model: 'tormoTied' },
    tormoKnelt: { model: 'tormoKnelt' },
    quarry3: { model: 'quarryman' },
  },
  chapters: [
    {
      id: 'plans',
      closedBy: 'nadd',
      piece: 'keyBelt',
      acts: [
        {
          // By night a small bronze golem comes up the bastion's road with a letter tied at its neck; the guards draw on
          // it; it sits and waits.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-artificer-1',
            place: 'bastionGate',
            sky: 'night',
            cam: [
              { from: [-1.5, 8.6, 2.6], at: [0.2, 2.4, 0.5], ms: 5200 },
              { from: [1.6, 4.6, 1.5], at: [0, 1.0, 0.45], ms: 5200 },
              { from: [-1.4, 2.4, 1.05], at: [0.2, 0.2, 0.4], ms: 5000 },
            ],
            figures: [
              { who: 'grillo', q: 0, r: 6, facing: 4.5, pose: 'walk', delay: 600, speed: 22, walkMs: 7200 },
              { who: 'sentry', q: -1, r: -1, facing: 1.2, pose: 'fight' },
              { who: 'sentry2', q: 1, r: -2, facing: 1.9, pose: 'fight' },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'hilde'), ln('l2', 'hilde'), ln('l3', 'letter')],
          cine: {
            place: 'forgeYard',
            sky: 'night',
            cam: [
              { from: [-3.2, 6.4, 2.5], at: [0.4, -0.8, 0.9], ms: 8000 },
              { from: [-1.2, 3.0, 1.3], at: [0.3, 0, 0.6], ms: 9000 },
              { from: [1.4, 2.2, 1.0], at: [-0.2, 0.2, 0.45], ms: 9000 },
            ],
            figures: [
              { who: 'hilde', q: -1, r: 0, facing: 0.3, pose: 'stand' },
              { who: 'grillo', q: 0, r: 1, facing: 4.6, pose: 'sit' },
              { who: 'hero', q: 1, r: 0, facing: 3.1, pose: 'stand' },
            ],
            structures: [{ type: 'forge', q: -1, r: -3 }],
            alt: {
              unless: 'forge',
              cine: {
                place: 'forgeYard',
                sky: 'night',
                cam: [
                  { from: [-3.2, 6.4, 2.5], at: [0.4, -0.8, 0.9], ms: 8000 },
                  { from: [-1.2, 3.0, 1.3], at: [0.3, 0, 0.6], ms: 9000 },
                  { from: [1.4, 2.2, 1.0], at: [-0.2, 0.2, 0.45], ms: 9000 },
                ],
                figures: [
                  { who: 'hilde', q: -1, r: 0, facing: 0.3, pose: 'stand' },
                  { who: 'grillo', q: 0, r: 1, facing: 4.6, pose: 'sit' },
                  { who: 'hero', q: 1, r: 0, facing: 3.1, pose: 'stand' },
                ],
              },
            },
          },
        },
        {
          // The goat path cut in the Flank Cliff: Grillo ahead, the sea below (§3, act 1).
          kind: 'fight',
          id: 'path',
          mode: 'cornice',
          place: 'cornice',
          weather: 'wind',
          budgetBp: 9000,
          goal: { kind: 'grillo' },
          npcs: [{ id: 'grillo', does: 'wait', hits: 12, vital: false }],
          says: [
            { on: 'cue', cue: 'sling', who: 'grillo', line: 'l1' },
            { on: 'cue', cue: 'down1', who: 'grillo', line: 'l2' },
            { on: 'cue', cue: 'door', who: 'grillo', line: 'l3' },
          ],
        },
        {
          // The door opens on the workshop cut in the rock: beams, pulleys, chains going down into the dark.
          kind: 'scene',
          id: 'door',
          lines: [ln('l1', 'girl'), ln('l2', 'dunna'), ln('l3', 'nadd')],
          cine: {
            place: 'shopTop',
            sky: 'night',
            cam: [
              { from: [-3.0, 5.6, 2.4], at: [0.6, -1.4, 1.2], ms: 8000 },
              { from: [-1.0, 2.8, 1.25], at: [0.2, -0.3, 0.75], ms: 9000 },
              { from: [1.3, 2.3, 0.95], at: [-0.4, 0, 0.55], ms: 9000 },
            ],
            figures: [
              { who: 'dunna', q: 0, r: -1, facing: 1.5, pose: 'stand' },
              { who: 'nadd', q: -1, r: -1, facing: 1.2, pose: 'stand' },
              { who: 'grillo', q: 1, r: 0, facing: 3.8, pose: 'stand' },
              { who: 'hero', q: 0, r: 1, facing: 4.5, pose: 'stand' },
            ],
          },
        },
        {
          // Down through the sleeping workshop with Dunna: five halls, Nadd's old golems at their posts (§3, act 2).
          kind: 'fight',
          id: 'halls',
          mode: 'workshop',
          place: 'workshop',
          budgetBp: 9000,
          goal: { kind: 'tormo' },
          npcs: [{ id: 'dunna', does: 'follow', hits: 28, follow: 1.4 }],
          says: [
            { on: 'start', who: 'dunna', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'wake', who: 'dunna', line: 'l2' },
            { on: 'cue', cue: 'low', who: 'dunna', line: 'l3' },
            { on: 'cue', cue: 'boiler', who: 'dunna', line: 'l4', delay: sec(1) },
            { on: 'cue', cue: 'grille1', who: 'dunna', line: 'l5' },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['nadd'],
          questions: [
            { id: 'eyes', lines: [ln('l1', 'nadd'), ln('l2', 'nadd')], set: ['askedEyes'] },
            { id: 'why', lines: [ln('l1', 'nadd'), ln('l2', 'nadd')], set: ['askedWhy'] },
            { id: 'hilde', lines: [ln('l1', 'nadd'), ln('l2', 'nadd')], set: ['askedHilde'] },
          ],
          cine: {
            place: 'naddForge',
            sky: 'night',
            cam: [
              { from: [2.6, 3.0, 2.1], at: [-0.3, -0.3, 0.35], ms: 9000 },
              { from: [-1.6, 3.2, 1.6], at: [0.1, -0.3, 0.4], ms: 9000 },
            ],
            figures: [
              { who: 'nadd', q: -1, r: 1, facing: 5.4, pose: 'sit' },
              { who: 'dunna', q: 0, r: 1, facing: 4.6, pose: 'sit' },
              { who: 'grillo', q: -2, r: 1, facing: 0.2, pose: 'sit' },
              { who: 'hero', q: 1, r: -1, facing: 2.2, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: the lift's hall, the gear train for its drum (§4, puzzle 1), the golems waking one by one, the
          // Overseer by the lift.
          kind: 'fight',
          id: 'gears',
          mode: 'gears',
          place: 'gearHall',
          budgetBp: 9000,
          goal: { kind: 'lift' },
          npcs: [{ id: 'dunna', does: 'wait', hits: 18 }],
          says: [
            { on: 'start', who: 'dunna', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'jam1', who: 'dunna', line: 'l2' },
            { on: 'cue', cue: 'golem1', who: 'dunna', line: 'l3' },
            { on: 'cue', cue: 'right', who: 'nadd', line: 'l4' },
            { on: 'cue', cue: 'bossWake', who: 'dunna', line: 'l5', delay: sec(1) },
            { on: 'cue', cue: 'boiler1', who: 'dunna', line: 'l6' },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [ln('l1', 'narrator'), ln('l2', 'nadd'), ln('l3', 'dunna'), ln('l4', 'nadd'), ln('l5', 'narrator'), ln('l6', 'dunna'), ln('l7', 'clanLetter'), ln('l8', 'nadd'), ln('l9', 'narrator')],
          cine: {
            place: 'tormoHall',
            sky: 'night',
            cam: [
              // From the lift's cage down into the cavern, the one lamp; then up Tormo's body to where its head isn't.
              { from: [-2.6, 6.6, 3.2], at: [0.6, -2.0, 1.4], ms: 9000 },
              { from: [-0.6, 2.4, 1.0], at: [0.4, -2.6, 2.6], ms: 9000 },
              { from: [1.6, 1.8, 1.0], at: [-0.4, 0.4, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'tormo', q: 0, r: -3, facing: 1.5, pose: 'sit' },
              { who: 'nadd', q: -1, r: 1, facing: 1.4, pose: 'stand' },
              { who: 'dunna', q: 0, r: 0, facing: 1.6, pose: 'stand' },
              { who: 'grillo', q: -2, r: 1, facing: 1.2, pose: 'stand' },
              { who: 'hero', q: 1, r: 0, facing: 2.0, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 2, «El cablecarril» (level 24): the plan of the cable on Nadd's table; the winch house at dawn, the car
      // and the line across the abyss; the hanging quarry's crane (Dunna lowers Tormo's arms, the gargoyles on its arm);
      // the car back across the abyss with Grada and the Raja below; the night in the car at the last pylon; the test,
      // the gearbox (the ratio of two stages, the loose axle that turns the way round, La Picotera on the drum); the car
      // in at dusk, and the clan's chalk on the iron.
      id: 'cable',
      closedBy: 'nadd',
      piece: 'smokedGoggles',
      acts: [
        {
          // Dunna unrolls a plan on the table: a line across the abyss, from the cliff to a quarry hanging on the far
          // face; in the margin, a note of Nadd's.
          kind: 'scene',
          id: 'cine',
          lines: [ln('l1', 'narrator'), ln('l2', 'note')],
          cine: {
            video: 'senda-artificer-2',
            place: 'planTable',
            sky: 'night',
            cam: [
              { from: [-0.6, 4.4, 1.2], at: [0.2, 0.4, 0.2], ms: 6000 },
              { from: [0.9, 2.6, 0.9], at: [-0.1, 0.4, 0.1], ms: 6000 },
              { from: [-1.5, 2.2, 1.3], at: [0.3, 0.8, 0.4], ms: 6000 },
            ],
            figures: [
              { who: 'dunna', q: 0, r: -1, facing: 1.6, pose: 'stand' },
              { who: 'nadd', q: -1, r: 0, facing: 0.6, pose: 'sit' },
              { who: 'grillo', q: -2, r: 1, facing: 0.4, pose: 'sit' },
            ],
          },
        },
        {
          // The winch house on the workshop's side at dawn: the car at the platform, the cable going out over the abyss
          // to the quarry on the far face.
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'nadd'), ln('l2', 'dunna'), ln('l3', 'dunna')],
          cine: {
            place: 'cableStation',
            sky: 'dawn',
            cam: [
              { from: [-3.4, 5.6, 3.2], at: [1.4, -1.2, 0.2], ms: 8000 },
              { from: [-1.2, 2.8, 1.6], at: [0.4, 0, 0.6], ms: 9000 },
              { from: [1.8, 2.4, 1.2], at: [-0.2, 0.2, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'nadd', q: -2, r: 2, facing: 4.7, pose: 'stand' },
              { who: 'dunna', q: -1, r: 2, facing: 4.5, pose: 'stand' },
              { who: 'grillo', q: -3, r: 2, facing: 4.9, pose: 'sit' },
              { who: 'hero', q: 0, r: 1, facing: 3.6, pose: 'stand' },
            ],
          },
        },
        {
          // The hanging quarry: Dunna at the crane's winch lowers the three pieces of Tormo's arms to the car (§3, act 1).
          kind: 'fight',
          id: 'crane',
          mode: 'crane',
          place: 'quarry',
          weather: 'wind',
          budgetBp: 10000,
          goal: { kind: 'crane' },
          npcs: [{ id: 'dunna', does: 'wait', hits: 15 }],
          says: [
            { on: 'start', who: 'dunna', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'flock', who: 'dunna', line: 'l2', delay: sec(1) },
            { on: 'cue', cue: 'tilt', who: 'dunna', line: 'l3' },
            { on: 'cue', cue: 'sway', who: 'dunna', line: 'l4' },
            { on: 'cue', cue: 'piece1', who: 'dunna', line: 'l5' },
            { on: 'cue', cue: 'piece2', who: 'dunna', line: 'l6' },
            { on: 'npcHit', npc: 'dunna', who: 'dunna', line: 'l7' },
            { on: 'cue', cue: 'piece3', who: 'dunna', line: 'l8' },
          ],
        },
        {
          // Back across the abyss in the car, the arms in it; Grada below on the wall, the Raja above (§3, act 2).
          kind: 'fight',
          id: 'abyss',
          mode: 'gondola',
          place: 'gondola',
          weather: 'wind',
          budgetBp: 10000,
          goal: { kind: 'span' },
          npcs: [{ id: 'dunna', does: 'wait', hits: 15 }],
          says: [
            { on: 'start', who: 'dunna', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'tilt', who: 'dunna', line: 'l2' },
            { on: 'cue', cue: 'way7', who: 'dunna', line: 'l3' },
            { on: 'cue', cue: 'way8', who: 'dunna', line: 'l4', if: { flag: 'askedWhy' } },
            { on: 'cue', cue: 'way10', who: 'dunna', line: 'l5' },
            { on: 'cue', cue: 'stop', who: 'dunna', line: 'l6' },
            { on: 'cue', cue: 'pylon1', who: 'dunna', line: 'l7' },
            { on: 'cue', cue: 'way17', who: 'dunna', line: 'l8' },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['dunna'],
          questions: [
            { id: 'leave', lines: [ln('l1', 'dunna'), ln('l2', 'dunna')], set: ['askedLeave'] },
            { id: 'believe', lines: [ln('l1', 'dunna'), ln('l2', 'dunna')], set: ['askedBelieve'] },
            { id: 'grada', lines: [ln('l1', 'dunna'), ln('l2', 'dunna')], set: ['askedGrada'] },
          ],
          cine: {
            // The car still at the last pylon by night, Dunna's lamp, the lights of Grada below.
            place: 'cableNight',
            sky: 'night',
            cam: [
              { from: [2.4, 2.6, 2.4], at: [-0.4, -0.6, 0.3], ms: 9000 },
              { from: [-1.8, 2.8, 1.6], at: [0.2, -0.8, 0.3], ms: 9000 },
            ],
            figures: [
              { who: 'dunna', q: 0, r: 0, facing: 4.4, pose: 'sit' },
              { who: 'hero', q: -1, r: 0, facing: 1.2, pose: 'sit' },
            ],
          },
        },
        {
          // The test: the winch house's gearbox, La Picotera on the drum (§4, puzzle 2).
          kind: 'fight',
          id: 'gearbox',
          mode: 'gearbox',
          place: 'winchHouse',
          weather: 'wind',
          budgetBp: 10000,
          goal: { kind: 'haul' },
          npcs: [{ id: 'dunna', does: 'wait', hits: 15 }],
          says: [
            { on: 'start', who: 'dunna', line: 'l1', delay: sec(2) },
            { on: 'start', who: 'dunna', line: 'l2', delay: sec(9) },
            { on: 'cue', cue: 'err1', who: 'dunna', line: 'l3' },
            { on: 'cue', cue: 'err2', who: 'dunna', line: 'l4' },
            { on: 'cue', cue: 'err3', who: 'dunna', line: 'l5' },
            { on: 'cue', cue: 'knock', who: 'dunna', line: 'l6' },
            { on: 'cue', cue: 'beat', who: 'dunna', line: 'l7' },
            { on: 'cue', cue: 'right', who: 'nadd', line: 'l8', delay: sec(1) },
            { on: 'cue', cue: 'half', who: 'dunna', line: 'l9' },
            { on: 'cue', cue: 'bossDown', who: 'dunna', line: 'l10' },
            { on: 'cue', cue: 'sat', who: 'dunna', line: 'l11' },
            { on: 'cue', cue: 'loose', who: 'dunna', line: 'l12' },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'nadd'),
            ln('l3', 'dunna'),
            ln('l4', 'narrator'),
            ln('l5', 'nadd'),
            ln('l6', 'dunna', { flag: 'askedEyes' }),
            ln('l7', 'nadd', { flag: 'askedEyes' }),
            ln('l8', 'narrator'),
          ],
          cine: {
            place: 'cableStation',
            sky: 'dusk',
            cam: [
              // From over the abyss onto the platform, the car in; then close on the arms' iron and Nadd's hand on it.
              { from: [3.6, 5.2, 3.0], at: [-0.6, -0.8, 0.6], ms: 9000 },
              { from: [-1.4, 2.6, 1.4], at: [0.6, -0.2, 0.6], ms: 9000 },
              { from: [1.4, 1.8, 1.0], at: [0.2, 0.0, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'nadd', q: -2, r: 2, facing: 4.7, pose: 'stand' },
              { who: 'dunna', q: -1, r: 2, facing: 4.4, pose: 'stand' },
              { who: 'grillo', q: -3, r: 2, facing: 4.9, pose: 'stand' },
              { who: 'hero', q: 0, r: 1, facing: 3.6, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 3, «El corazón» (level 30): the Raja opens a hand's breadth over Grada and the quarrymen climb to the
      // workshop to claim Nadd's promise; Tormo lacks its heart, a stone that forms where the titan's heat runs, in the
      // Médula. Down the Hondura's deep road with Dunna and Grillo (the vents' beat, the clan's chalk ahead); the river of
      // embers (Dunna unjams the old dwarven trip-hammer while the magma rises in pulses); the night on a warm slab;
      // the test, the hammer's cams (the stone freed only struck on its live faces in their order; La Tenaza for the
      // wheel); and up in the workshop, Brask at the door with the clan, and the truth of the flask.
      id: 'heart',
      closedBy: 'nadd',
      piece: 'ironGlove',
      acts: [
        {
          // A tremor. On the wall, the Raja opens a hand's breadth and sheds stones on Grada's ledges; up the stairs,
          // the people climb.
          kind: 'scene',
          id: 'cine',
          lines: [ln('l1', 'narrator'), ln('l2', 'narrator')],
          cine: {
            video: 'senda-artificer-3',
            place: 'rajaWall',
            sky: 'dawn',
            cam: [
              { from: [0.5, 6.0, 6.0], at: [0, -3.0, 4.0], ms: 6000 },
              { from: [2.4, 2.6, 2.4], at: [0, -2.4, 1.6], ms: 6000 },
              { from: [-1.6, 3.2, 1.4], at: [0.4, -1.6, 0.8], ms: 6000 },
            ],
            figures: [
              { who: 'quarry', q: -1, r: 2, facing: 4.6, pose: 'walk', delay: 400, speed: 18, walkMs: 9000 },
              { who: 'quarry2', q: 0, r: 3, facing: 4.5, pose: 'walk', delay: 900, speed: 17, walkMs: 9000 },
              { who: 'pickWoman', q: 1, r: 1, facing: 4.4, pose: 'walk', delay: 200, speed: 16, walkMs: 9000 },
            ],
          },
        },
        {
          // In the workshop at dawn, quarrymen of Grada at the door.
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'pickWoman'), ln('l2', 'nadd'), ln('l3', 'nadd'), ln('l4', 'dunna', { flag: 'askedGrada' }), ln('l5', 'dunna')],
          cine: {
            place: 'shopTop',
            sky: 'dawn',
            cam: [
              { from: [-3.0, 5.6, 2.4], at: [0.6, -1.4, 1.2], ms: 8000 },
              { from: [-1.2, 2.6, 1.2], at: [0.4, 0, 0.7], ms: 9000 },
              { from: [1.4, 2.2, 1.0], at: [-0.4, 0, 0.55], ms: 9000 },
            ],
            figures: [
              { who: 'pickWoman', q: 1, r: 1, facing: 3.6, pose: 'stand' },
              { who: 'quarry', q: 2, r: 1, facing: 3.4, pose: 'stand' },
              { who: 'quarry2', q: 2, r: 0, facing: 3.2, pose: 'stand' },
              { who: 'nadd', q: -1, r: 0, facing: 0.3, pose: 'stand' },
              { who: 'dunna', q: 0, r: -1, facing: 0.9, pose: 'stand' },
              { who: 'grillo', q: -1, r: 1, facing: 0.1, pose: 'sit' },
              { who: 'hero', q: 0, r: 1, facing: 5.2, pose: 'stand' },
            ],
          },
        },
        {
          // The Hondura's deep road down toward the heat: the vents' beat, Grillo ahead (§3, act 1).
          kind: 'fight',
          id: 'road',
          mode: 'vents',
          place: 'deepRoad',
          budgetBp: 11000,
          goal: { kind: 'marrow' },
          npcs: [
            { id: 'grillo', does: 'wait', hits: 12, vital: false, safe: true },
            { id: 'dunna', does: 'wait', hits: 28 },
          ],
          says: [
            { on: 'start', who: 'dunna', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'vent1', who: 'dunna', line: 'l2', delay: sec(1) },
            { on: 'cue', cue: 'burn', who: 'dunna', line: 'l3' },
            { on: 'cue', cue: 'chalk1', who: 'dunna', line: 'l4' },
            { on: 'cue', cue: 'foeBurn', who: 'dunna', line: 'l5' },
            { on: 'cue', cue: 'half', who: 'grillo', line: 'l6' },
            { on: 'npcHit', npc: 'dunna', who: 'dunna', line: 'l7' },
            { on: 'cue', cue: 'mouth', who: 'dunna', line: 'l8' },
          ],
        },
        {
          // The river of embers: Dunna unjams the trip-hammer on the bank while the magma rises (§3, act 2).
          kind: 'fight',
          id: 'river',
          mode: 'ember',
          place: 'emberRiver',
          budgetBp: 11000,
          goal: { kind: 'unjam' },
          npcs: [{ id: 'dunna', does: 'wait', hits: 15 }],
          says: [
            { on: 'start', who: 'dunna', line: 'l1', delay: sec(2) },
            { on: 'start', who: 'dunna', line: 'l2', delay: sec(8) },
            { on: 'cue', cue: 'pulse1', who: 'dunna', line: 'l3' },
            { on: 'cue', cue: 'half', who: 'dunna', line: 'l4' },
            { on: 'npcHit', npc: 'dunna', who: 'dunna', line: 'l5' },
            { on: 'cue', cue: 'near', who: 'dunna', line: 'l6' },
            { on: 'cue', cue: 'free', who: 'dunna', line: 'l7' },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['dunna'],
          questions: [
            { id: 'family', lines: [ln('l1', 'dunna'), ln('l2', 'dunna'), ln('l3', 'dunna', { flag: 'askedLeave' })], set: ['askedFamily'] },
            { id: 'brask', lines: [ln('l1', 'dunna'), ln('l2', 'dunna')], set: ['askedBrask'] },
            { id: 'dust', lines: [ln('l1', 'dunna'), ln('l2', 'dunna')], set: ['askedDust'] },
          ],
          cine: {
            // On a warm slab of rock by night, the river of embers behind.
            place: 'emberSlab',
            sky: 'night',
            cam: [
              { from: [2.4, 2.6, 2.2], at: [-0.4, -0.6, 0.35], ms: 9000 },
              { from: [-1.8, 2.8, 1.6], at: [0.2, -0.8, 0.35], ms: 9000 },
            ],
            figures: [
              { who: 'dunna', q: 0, r: 0, facing: 4.4, pose: 'sit' },
              { who: 'grillo', q: 1, r: 0, facing: 3.4, pose: 'sit' },
              { who: 'hero', q: -1, r: 0, facing: 1.2, pose: 'sit' },
            ],
          },
        },
        {
          // The test: the trip-hammer's cams (§4, puzzle 3), La Tenaza out of the river for the wheel.
          kind: 'fight',
          id: 'cams',
          mode: 'cams',
          place: 'tripHammer',
          budgetBp: 11000,
          goal: { kind: 'stone' },
          npcs: [{ id: 'dunna', does: 'wait', hits: 15 }],
          says: [
            { on: 'start', who: 'dunna', line: 'l1', delay: sec(2) },
            { on: 'start', who: 'dunna', line: 'l2', delay: sec(9) },
            { on: 'cue', cue: 'out', who: 'dunna', line: 'l3' },
            { on: 'cue', cue: 'chip1', who: 'dunna', line: 'l4' },
            { on: 'cue', cue: 'chip2', who: 'dunna', line: 'l5' },
            { on: 'cue', cue: 'grip', who: 'dunna', line: 'l6' },
            { on: 'cue', cue: 'front', who: 'dunna', line: 'l7' },
            { on: 'cue', cue: 'right1', who: 'dunna', line: 'l8' },
            { on: 'cue', cue: 'splash', who: 'dunna', line: 'l9' },
            { on: 'cue', cue: 'free', who: 'dunna', line: 'l10' },
            { on: 'cue', cue: 'free', who: 'dunna', line: 'l11', if: { flag: 'askedBelieve' }, delay: sec(3) },
            { on: 'cue', cue: 'bossDown', who: 'dunna', line: 'l12' },
            { on: 'cue', cue: 'turning', who: 'dunna', line: 'l13' },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'cloak'),
            ln('l3', 'nadd'),
            ln('l4', 'brask'),
            ln('l5', 'narrator'),
            ln('l6', 'dunna'),
            ln('l7', 'brask'),
            ln('l8', 'brask'),
            ln('l8b', 'dunna'),
            ln('l9', 'brask'),
            ln('l10', 'brask', { flag: '@bruni.fallen' }),
            ln('l11', 'narrator'),
            ln('l12', 'dunna', { flag: 'askedDust' }),
            ln('l13', 'dunna'),
            ln('l14', 'nadd'),
            ln('l15', 'narrator'),
          ],
          cine: {
            place: 'shopTop',
            sky: 'dusk',
            cam: [
              // From the shaft up to the door, the clan on the ledge outside; then close on Dunna with the stone, Nadd's head down.
              { from: [-3.0, 5.6, 2.6], at: [0.8, -1.0, 1.0], ms: 9000 },
              { from: [1.6, 2.6, 1.3], at: [-0.4, 0.2, 0.7], ms: 9000 },
              { from: [-1.2, 1.6, 0.95], at: [0.2, -0.2, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'brask', q: 2, r: 0, facing: 3.1, pose: 'stand' },
              { who: 'clan', q: 3, r: -1, facing: 3.0, pose: 'stand' },
              { who: 'clan2', q: 3, r: 0, facing: 3.2, pose: 'stand' },
              { who: 'clan', q: 4, r: -1, facing: 3.0, pose: 'stand' },
              { who: 'clan2', q: 3, r: 1, facing: 3.3, pose: 'stand' },
              { who: 'dunna', q: 0, r: 0, facing: 0.1, pose: 'stand' },
              { who: 'nadd', q: -1, r: 0, facing: 0.0, pose: 'stand' },
              { who: 'grillo', q: -1, r: 1, facing: 0.3, pose: 'stand' },
              { who: 'hero', q: 0, r: 1, facing: 0.4, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 4, «Los ojos» (level 36): Nadd tells Dunna the truth of the flask and the night he promised (played: the
      // memory of Grada's stair in the Marea of the Thaw, fifty-five years back, as young Nadd, with Bruni and his two
      // borrowed golems); up the glacier's tongue into the dwarven mine (the carts, the ink beetles on the rails); the
      // night by an overturned cart; the test, the mine's drills (the mill's force, the clutches, the crossed belts; La
      // Barrena for the drills that turn); and back in the workshop, the lowest point: the heart taken, Grillo coming back
      // at dawn with it in his arms, and the dust (the path's one decision).
      id: 'eyes',
      closedBy: 'nadd',
      piece: 'dustPack',
      acts: [
        {
          // By night in the workshop: Nadd, sitting, runs his hand over Grillo's head. Dunna hasn't spoken to him in days.
          kind: 'scene',
          id: 'cine',
          lines: [ln('l1', 'narrator'), ln('l2', 'narrator')],
          cine: {
            video: 'senda-artificer-4',
            place: 'shopTop',
            sky: 'night',
            cam: [
              { from: [-2.6, 4.4, 2.2], at: [0.2, -0.6, 0.8], ms: 6000 },
              { from: [-1.4, 1.8, 0.9], at: [-0.6, 0.2, 0.45], ms: 6000 },
              { from: [1.6, 1.6, 1.0], at: [0.4, -0.4, 0.6], ms: 6000 },
            ],
            figures: [
              { who: 'nadd', q: -1, r: 0, facing: 0.4, pose: 'sit' },
              { who: 'grillo', q: -1, r: 1, facing: 5.2, pose: 'sit' },
              { who: 'dunna', q: 2, r: -1, facing: 3.4, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'nadd'), ln('l2', 'nadd'), ln('l3', 'nadd'), ln('l4', 'dunna'), ln('l4b', 'dunna', { flag: 'askedFamily' }), ln('l5', 'nadd'), ln('l6', 'dunna'), ln('l7', 'nadd')],
          cine: {
            place: 'shopTop',
            sky: 'night',
            cam: [
              { from: [-2.2, 3.4, 1.8], at: [0.4, -0.4, 0.7], ms: 9000 },
              { from: [1.4, 2.0, 1.0], at: [-0.6, 0.2, 0.55], ms: 9000 },
              { from: [-1.2, 1.4, 0.9], at: [0.8, -0.2, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'nadd', q: -1, r: 0, facing: 0.4, pose: 'sit' },
              { who: 'grillo', q: -1, r: 1, facing: 5.2, pose: 'sit' },
              { who: 'dunna', q: 1, r: -1, facing: 3.6, pose: 'stand' },
              { who: 'hero', q: 0, r: 1, facing: 4.8, pose: 'stand' },
            ],
          },
        },
        {
          // The memory: the night of the promise, the Marea of the Thaw climbing Grada's wall, played as young Nadd (§3,
          // act 1). Bruni down from the forge with two old golems, asking nobody; the stair hanging from four struts.
          kind: 'fight',
          id: 'promise',
          mode: 'struts',
          place: 'gradaThaw',
          night: true,
          budgetBp: 12000,
          goal: { kind: 'struts' },
          memory: 'naddYoung',
          npcs: [
            { id: 'golem1', does: 'wait', hits: 10, vital: false, model: 'oldGolem' },
            { id: 'golem2', does: 'wait', hits: 10, vital: false, model: 'oldGolem' },
            { id: 'bruni', does: 'wait', hits: 1, vital: false, safe: true, model: 'bruniYoung' },
            { id: 'grillo', does: 'wait', hits: 1, vital: false, safe: true, model: 'grilloNew' },
            { id: 'quarry', does: 'wait', hits: 1, vital: false, safe: true },
            { id: 'quarry2', does: 'wait', hits: 1, vital: false, safe: true },
          ],
          says: [
            { on: 'start', who: 'bruni', line: 'l1', delay: sec(1) },
            { on: 'start', who: 'naddYoung', line: 'l2', delay: sec(5) },
            { on: 'start', who: 'bruni', line: 'l3', delay: sec(10) },
            { on: 'cue', cue: 'point', who: 'naddYoung', line: 'l4' },
            { on: 'cue', cue: 'drop1', who: 'bruni', line: 'l5' },
            { on: 'cue', cue: 'caulk1', who: 'bruni', line: 'l6' },
            { on: 'cue', cue: 'golemDown', who: 'bruni', line: 'l7' },
            { on: 'cue', cue: 'house1', who: 'quarry', line: 'l8' },
            { on: 'cue', cue: 'hurt', who: 'naddYoung', line: 'l9' },
            { on: 'cue', cue: 'house4', who: 'quarry2', line: 'l10' },
            { on: 'cue', cue: 'caulk4', who: 'bruni', line: 'l11' },
            { on: 'cue', cue: 'groan', who: 'naddYoung', line: 'l12' },
          ],
        },
        {
          // Dawn, still in the memory: a girl of fifteen with a pick, and the promise.
          kind: 'scene',
          id: 'dawn',
          lines: [ln('l1', 'narrator'), ln('l2', 'pickGirl'), ln('l3', 'naddYoung'), ln('l4', 'pickGirl'), ln('l5', 'narrator'), ln('l6', 'naddYoung'), ln('l7', 'bruni'), ln('l8', 'naddYoung')],
          cine: {
            place: 'gradaThaw',
            sky: 'dawn',
            memory: true,
            cam: [
              { from: [-2.4, 4.6, 2.4], at: [0.4, -1.0, 1.0], ms: 9000 },
              { from: [1.6, 2.0, 1.1], at: [-0.4, -0.2, 0.6], ms: 9000 },
              // Up the wall to the Raja, a thin line still.
              { from: [0.2, 2.6, 1.0], at: [0, -6.0, 6.0], ms: 9000 },
            ],
            figures: [
              { who: 'naddYoung', q: 0, r: 0, facing: 0.6, pose: 'stand' },
              { who: 'pickGirl', q: 1, r: -1, facing: 3.6, pose: 'stand' },
              { who: 'bruni', q: -1, r: 0, facing: 0.2, pose: 'stand' },
              { who: 'grilloNew', q: -1, r: 1, facing: 0.6, pose: 'sit' },
            ],
          },
        },
        {
          // Back in the workshop.
          kind: 'scene',
          id: 'back',
          lines: [ln('l1', 'nadd'), ln('l2', 'nadd'), ln('l3', 'dunna')],
          cine: {
            place: 'shopTop',
            sky: 'night',
            cam: [
              { from: [1.4, 2.0, 1.0], at: [-0.6, 0.2, 0.55], ms: 9000 },
              { from: [-1.6, 1.6, 1.0], at: [0.6, -0.4, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'nadd', q: -1, r: 0, facing: 0.4, pose: 'sit' },
              { who: 'grillo', q: -1, r: 1, facing: 5.2, pose: 'sit' },
              { who: 'dunna', q: 0, r: -1, facing: 2.8, pose: 'stand' },
              { who: 'hero', q: 0, r: 1, facing: 4.8, pose: 'stand' },
            ],
          },
        },
        {
          // Up the glacier's tongue into the dwarven mine: rails, carts frozen on the slopes, ink beetles asleep on the
          // rails (§3, act 2).
          kind: 'fight',
          id: 'carts',
          mode: 'carts',
          place: 'iceMine',
          budgetBp: 12000,
          goal: { kind: 'drillHall' },
          npcs: [{ id: 'dunna', does: 'follow', hits: 24, follow: 1400 }],
          says: [
            { on: 'start', who: 'dunna', line: 'l1', delay: sec(2) },
            { on: 'start', who: 'dunna', line: 'l2', delay: sec(9) },
            { on: 'cue', cue: 'shell', who: 'dunna', line: 'l3' },
            { on: 'cue', cue: 'cart1', who: 'dunna', line: 'l4' },
            { on: 'cue', cue: 'crack', who: 'dunna', line: 'l5' },
            { on: 'cue', cue: 'spent', who: 'dunna', line: 'l6' },
            { on: 'cue', cue: 'gate2', who: 'dunna', line: 'l7' },
            { on: 'npcHit', npc: 'dunna', who: 'dunna', line: 'l8' },
            { on: 'cue', cue: 'hall', who: 'dunna', line: 'l9' },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['dunna'],
          questions: [
            { id: 'dunnaGo', lines: [ln('l1', 'dunna'), ln('l2', 'dunna')], set: ['askedDunnaGo'] },
            { id: 'tormoDo', lines: [ln('l1', 'dunna'), ln('l2', 'dunna'), ln('l3', 'dunna', { flag: 'askedBelieve' })], set: ['askedTormoDo'] },
            { id: 'grilloNight', lines: [ln('l1', 'dunna'), ln('l2', 'dunna')], set: ['askedGrilloNight'] },
          ],
          cine: {
            // In a gallery of the mine, by an overturned cart.
            place: 'mineCamp',
            sky: 'night',
            cam: [
              { from: [2.4, 2.4, 2.0], at: [-0.4, -0.4, 0.35], ms: 9000 },
              { from: [-1.8, 2.6, 1.5], at: [0.2, -0.6, 0.35], ms: 9000 },
            ],
            figures: [
              { who: 'dunna', q: 0, r: 0, facing: 4.4, pose: 'sit' },
              { who: 'hero', q: -1, r: 0, facing: 1.2, pose: 'sit' },
            ],
          },
        },
        {
          // The test: the drills (§4, puzzle 4), La Barrena out of the broken drills for the ones that turn.
          kind: 'fight',
          id: 'drills',
          mode: 'drills',
          place: 'drillHall',
          budgetBp: 12000,
          goal: { kind: 'geode' },
          npcs: [{ id: 'dunna', does: 'wait', hits: 20 }],
          says: [
            { on: 'start', who: 'dunna', line: 'l1', delay: sec(2) },
            { on: 'start', who: 'dunna', line: 'l2', delay: sec(9) },
            { on: 'cue', cue: 'jam', who: 'dunna', line: 'l3' },
            { on: 'cue', cue: 'wrong', who: 'dunna', line: 'l4' },
            { on: 'cue', cue: 'out', who: 'dunna', line: 'l5' },
            { on: 'cue', cue: 'stall', who: 'dunna', line: 'l6' },
            { on: 'cue', cue: 'spin', who: 'dunna', line: 'l7' },
            { on: 'cue', cue: 'wall1', who: 'dunna', line: 'l8' },
            { on: 'cue', cue: 'wall2', who: 'dunna', line: 'l9' },
            { on: 'cue', cue: 'wall2', who: 'dunna', line: 'l10', if: { flag: 'askedTormoDo' }, delay: sec(3) },
            { on: 'cue', cue: 'bossDown', who: 'dunna', line: 'l11' },
          ],
          trial: true,
        },
        {
          // The lowest point: the workshop by night, its door broken, Tormo's chest open and empty; at dawn, Grillo.
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'nadd'),
            ln('l3', 'nadd'),
            ln('l4', 'nadd'),
            ln('l5', 'dunna'),
            ln('l6', 'nadd'),
            ln('l7', 'dunna'),
            ln('l8', 'narrator'),
            ln('l9', 'dunna'),
            ln('l10', 'nadd'),
            ln('l11', 'narrator'),
            ln('l12', 'dunna', { flag: 'askedGrilloNight' }),
          ],
          cine: {
            place: 'shopTop',
            sky: 'dawn',
            broken: true,
            cam: [
              { from: [-3.0, 5.0, 2.4], at: [0.6, -0.8, 1.0], ms: 9000 },
              // Up the ledge: something that creaks.
              { from: [0.6, 2.6, 1.0], at: [3.0, -0.4, 0.4], ms: 9000 },
              { from: [-1.2, 1.4, 0.8], at: [0.4, 0.2, 0.3], ms: 9000 },
            ],
            figures: [
              { who: 'nadd', q: 0, r: 0, facing: 0.0, pose: 'kneel' },
              { who: 'grilloBroken', q: 1, r: 0, facing: 3.1, pose: 'lie' },
              { who: 'dunna', q: 0, r: -1, facing: 0.8, pose: 'stand' },
              { who: 'hero', q: -1, r: 1, facing: 0.4, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'choice',
          id: 'choice',
          lines: [ln('l0', 'narrator'), ln('l1', 'nadd'), ln('l2', 'nadd'), ln('l3', 'nadd')],
          options: [
            { id: 'old', set: ['oldDust'] },
            { id: 'grillo', set: ['grilloDust'] },
          ],
          cine: {
            place: 'shopTop',
            sky: 'dawn',
            broken: true,
            cam: [
              { from: [-1.6, 1.8, 1.0], at: [0.4, -0.2, 0.5], ms: 9000 },
              { from: [1.4, 1.6, 0.9], at: [-0.2, 0, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'nadd', q: 0, r: 0, facing: 0.0, pose: 'kneel' },
              { who: 'grilloBroken', q: 1, r: 0, facing: 3.1, pose: 'lie' },
              { who: 'dunna', q: 0, r: -1, facing: 0.8, pose: 'stand' },
              { who: 'hero', q: -1, r: 1, facing: 0.4, pose: 'stand' },
            ],
          },
        },
        {
          // The decision's weight, in this same scene: the tube in Tormo's chest dark red, or light red and blinking.
          kind: 'scene',
          id: 'after',
          lines: [
            ln('l1', 'narrator', { flag: 'oldDust' }),
            ln('l2', 'dunna', { flag: 'oldDust' }),
            ln('l3', 'nadd', { flag: 'oldDust' }),
            ln('l4', 'narrator', { flag: 'oldDust' }),
            ln('l5', 'narrator', { flag: 'grilloDust' }),
            ln('l6', 'dunna', { flag: 'grilloDust' }),
            ln('l7', 'nadd', { flag: 'grilloDust' }),
            ln('l8', 'narrator', { flag: 'grilloDust' }),
            ln('l9', 'narrator'),
          ],
          cine: {
            place: 'shopTop',
            sky: 'dawn',
            broken: true,
            cam: [
              { from: [-1.4, 2.2, 1.3], at: [0.6, -0.8, 1.0], ms: 9000 },
              { from: [1.2, 1.4, 0.8], at: [0.4, 0.2, 0.3], ms: 9000 },
            ],
            figures: [
              { who: 'nadd', q: 0, r: 0, facing: 0.6, pose: 'stand' },
              { who: 'grilloBroken', q: 1, r: 0, facing: 3.1, pose: 'lie' },
              { who: 'dunna', q: 0, r: -1, facing: 0.4, pose: 'stand' },
              { who: 'hero', q: -1, r: 1, facing: 0.4, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 5, «Tormo» (level 42): the heart in his chest and the dust in his tube before dawn, the eyes black;
      // his knees and elbows locked in his falling hall (the gear trains, on purpose wrong); the winch pulling him off
      // the Raja over Grada (the slowest the box gives); Adela and the quarrymen in the yard; the campfire by him tied;
      // the stone off his shoulders and back by Nadd's drills, the chest spared; the speech; the plates (§5); the
      // epilogue: he kneels to Nadd unbidden, Brask takes the clan's dust and carves the clan's mark, Tormo goes up to
      // the Raja alone and sets his shoulder to it.
      id: 'tormo',
      closedBy: 'nadd',
      piece: 'tormoMark',
      call: true,
      // Lost to what got into him, a closed chapter opens again at his fight: the knees, the winch and the shell stay won.
      keep: 'plates',
      acts: [
        {
          // Before dawn in Tormo's hall: Dunna puts the heart's stone in his chest and pours the dust. A light in each eye.
          kind: 'scene',
          id: 'cine',
          lines: [ln('l1', 'narrator'), ln('l2', 'narrator', { flag: 'oldDust' }), ln('l3', 'narrator', { flag: 'grilloDust' }), ln('l4', 'narrator')],
          cine: {
            video: 'senda-artificer-5',
            place: 'tormoDown',
            sky: 'night',
            cam: [
              { from: [-2.6, 5.4, 2.6], at: [0.4, -2.4, 2.2], ms: 6000 },
              { from: [-0.4, 1.8, 1.2], at: [0.2, -2.8, 2.4], ms: 6000 },
              { from: [1.8, 2.4, 1.4], at: [0, -3.2, 4.2], ms: 6000 },
            ],
            figures: [
              { who: 'tormoDark', q: 0, r: -3, facing: 1.5, pose: 'stand' },
              { who: 'dunna', q: 0, r: -1, facing: 4.6, pose: 'stand' },
              { who: 'nadd', q: -1, r: 1, facing: 1.4, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'nadd'), ln('l2', 'dunna'), ln('l3', 'dunna'), ln('l4', 'narrator')],
          cine: {
            place: 'tormoDown',
            sky: 'night',
            cam: [
              { from: [-2.2, 3.6, 1.6], at: [0.2, -2.0, 1.8], ms: 9000 },
              { from: [1.4, 2.0, 1.0], at: [-0.4, 0.4, 0.6], ms: 9000 },
              { from: [0.2, 3.0, 1.2], at: [0, -3.0, 4.0], ms: 9000 },
            ],
            figures: [
              { who: 'tormoDark', q: 0, r: -3, facing: 1.5, pose: 'stand' },
              { who: 'dunna', q: 0, r: 0, facing: 4.4, pose: 'stand' },
              { who: 'nadd', q: -1, r: 1, facing: 1.4, pose: 'stand' },
              { who: 'hero', q: 1, r: 0, facing: 2.0, pose: 'stand' },
            ],
          },
        },
        {
          // Act 1: the knees (§3; battle/knees.ts). Tormo after Nadd round his falling hall; his joints' gear trains.
          kind: 'fight',
          id: 'knees',
          mode: 'knees',
          place: 'tormoHall',
          night: true,
          budgetBp: 12500,
          goal: { kind: 'knees' },
          npcs: [
            { id: 'dunna', does: 'wait', hits: 8 },
            { id: 'nadd', does: 'wait', hits: 1, safe: true, vital: false },
          ],
          says: [
            { on: 'start', who: 'nadd', line: 'l1', delay: sec(1) },
            { on: 'start', who: 'dunna', line: 'l2', delay: sec(6) },
            { on: 'cue', cue: 'wrong', who: 'dunna', line: 'l3' },
            { on: 'cue', cue: 'lock1', who: 'nadd', line: 'l4' },
            { on: 'cue', cue: 'lock3', who: 'dunna', line: 'l5' },
            { on: 'npcHit', npc: 'dunna', who: 'dunna', line: 'l6' },
            { on: 'cue', cue: 'near', who: 'dunna', line: 'l7' },
            { on: 'cue', cue: 'fall', who: 'nadd', line: 'l8', delay: sec(1) },
          ],
        },
        {
          // Act 2: the winch (§3; battle/torno.ts). Tormo on the wall over Grada, his fingers at the Raja.
          kind: 'fight',
          id: 'torno',
          mode: 'torno',
          place: 'rajaWall',
          budgetBp: 12500,
          goal: { kind: 'raja' },
          npcs: [{ id: 'dunna', does: 'wait', hits: 20 }],
          says: [
            { on: 'start', who: 'dunna', line: 'l1', delay: sec(2) },
            { on: 'start', who: 'nadd', line: 'l2', delay: sec(8) },
            { on: 'cue', cue: 'perch', who: 'dunna', line: 'l3' },
            { on: 'cue', cue: 'slip', who: 'dunna', line: 'l4' },
            { on: 'cue', cue: 'climb', who: 'dunna', line: 'l5' },
            { on: 'cue', cue: 'cable', who: 'dunna', line: 'l6' },
            { on: 'cue', cue: 'haul', who: 'dunna', line: 'l7' },
            { on: 'cue', cue: 'free', who: 'dunna', line: 'l8' },
          ],
        },
        {
          // Tormo tied in the yard; Grada's quarrymen come up the ledge, Adela in front with a pick and twenty behind.
          kind: 'scene',
          id: 'yard',
          lines: [ln('l1', 'narrator'), ln('l2', 'adela'), ln('l3', 'nadd'), ln('l4', 'adela')],
          cine: {
            place: 'shopYard',
            sky: 'dusk',
            cam: [
              { from: [-3.4, 5.6, 2.6], at: [0.4, -1.2, 1.4], ms: 9000 },
              { from: [2.2, 3.0, 1.2], at: [-0.6, 0.8, 0.7], ms: 9000 },
            ],
            figures: [
              { who: 'tormoTied', q: 0, r: -2, facing: 1.5, pose: 'stand' },
              { who: 'nadd', q: -1, r: 2, facing: 0.6, pose: 'stand' },
              { who: 'dunna', q: 0, r: 2, facing: 0.8, pose: 'stand' },
              { who: 'adela', q: 2, r: 1, facing: 3.4, pose: 'stand' },
              { who: 'quarry', q: 3, r: 1, facing: 3.2, pose: 'stand' },
              { who: 'quarry2', q: 3, r: 2, facing: 3.4, pose: 'stand' },
              { who: 'hero', q: -2, r: 2, facing: 0.4, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['nadd', 'dunna'],
          questions: [
            { id: 'save', lines: [ln('l1', 'nadd'), ln('l2', 'nadd')], set: ['askedSave'] },
            { id: 'name', lines: [ln('l1', 'nadd'), ln('l2', 'nadd')], set: ['askedName'] },
            { id: 'forgive', lines: [ln('l1', 'dunna'), ln('l2', 'dunna')], set: ['askedForgive'] },
          ],
          cine: {
            // By night in the yard, Tormo tied and still in the forge's light.
            place: 'shopYard',
            sky: 'night',
            cam: [
              { from: [2.6, 4.4, 1.8], at: [-0.4, 0.6, 0.5], ms: 9000 },
              { from: [-2.0, 3.6, 1.4], at: [0.2, -1.6, 1.4], ms: 9000 },
            ],
            figures: [
              { who: 'tormoTied', q: 0, r: -2, facing: 1.5, pose: 'stand' },
              { who: 'nadd', q: -1, r: 2, facing: 0.6, pose: 'sit' },
              { who: 'dunna', q: 1, r: 1, facing: 3.4, pose: 'sit' },
              { who: 'hero', q: 0, r: 3, facing: 4.8, pose: 'sit' },
            ],
            fire: { q: 0, r: 2 },
          },
        },
        {
          // Act 3: the shell (§3; battle/shell.ts), the chapter's test: the stone off his shoulders and back, the chest spared.
          kind: 'fight',
          id: 'shell',
          mode: 'shell',
          place: 'shopYard',
          budgetBp: 12500,
          goal: { kind: 'shell' },
          npcs: [{ id: 'dunna', does: 'wait', hits: 20 }],
          says: [
            { on: 'start', who: 'nadd', line: 'l1', delay: sec(2) },
            { on: 'start', who: 'nadd', line: 'l2', delay: sec(8) },
            { on: 'cue', cue: 'jam', who: 'dunna', line: 'l3' },
            { on: 'cue', cue: 'crack1', who: 'nadd', line: 'l4' },
            { on: 'cue', cue: 'crack2', who: 'dunna', line: 'l5' },
            { on: 'cue', cue: 'twist', who: 'dunna', line: 'l6' },
            { on: 'cue', cue: 'stone1', who: 'nadd', line: 'l7' },
            { on: 'cue', cue: 'stone2', who: 'dunna', line: 'l8' },
            { on: 'cue', cue: 'stall', who: 'dunna', line: 'l9' },
            { on: 'cue', cue: 'stone3', who: 'nadd', line: 'l10' },
          ],
          trial: true,
        },
        {
          // The speech: Tormo breaks the cable; before he stands, Nadd to Grada (docs/SENDAS.md, emotion, pieces 7 and 8).
          kind: 'choice',
          id: 'speech',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'nadd'),
            ln('l3', 'adela'),
            ln('l4', 'nadd'),
            ln('l5', 'dunna'),
            ln('l6', 'nadd'),
            { id: 'l7', who: 'quarry', cue: 'picks' },
            ln('l8', 'nadd'),
          ],
          options: [
            { id: 'wall', set: ['vowWall'] },
            { id: 'clan', set: ['vowClan'] },
          ],
          cine: {
            place: 'shopYard',
            sky: 'dawn',
            cam: [
              { from: [0.4, 5.6, 1.6], at: [0, -1.6, 2.0], ms: 12000 },
              { from: [-2.6, 3.4, 0.9], at: [0.6, 1.4, 0.8], ms: 12000 },
              { from: [2.4, 3.6, 0.7], at: [-0.4, 1.8, 0.6], ms: 12000 },
            ],
            figures: [
              { who: 'tormoTied', q: 0, r: -2, facing: 1.5, pose: 'stand' },
              { who: 'nadd', q: 0, r: 2, facing: 4.6, pose: 'stand' },
              { who: 'dunna', q: -1, r: 2, facing: 4.4, pose: 'stand' },
              { who: 'adela', q: 1, r: 2, facing: 4.7, pose: 'stand' },
              { who: 'quarry', q: 2, r: 2, facing: 4.6, pose: 'stand' },
              { who: 'quarry2', q: -2, r: 3, facing: 4.5, pose: 'stand' },
              { who: 'quarry3', q: 1, r: 3, facing: 4.6, pose: 'stand' },
              { who: 'hero', q: 0, r: 1, facing: 1.5, pose: 'stand' },
            ],
          },
        },
        {
          // Act 4: Tormo, what got into him in his plates (§5; battle/plates.ts). The quarrymen at the crossbows and before Nadd.
          kind: 'fight',
          id: 'plates',
          mode: 'plates',
          place: 'shopYard',
          budgetBp: 12500,
          goal: { kind: 'free' },
          npcs: [
            { id: 'nadd', does: 'wait', hits: 12 },
            { id: 'dunna', does: 'wait', hits: 20, vital: false },
            { id: 'adela', does: 'wait', hits: 12, vital: false },
            { id: 'quarry', does: 'wait', hits: 10, vital: false },
            { id: 'quarry2', does: 'wait', hits: 1, safe: true, vital: false },
            { id: 'quarry3', does: 'wait', hits: 1, safe: true, vital: false },
          ],
          says: [
            { on: 'start', who: 'nadd', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'out1', who: 'dunna', line: 'l2' },
            { on: 'cue', cue: 'phase2', who: 'nadd', line: 'l3', delay: sec(1) },
            { on: 'cue', cue: 'cubs', who: 'adela', line: 'l4' },
            { on: 'cue', cue: 'phase3', who: 'dunna', line: 'l5', delay: sec(1) },
            { on: 'cue', cue: 'iron', who: 'nadd', line: 'l6' },
            { on: 'cue', cue: 'hammer', who: 'dunna', line: 'l7' },
            { on: 'cue', cue: 'last', who: 'nadd', line: 'l8' },
            { on: 'cue', cue: 'last', who: 'adela', line: 'l9', delay: sec(4) },
          ],
          foe: true,
          trial: true,
        },
        {
          // The last plate out: what got in goes off in black dust; Tormo kneels to Nadd unbidden; Brask comes up the
          // ledge alone for what is the clan's, carves the clan's mark beside Nadd's and goes.
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'nadd'),
            ln('l3', 'dunna'),
            ln('l4', 'nadd'),
            ln('l5', 'narrator'),
            ln('l6', 'nadd'),
            ln('l7', 'nadd'),
            ln('l8', 'dunna'),
            ln('l9', 'narrator'),
            ln('l10', 'brask'),
            ln('l11', 'dunna', { flag: 'askedDust' }),
            ln('l12', 'nadd'),
            ln('l13', 'nadd', { flag: 'oldDust' }),
            ln('l14', 'nadd', { flag: 'grilloDust' }),
            ln('l15', 'narrator'),
            ln('l16', 'nadd'),
            ln('l17', 'narrator'),
            ln('l18', 'brask'),
            ln('l19', 'nadd'),
            ln('l20', 'brask'),
            ln('l21', 'narrator'),
            ln('l22', 'narrator', { flag: 'oldDust' }),
            ln('l23', 'nadd', { flag: 'oldDust' }),
          ],
          cine: {
            place: 'shopYard',
            sky: 'day',
            cam: [
              { from: [1.6, 4.2, 1.4], at: [-0.2, 0.8, 1.2], ms: 10000 },
              // Brask at the ledge's head, far from Tormo; then the chisel at Tormo's left shoulder.
              { from: [-1.2, 3.6, 1.0], at: [3.2, 1.4, 0.7], ms: 10000 },
              { from: [-2.0, 2.6, 2.4], at: [-0.4, 0.6, 2.2], ms: 10000 },
            ],
            figures: [
              { who: 'tormoKnelt', q: 0, r: 1, facing: 4.5, pose: 'stand' },
              { who: 'nadd', q: 0, r: 2, facing: 1.5, pose: 'stand' },
              { who: 'dunna', q: -1, r: 2, facing: 0.8, pose: 'stand' },
              { who: 'adela', q: 1, r: 2, facing: 4.0, pose: 'stand' },
              { who: 'brask', q: 4, r: 0, facing: 3.4, pose: 'stand' },
              { who: 'hero', q: -2, r: 3, facing: 0.4, pose: 'stand' },
            ],
          },
        },
        {
          // Tormo goes up to the Raja over Grada alone and sets his shoulder to it; the picks against the stone, ledge by ledge.
          kind: 'scene',
          id: 'epilogue',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'narrator'),
            ln('l3', 'adela'),
            ln('l4', 'adela'),
            ln('l5', 'adela'),
            ln('l6', 'nadd', { flag: 'vowWall' }),
            ln('l7', 'nadd', { flag: 'vowClan' }),
            ln('l8', 'dunna', { flag: 'askedDunnaGo' }),
            ln('l9', 'dunna', { notFlag: 'askedDunnaGo' }),
            ln('l10', 'narrator', { flag: 'askedForgive' }),
            ln('l11', 'dunna', { flag: 'askedForgive' }),
            ln('l12', 'narrator'),
            ln('l13', 'narrator', { flag: 'grilloDust' }),
            ln('l14', 'dunna', { flag: 'grilloDust' }),
            ln('l15', 'narrator', { flag: 'oldDust' }),
            ln('l17', 'narrator'),
            ln('l16', 'nadd', { flag: 'askedName' }),
          ],
          cine: {
            video: 'senda-artificer-5-end',
            place: 'gradaRaja',
            sky: 'dawn',
            cam: [
              { from: [-1.8, 6.4, 1.6], at: [0.2, -3.6, 6.4], ms: 12000 },
              { from: [1.6, 3.2, 1.0], at: [-0.6, 0.4, 0.8], ms: 12000 },
            ],
            figures: [
              { who: 'tormoUp', q: 0, r: -4, facing: 4.5, pose: 'stand' },
              { who: 'adela', q: 1, r: 1, facing: 4.6, pose: 'stand' },
              { who: 'nadd', q: -1, r: 2, facing: 4.4, pose: 'stand' },
              { who: 'dunna', q: 0, r: 2, facing: 4.6, pose: 'stand' },
              { who: 'quarry', q: 2, r: 1, facing: 4.5, pose: 'stand' },
              { who: 'quarry2', q: -2, r: 1, facing: 4.7, pose: 'stand' },
              { who: 'hero', q: 1, r: 2, facing: 4.5, pose: 'stand' },
            ],
          },
        },
      ],
    },
  ],
};

export const SENDAS: SendaDef[] = [
  WARRIOR,
  PALADIN,
  ARTIFICER,
  BARBARIAN_PATH,
  CAMINO_PATH,
  DRUID_SENDA,
  RANGER,
  MAGE_PATH,
  {
    id: 'test',
    // (On a class whose own path isn't written: the ranger's is, since its chapter 1.)
    cls: 'mage',
    test: true,
    cast: { tutor: { cls: 'warrior', tier: 3, look: 'mace~common~shield~uncommon~coif~hauberk~mailBoots' } },
    chapters: [
      {
        id: 'yard',
        closedBy: 'tutor',
        piece: 'band',
        acts: [
          {
            kind: 'scene',
            id: 'open',
            lines: [ln('l1', 'narrator'), ln('l2', 'tutor'), ln('l3', 'tutor')],
            cine: {
              place: 'camp',
              sky: 'dusk',
              weather: 'wind',
              cam: [
                { from: [-2, 7, 3.6], at: [0, 0, 0.3], ms: 6000 },
                { from: [0, 3, 1.5], at: [0.5, 0, 0.7], ms: 7000 },
              ],
              figures: [
                { who: 'tutor', q: 0, r: 0, facing: 0.6, pose: 'stand' },
                { who: 'hero', q: 1, r: 0, facing: 2.6, pose: 'stand' },
              ],
            },
          },
          {
            kind: 'fire',
            id: 'fire',
            with: ['tutor'],
            questions: [
              { id: 'who', lines: [ln('l1', 'tutor'), ln('l2', 'tutor')], set: ['askedWho'] },
              { id: 'why', lines: [ln('l1', 'tutor')], set: ['askedWhy'] },
              { id: 'yard', lines: [ln('l1', 'tutor')], set: ['askedYard'] },
            ],
            cine: {
              place: 'camp',
              sky: 'night',
              cam: [
                { from: [3, 3, 1.9], at: [0, 0, 0.4], ms: 9000 },
                { from: [4, 1, 1.3], at: [0, 0, 0.5], ms: 9000 },
              ],
              figures: [
                { who: 'tutor', q: -1, r: 1, facing: 5, pose: 'sit' },
                { who: 'hero', q: 1, r: -1, facing: 2, pose: 'sit' },
              ],
              fire: { q: 0, r: 0 },
            },
          },
          {
            kind: 'fight',
            id: 'hold',
            mode: 'hold',
            place: 'camp',
            budgetBp: 1500,
            goal: { kind: 'time', ticks: sec(40) },
            npcs: [{ id: 'tutor', does: 'wait', hits: 15, at: { q: 0, r: 1 } }],
            says: [
              { on: 'start', who: 'tutor', line: 'l1' },
              { on: 'npcHit', who: 'tutor', line: 'l2', npc: 'tutor' },
              { on: 'half', who: 'tutor', line: 'l3' },
            ],
            trial: true,
          },
          {
            kind: 'scene',
            id: 'close',
            lines: [ln('l1', 'tutor'), ln('l2', 'tutor', { flag: 'askedWho' }), ln('l3', 'tutor', { notFlag: 'askedWho' })],
            cine: {
              place: 'camp',
              sky: 'dawn',
              weather: 'fog',
              cam: [
                { from: [4, 3, 2.6], at: [0.5, -0.5, 0.4], ms: 7000 },
                { from: [3, 1, 1.4], at: [0.5, -0.5, 0.6], ms: 7000 },
              ],
              figures: [
                { who: 'tutor', q: 0, r: 0, facing: 0.5, pose: 'kneel' },
                { who: 'hero', q: 1, r: -1, facing: 2, pose: 'stand' },
                { who: 'goblin', q: 3, r: -3, facing: 5, pose: 'walk' },
                { who: 'goblin', q: 4, r: -2, facing: 5.4, pose: 'walk' },
              ],
            },
          },
        ],
      },
    ],
  },
];

/** The path a class walks (its own; the test path only where none is written). */
/**
 * Only the test paths, for the common tests (packages/sim and apps/server sendas.test.ts): with it on, a class walks its
 * test path or none, whichever real paths are written (so those tests don't hang on a class being left without one).
 */
export const SENDA_TESTS = { only: false };

export const sendaForClass = (cls: HeroClass): SendaDef | undefined =>
  SENDA_TESTS.only ? SENDAS.find((d) => d.cls === cls && d.test) : (SENDAS.find((d) => d.cls === cls && !d.test) ?? SENDAS.find((d) => d.cls === cls));

export const sendaById = (id: string): SendaDef | undefined => SENDAS.find((d) => d.id === id);
